import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';
import { lazyWithReload } from './lib/lazyRetry';
import ChunkBoundary from './components/ChunkBoundary';
import SocialIcon from './components/SocialIcon';
import InstallApp from './components/InstallApp';
import { SOCIAL_LINKS } from './content/social';
import { AccessibilityButton, AccessibilityPanel } from './components/AccessibilityMenu';
import { readAloud, getA11y } from './lib/a11y';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'motion/react';
import { Screen, Message, ReplyQuote, Thought, ADJECTIVES, ELEMENTS, newMessageId } from './types';
import { promptStage, promptsFor, showPrompts } from './content/chatPrompts';
import { compressImage, fileToBase64 } from './utils/imageUtils';
import GlassButton from './components/ui/GlassButton';
import HeardAboutPrompt from './components/HeardAboutPrompt';
import ChatText, { copyText } from './components/chat/ChatText';
import VoiceNote from './components/chat/VoiceNote';
import ReactionWrap from './components/chat/ReactionWrap';
import { MeetAgainSheet, MeetMoonButton, MeetOfferCard, MeetIcon, MeetIntroCard, MeetEndCard, type MeetTapState } from './components/chat/MeetAgain';
import { loadMeetLocal, saveMeetLocal, meetLabel, type MeetLocal, type MeetPairInfo } from './lib/meetAgain';
import type { Gif } from './components/chat/GifPicker';
import { FEELINGS, type FeelingKey } from './content/feelings';
import { SKY_FEELINGS, skyFeelingLabel, feltPhrase, FEELING_COUNT_MIN, MAX_FEELINGS } from './content/skyFeelings';
import { FEELINGS_HUB, feelingPageBySlug, feelingPageByKey, feelingPath } from './content/feelingPages';
import { CHAT_HOUR_PATH, CHAT_HOUR_COPY, CHAT_HOUR_META, liveSession, nextSession, timeIn as chatTimeIn } from './content/chatHour';
import { AI_COMPANION_META } from './content/aiCompanion.meta';
import { LISTENER_GUIDE_META } from './content/listenerGuide.meta';
import { MEET_AGAIN_META } from './content/meetAgainGuide.meta';
import { WMHD_PATH, WMHD_META, WMHD_URL, isWmhdSeason } from './content/worldMentalHealthDay.meta';
import WmhdNudge from './components/WmhdNudge';
import { CHANGELOG_META } from './content/changelog.meta';
import { PAGE_IMAGES } from './content/pageImages';
import MeadowBackground from './components/MeadowBackground';
import CloudSky from './components/CloudSky';
import StarrySky from './components/StarrySky';
import ThoughtRelease, { RELEASE_MS, type ReleaseBurst } from './components/ThoughtRelease';
import FeelingChips, { FEELING_ICONS } from './components/FeelingChips';
import FeelingsHub from './components/FeelingsHub';
import NotFound from './components/NotFound';
const ChatHourPage = lazyWithReload(() => import('./components/ChatHourPage'));
const WorldMentalHealthDayPage = lazyWithReload(() => { void import('./pages/InfoPages').catch(() => {}); return import('./components/WorldMentalHealthDayPage'); });
import { Stars } from './components/ui/Particles';
import { useSocket } from './hooks/useSocket';
import { loadOpenChat, saveOpenChat, clearOpenChat, CHAT_GRACE_MS } from './lib/chatRestore';
import { API_BASE } from './lib/config';
import { PAGE_LABELS, CONTENT_PAGE_PATHS } from './content/seoPages.meta';
import { looksLikeCrisis, HELPLINE_URL, REPORT_REASONS, type ReportReason } from './lib/crisis';
import { pickNudge, type AiNudge } from './lib/aiNudge';
import NudgeChip from './components/NudgeChip';
import { AI_VENTER_TINT } from './lib/aiTint';
import { useSkyLayout, type SkyApi } from './components/useSkyLayout';
import type { Pt } from './lib/skyLayout';
import { rememberPost, myPostIds, unseenReply, markRepliesSeen, newPostKey, postKey, rememberReply, replyKey, unseenLove, markLoveSeen, setPostStatus, setPostWaiting, forgetPost, postStatus, pendingChecks, setReplyStatus as setMyReplyStatus, forgetReply, replyInfo } from './lib/myPosts';
import type { IncomingChatRequest } from './hooks/useSocket';
import { playThoughtPosted, useSoundPrefs, useAmbientHeld } from './lib/sounds';
import SoundMenu from './components/SoundMenu';
import EmojiShortcodes from './components/EmojiShortcodes';
import { askToNotify, notifyPermission, announceMessage } from './lib/attention';
import { Moon, Sun, Share, ArrowLeft, Sparkles, Heart, Wind, User, Mic, Image, Star, X as XIcon, Reply as ReplyIcon, Pencil, Trash2, ArrowUp, Smile, Loader2, MoreHorizontal, SmilePlus, Plus, LifeBuoy, Flag, Ban, Check, MessageCircle, EyeOff, Clock, ImagePlay, List as ListIcon, ChevronDown } from 'lucide-react';

// Whole pages reload once if a new deploy removed their file (see lazyRetry).
const Dashboard = lazyWithReload(() => import('./components/Dashboard'));
const LetGo = lazyWithReload(() => import('./components/LetGo'));
const AiCompanionPage = lazyWithReload(() => import('./components/AiCompanionPage'));
const ListenerGuidePage = lazyWithReload(() => import('./components/ListenerGuidePage'));
const MeetAgainPage = lazyWithReload(() => import('./components/MeetAgainPage'));
// Info and topic pages (About, Support, FAQ, alternatives...) share one file that
// loads when one of them opens, or quietly once the home page is idle (see App).
const loadInfoPages = () => import('./pages/InfoPages');
const AboutZuzu = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.AboutZuzu })));
const SupportZuzu = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.SupportZuzu })));
const ContactZuzu = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.ContactZuzu })));
const FeedbackZuzu = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.FeedbackZuzu })));
const ReviewZuzu = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.ReviewZuzu })));
const PressZuzu = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.PressZuzu })));
const ChangelogPage = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.ChangelogPage })));
const PrivacyTerms = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.PrivacyTerms })));
const CommunitySafety = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.CommunitySafety })));
const AskAiPage = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.AskAiPage })));
const ZuzuForm = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.ZuzuForm })));
const ContentRoute = lazyWithReload(() => loadInfoPages().then(m => ({ default: m.ContentRoute })));
// The full emoji picker lives inside a chat, where a reload would end the chat.
// So it is fetched as soon as a chat opens (then a later deploy can't break it),
// and if it still fails, ChunkBoundary shows a note instead of crashing.
const loadFullEmojiPicker = () => import('./components/chat/FullEmojiPicker');
const FullEmojiPicker = lazy(loadFullEmojiPicker);

// Parts that only show after a tap or later in a chat (photo viewer, GIFs, the
// after-chat feeling prompt) and the Sky's own code load on their own. They are
// fetched quietly once the first screen is idle, and again when a chat or the
// Sky opens, so they are ready before anyone needs them (see prefetchLater).
const loadChatExtras = () => Promise.all([
  import('./components/chat/ImageViewer'),
  import('./components/chat/GifPicker'),
  import('./components/chat/PostChatFeeling'),
]);
const loadInfiniteSky = () => import('./components/InfiniteSky');
const ImageViewer = lazy(() => import('./components/chat/ImageViewer'));
const GifPicker = lazy(() => import('./components/chat/GifPicker'));
const PostChatFeeling = lazy(() => import('./components/chat/PostChatFeeling'));
const InfiniteSky = lazyWithReload(loadInfiniteSky);
const prefetchLater = () => { loadChatExtras().catch(() => {}); loadInfiniteSky().catch(() => {}); };
// A lazy part that must never break the page: nothing shows until it is ready.
const Later = ({ children }: { children: React.ReactNode }) => (
  <ChunkBoundary fallback={null}><Suspense fallback={null}>{children}</Suspense></ChunkBoundary>
);

// DeferredVideo component to improve performance by delaying video load
const DeferredVideo = ({ src, className, isNight, videoRef }: { src: string, className: string, isNight: boolean, videoRef?: React.RefObject<HTMLVideoElement | null> }) => {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // Delay loading the video to prioritize main thread and FCP
    const timer = setTimeout(() => setShouldLoad(true), 500);
    return () => clearTimeout(timer);
  }, []);

  if (!shouldLoad) {
    return <div className={`absolute inset-0 w-full h-full ${isNight ? 'bg-[#001122]' : 'bg-[#f0f4f8]'} z-0`} />;
  }

  return (
    <video 
      ref={videoRef}
      autoPlay 
      loop={isNight} 
      muted 
      playsInline 
      className={className}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

// Re-export STAR_COLORS is only needed by Particles.tsx (imported from types.ts there directly)


// --- Components ---

const AudioVisualizer: React.FC<{ isNight: boolean }> = ({ isNight }) => {
  const [bars] = useState(() =>
    Array.from({ length: 15 }, () => ({
      maxH: Math.random() * 20 + 10,
      dur: 0.5 + Math.random() * 0.5,
    }))
  );
  return (
    <div className="flex-1 flex items-center justify-center gap-1 px-4">
      {bars.map((bar, i) => (
        <motion.div
          key={i}
          animate={{ height: [10, bar.maxH, 10] }}
          transition={{ repeat: Infinity, duration: bar.dur }}
          className={`w-1 rounded-full ${isNight ? 'bg-white/50' : 'bg-black/50'}`}
        />
      ))}
    </div>
  );
};

const Footer = ({ isNight }: { isNight: boolean }) => {
  const isSharing = useRef(false);

  return (
    <div className={`w-full max-w-3xl mx-auto flex flex-col items-center mt-auto mb-6 md:mb-3 pt-8 pb-6 md:pt-4 md:pb-4 backdrop-blur-md rounded-[24px] border ${
      isNight ? 'bg-black/20 border-white/5' : 'bg-white/20 border-black/5'
    }`}>
      {/* In October: a pill to the World Mental Health Day resources. */}
      {new Date().getMonth() === 9 && (
        <Link to={WMHD_PATH} className={`mb-4 md:mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[0.6875rem] font-medium border backdrop-blur-md transition-all hover:scale-[1.02] ${
          isNight ? 'border-white/15 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white' : 'border-black/10 bg-white/30 text-black/70 hover:bg-white/50 hover:text-black'
        }`}>
          🌻 World Mental Health Day Resources
        </Link>
      )}
      <div className="flex flex-col md:flex-row items-center justify-center mb-4 md:mb-3 text-[0.6875rem] tracking-widest font-medium opacity-60 gap-3 md:gap-4">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4">
          <Link to="/about" className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">About</Link>
          <Link to="/support" className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">Support</Link>
          <Link to="/faq" className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">FAQ</Link>
          <Link to="/contact" className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">Contact</Link>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4">
          <Link to={CHAT_HOUR_PATH} className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">Chat Hour</Link>
          <Link to="/sky-of-thoughts/feelings" className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">Feelings</Link>
          <Link to="/ai-companion" className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">AI Companion</Link>
          <Link to="/whimsical-websites" aria-label="Whimsical websites" className="uppercase whitespace-nowrap hover:opacity-100 transition-opacity">Whimsical<span className="hidden sm:inline"> Sites</span></Link>
        </div>
      </div>

      <p className={`text-[0.625rem] md:text-xs text-center max-w-2xl md:max-w-3xl px-6 leading-relaxed mb-4 md:mb-3 font-medium ${
        isNight ? 'text-white/40' : 'text-black/50'
      }`}>
        Zuzu Club {isNight ? '🤍' : '💛'} is a free, anonymous peer-support space: let a worry go in <Link to="/let-go" className="underline hover:opacity-100">Let Go</Link>, share a feeling in the <Link to="/sky-of-thoughts" className="underline hover:opacity-100">Sky of Thoughts</Link>, or chat with a kind stranger. Good vibes, no ads, no paywalls. It is peer support, not therapy, and not a substitute for professional mental health care.
      </p>

      {/* leading-6: each small link is a 24px-tall tap target (WCAG target size), rows touch, no gap. */}
      <nav aria-label="More from Zuzu" className={`flex flex-wrap justify-center gap-x-3 px-6 mb-4 md:mb-3 text-[0.625rem] leading-6 ${
        isNight ? 'text-white/30' : 'text-black/40'
      }`}>
        <Link to={WMHD_PATH} className="hover:opacity-100 hover:underline transition-opacity">World Mental Health Day</Link>
        {['/vent-anonymously', '/talk-to-strangers', '/alternatives'].map(path => (
          <Link key={path} to={path} className="hover:opacity-100 hover:underline transition-opacity">{PAGE_LABELS[path]}</Link>
        ))}
        <Link to="/feedback" className="hover:opacity-100 hover:underline transition-opacity">Feedback</Link>
        <Link to="/privacy" className="hover:opacity-100 hover:underline transition-opacity">Privacy & Terms</Link>
        <Link to="/safety" className="hover:opacity-100 hover:underline transition-opacity">Community Safety</Link>
        <Link to="/ask-ai" className="hover:opacity-100 hover:underline transition-opacity">Ask AI about Zuzu</Link>
        <Link to="/changelog" className="hover:opacity-100 hover:underline transition-opacity">What's new</Link>
        <Link to="/review" className="hover:opacity-100 hover:underline transition-opacity">Help others find Zuzu</Link>
        <Link to="/listener-guide" className="hover:opacity-100 hover:underline transition-opacity">Listener guide</Link>
        <Link to="/press" className="hover:opacity-100 hover:underline transition-opacity">Press</Link>
      </nav>

      {/* One row on desktop (so the home page fits without scrolling): help, socials, share */}
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-5 px-4">
        <a
          href={HELPLINE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.6875rem] font-medium border transition-all hover:scale-[1.02] ${
            isNight ? 'border-white/10 bg-white/5 text-white/70 hover:text-white' : 'border-black/10 bg-white/40 text-black/70 hover:text-black'
          }`}
        >
          <LifeBuoy size={13} />
          Need urgent help? Find a helpline
        </a>
        <div className="flex items-center gap-2">
          {SOCIAL_LINKS.map((social, i) => (
            <a
              key={i}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.name}
              className={`p-2.5 md:p-2 rounded-full transition-all cursor-pointer ${
                isNight 
                  ? 'liquid-glass text-night-ink' 
                  : 'bg-black/5 hover:bg-black/10 text-black border border-black/10'
              } hover:scale-[1.03]`}
            >
              <SocialIcon name={social.key} size={17} />
            </a>
          ))}
        </div>
        <button
          onClick={() => {
            if (isSharing.current) return;
            if (navigator.share) {
              isSharing.current = true;
              navigator.share({
                title: 'Zuzu Club',
                text: 'Zuzu Club 🤍 a free, anonymous place to let a worry go, share a feeling, or talk to a kind stranger. No signup.',
                url: 'https://www.zuzu.club',
              }).then(() => {
                isSharing.current = false;
              }).catch((error) => {
                isSharing.current = false;
                if (error.name !== 'AbortError') {
                  console.error('Error sharing:', error);
                }
              });
            }
          }}
          className={`text-sm font-medium flex items-center gap-2 transition-opacity ${isNight ? 'text-white/60 hover:text-white' : 'text-black/60 hover:text-black'}`}
        >
          Share the Goodness ❤️
        </button>
        {/* Phones only: add Zuzu to the home screen */}
        <InstallApp isNight={isNight} />
      </div>
    </div>
  );
};

// transparentHeader: no bar behind the header, so a full-screen background (Chat Hour) shows edge to edge.
// The pages visited in this tab, with their place in the browser history, so the
// back arrow can return to the last different page (skipping #jumps within a page).
const pageTrail: { path: string; idx: number }[] = [];
const historyIdx = () => (window.history.state as { idx?: number } | null)?.idx ?? 0;
// Kept for every page, not only the ones in Layout, so back from "All feelings"
// returns to the Sky (or Let Go, or the home page) it was opened from.
const PageTrail = () => {
  const { pathname, key: locationKey } = useLocation();
  useEffect(() => {
    const idx = historyIdx();
    while (pageTrail.length && pageTrail[pageTrail.length - 1].idx >= idx) pageTrail.pop();
    pageTrail.push({ path: pathname, idx });
  }, [pathname, locationKey]);
  return null;
};

const Layout = ({ children, isNight, setIsNight, isMuted, showBackground = true, showFooter = true, transparentHeader = false }: any) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Back returns to the last different page in Zuzu. On a fresh visit it follows the link.
  // On a filtered Whimsical websites list (?q= or ?mood=), back first shows the whole list.
  const goBack = (e: React.MouseEvent) => {
    if (pathname === '/whimsical-websites' && /[?&](q|mood)=/.test(window.location.search)) {
      e.preventDefault();
      window.dispatchEvent(new Event('whimsy:clear'));
      return;
    }
    const idx = historyIdx();
    const prev = [...pageTrail].reverse().find(t => t.idx < idx && t.path !== pathname);
    if (!prev) return;
    e.preventDefault();
    navigate(prev.idx - idx);
  };
  // Pages that open with a hero card (Whimsical websites, Support) start right under the
  // header, and the back arrow waits until the page has scrolled a little. Press also starts
  // right under the header, but keeps its back arrow (and a share button) like WMHD.
  const backAfterScroll = pathname === '/whimsical-websites' || pathname === '/support';
  const tightTop = backAfterScroll || pathname === '/press';
  // A see-through header gets its frosted bar once the page scrolls, so text never runs under the logo.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el || !(transparentHeader || backAfterScroll)) return;
    const onScroll = () => setScrolled(el.scrollTop > 60);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [transparentHeader, backAfterScroll]);
  const clearHeader = transparentHeader && !scrolled;

  // Pages reuse this layout, so its scroll area keeps its place between routes.
  // Start every new page at the top (e.g. after tapping a footer link).
  useEffect(() => {
    scrollAreaRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [pathname]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      if (!isMuted) {
        audioRef.current.play().catch(() => {});
      }
    }
  }, [isMuted]);

  return (
    <div className={`relative h-[100dvh] w-screen overflow-hidden transition-colors duration-1000 flex flex-col ${
      isNight ? 'bg-night-bg text-night-ink' : 'bg-day-bg text-black'
    }`}>
      {/* Nature Sound */}
      <audio 
        ref={audioRef}
        loop
        preload="none"
        src="/zuzu-club-ambient-sound.mp3" 
      />

      {/* Background Elements */}
      {showBackground && (
        <>
          {isNight ? (
            <>
              <DeferredVideo 
                src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260314_131748_f2ca2a28-fed7-44c8-b9a9-bd9acdd5ec31.mp4"
                className="absolute inset-0 w-full h-full object-cover z-0"
                isNight={true}
              />
              <Stars />
              <div className="absolute inset-0 bg-black/40 z-[1]" />
            </>
          ) : (
            <>
              <img
                src="/sky-light-1.jpg"
                alt=""
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover z-0"
              />
              <div className="absolute inset-0 bg-white/10 z-[1]" />
            </>
          )}
        </>
      )}

      {/* Top Navigation: a frosted bar, so page text scrolling under it never clashes */}
      <div role="banner" className={`z-[60] absolute top-0 left-0 right-0 transition-colors duration-300 ${
        clearHeader ? 'border-b border-transparent' : `backdrop-blur-xl border-b ${isNight ? (transparentHeader ? 'bg-black/50 border-white/5' : 'bg-night-bg/60 border-white/5') : 'bg-day-bg/60 border-black/5'}`
      }`}>
      <div className="px-8 py-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-between w-full">
          <Link 
            to="/"
            className={`text-3xl tracking-tight cursor-pointer m-0 font-normal block ${isNight ? 'text-night-ink' : 'text-black'} ${
              clearHeader ? `-ml-4 px-4 pt-1 pb-1.5 rounded-full backdrop-blur-md border ${isNight ? 'bg-white/5 border-white/10' : 'bg-white/40 border-black/5'}` : 'p-0'
            }`}
            style={{ fontFamily: "'Instrument Serif', serif", textDecoration: 'none' }}
          >
            <span className="m-0 p-0 text-inherit font-inherit">Zuzu</span>
          </Link>

          <div className="hdr-group relative flex items-center gap-2 sm:gap-4">
            <SoundMenu isNight={isNight} buttonClassName={`hdr-btn p-2 sm:p-2.5 rounded-full transition-all cursor-pointer ${
              isNight
                ? 'liquid-glass text-night-ink'
                : 'bg-black/5 hover:bg-black/10 text-black border border-black/10'
            } hover:scale-[1.03]`} />
            <button aria-label={isNight ? 'Switch to day mode' : 'Switch to night mode'} 
              onClick={() => setIsNight(!isNight)}
              className={`hdr-btn p-2 sm:p-2.5 rounded-full transition-all cursor-pointer ${
                isNight 
                  ? 'liquid-glass text-night-ink' 
                  : 'bg-black/5 hover:bg-black/10 text-black border border-black/10'
              } hover:scale-[1.03]`}
            >
              {isNight ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <AccessibilityButton className={`hdr-btn p-2 sm:p-2.5 rounded-full transition-all cursor-pointer ${
                isNight 
                  ? 'liquid-glass text-night-ink' 
                  : 'bg-black/5 hover:bg-black/10 text-black border border-black/10'
              } hover:scale-[1.03]`} />
          </div>
        </div>
      </div>
      </div>

      {/* Back, below the logo (same as the Sky of Thoughts). It returns to the page you came
          from in Zuzu. On a fresh visit: comparison pages go to Alternatives, the rest home. */}
      <nav aria-label="Back">
      <Link
        to={pathname.endsWith('-alternative') ? '/alternatives' : '/'}
        onClick={goBack}
        aria-label="Go back"
        className={`${backAfterScroll && !scrolled ? 'opacity-0 pointer-events-none' : ''} ${transparentHeader
          // Glass button over a full-screen sky, solid enough that text scrolling behind it stays out of the way.
          ? `absolute z-50 top-[104px] left-4 md:left-8 p-3 rounded-full border backdrop-blur-xl shadow-lg transition-all hover:scale-[1.03] ${
            isNight ? 'bg-[#0b1626]/80 border-white/15 text-night-ink shadow-black/30' : 'bg-white/85 border-black/10 text-black shadow-black/10'
          }`
          : `absolute z-50 top-[104px] left-4 md:left-8 p-3 rounded-full backdrop-blur-xl border shadow-lg transition-all hover:scale-[1.03] ${
            isNight ? 'bg-night-bg/90 border-white/15 text-night-ink shadow-black/30' : 'bg-white/90 border-black/10 text-black shadow-black/10'
          }`}`}
      >
        <ArrowLeft size={20} />
      </Link>
      </nav>

      <div ref={scrollAreaRef} data-scroll-area className={`flex-1 relative overflow-y-auto no-scrollbar ${tightTop ? 'pt-[5.5rem] md:pt-28' : 'pt-40'}`}>
        <main id="main-content" tabIndex={-1} className="outline-none">
          {children}
          {showFooter && <Footer isNight={isNight} />}
        </main>
      </div>
    </div>
  );
};

import { shareZuzu } from './lib/share';

// A photo in a chat. Photos from the other person arrive blurred, so an
// unwanted image never catches anyone by surprise; one tap shows it, the next
// opens it full screen with zoom.
const ChatImage = ({ src, blurred, onReveal }: { src?: string; blurred: boolean; onReveal?: () => void }) => {
  const [shown, setShown] = useState(!blurred);
  const [viewing, setViewing] = useState(false);
  if (shown) {
    return (
      <>
        <button type="button" onClick={() => src && setViewing(true)} aria-label="Open photo full screen" className="block rounded-xl cursor-zoom-in">
          <img src={src} alt="Shared photo" draggable={false} className="rounded-xl max-w-full h-auto object-cover" referrerPolicy="no-referrer" />
        </button>
        {viewing && src && <Later><ImageViewer src={src} alt="Shared photo" onClose={() => setViewing(false)} /></Later>}
      </>
    );
  }
  return (
    <button
      type="button"
      onClick={() => { setShown(true); onReveal?.(); }}
      aria-label="Photo hidden. Tap to view"
      className="relative block min-w-[180px] min-h-[140px] overflow-hidden rounded-xl"
    >
      <img src={src} alt="" aria-hidden="true" draggable={false} className="max-w-full h-auto object-cover blur-2xl scale-110 select-none" referrerPolicy="no-referrer" />
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/45 text-white text-center px-3">
        <EyeOff size={18} className="opacity-90" />
        <span className="text-xs font-medium">Photo hidden</span>
        <span className="text-[0.6875rem] opacity-80">Tap to view</span>
      </span>
    </button>
  );
};

const SEOUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    
    // Update canonical tag for all routes
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    
    // Set base canonical URL
    const baseUrl = 'https://www.zuzu.club';
    canonicalTag.setAttribute('href', path === '/' ? baseUrl + '/' : baseUrl + path);
    
    // Topic pages (/faq, /vent-anonymously, ...) set their own tags: see ContentPageView.
    if (path === WMHD_PATH) {
      document.title = WMHD_META.title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", WMHD_META.description);
      document.querySelector('meta[name="keywords"]')?.setAttribute("content", WMHD_META.keywords);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      for (const [sel, val] of [['meta[property="og:title"]', WMHD_META.shareTitle], ['meta[property="og:description"]', WMHD_META.description], ['meta[property="og:url"]', WMHD_URL], ['meta[property="og:image"]', WMHD_META.image], ['meta[name="twitter:title"]', WMHD_META.shareTitle], ['meta[name="twitter:description"]', WMHD_META.description], ['meta[name="twitter:image"]', WMHD_META.image]] as const) {
        document.querySelector(sel)?.setAttribute("content", val);
      }
    } else if (path === CHAT_HOUR_PATH) {
      const url = `https://www.zuzu.club${CHAT_HOUR_PATH}`;
      document.title = CHAT_HOUR_META.title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", CHAT_HOUR_META.description);
      document.querySelector('meta[name="keywords"]')?.setAttribute("content", CHAT_HOUR_META.keywords);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      for (const [sel, val] of [['meta[property="og:title"]', CHAT_HOUR_META.title], ['meta[property="og:description"]', CHAT_HOUR_META.description], ['meta[property="og:url"]', url], ['meta[property="og:image"]', CHAT_HOUR_META.image], ['meta[name="twitter:title"]', CHAT_HOUR_META.title], ['meta[name="twitter:description"]', CHAT_HOUR_META.description], ['meta[name="twitter:image"]', CHAT_HOUR_META.image]] as const) {
        document.querySelector(sel)?.setAttribute("content", val);
      }
    } else if (path === FEELINGS_HUB.path || feelingPageBySlug(path.split('/')[2])) {
      // A feeling page (/sky-of-thoughts/lonely) or the feelings overview.
      const page = path === FEELINGS_HUB.path ? null : feelingPageBySlug(path.split('/')[2])!;
      const url = `https://www.zuzu.club${path}`;
      const title = page ? page.title : FEELINGS_HUB.title;
      const description = page ? page.description : FEELINGS_HUB.description;
      const image = page ? `https://www.zuzu.club/og/zuzu-club-feeling-${page.slug}.jpg?v=10` : 'https://www.zuzu.club/og/zuzu-club-sky-feelings.jpg?v=10';
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="keywords"]')?.setAttribute("content", page ? page.keywords : FEELINGS_HUB.keywords);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", location.search.includes('t=') ? "noindex, follow" : "index, follow");
      for (const [sel, val] of [['meta[property="og:title"]', title], ['meta[property="og:description"]', description], ['meta[property="og:url"]', url], ['meta[property="og:image"]', image], ['meta[name="twitter:title"]', title], ['meta[name="twitter:description"]', description], ['meta[name="twitter:image"]', image]] as const) {
        document.querySelector(sel)?.setAttribute("content", val);
      }
    } else if (path === '/sky-of-thoughts') {
      const title = "Sky of Thoughts 🌌 | Anonymous Stories & Real Emotions | Zuzu Club";
      const description = "Every emoji holds a secret. Tap to read real anonymous stories from people around the world. No login, no judgment. Just humanity, floating in the sky.";
      const keywords = "sky of thoughts, anonymous stories, read anonymous stories, real people real emotions, anonymous confessions, share feelings anonymously, emotional stories online, vent anonymously, anonymous mental health stories, no login anonymous stories, real anonymous confessions, strangers stories online, emotional release stories";
      const image = "https://www.zuzu.club/og/zuzu-club-sky-of-thoughts.jpg?v=10";
      
      const ogTitle = "The Sky of Thoughts 🌌 | Anonymous Stories 💬 & Real Emotions | Zuzu Club";
      const ogDesc = "Real anonymous stories floating in the sky. Tap any emotion to read what someone couldn't say out loud. No login. No judgment. Just humanity.";
      
      const twTitle = "The Sky of Thoughts 🌌 | Anonymous Stories 💬 & Real Emotions | Zuzu Club";
      const twDesc = "Every emoji up there is a real person with a real story. Tap to read it. Anonymous. Free. No sign-up.";
      
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="keywords"]')?.setAttribute("content", keywords);
      // A shared link to one thought (?t=…) is kept out of search results.
      document.querySelector('meta[name="robots"]')?.setAttribute("content", location.search.includes('t=') ? "noindex, follow" : "index, follow");
      
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", ogTitle);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", ogDesc);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", image);
      
      document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", twTitle);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", twDesc);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", image);
    } else if (path === '/let-go') {
      const title = "Let Go 🌟 | Release a Worry into the Night Sky | Zuzu Club";
      const description = "Write down what is worrying you, place it in a star, and watch it shrink into the vast night sky. A calm, anonymous way to let go. No signup, no login.";
      const keywords = "let go of a worry, release stress, calm anxiety, worry release, stress relief online, breathing meditation, anonymous mental wellness, watch a worry fade, put a thought in a star, feel small in the universe, free stress relief, no signup meditation";
      const image = "https://www.zuzu.club/og/zuzu-club-let-go.jpg?v=11";

      const ogTitle = "Let Go 🌟 | Send a Worry to the Sky | Zuzu Club";
      const ogDesc = "Put what is weighing on you into a star and watch it grow small against the whole night sky. Anonymous, calm, and free.";

      const twTitle = "Let Go 🌟 | Watch a Worry Fade Away";
      const twDesc = "Write it. Place it in a star. Watch it shrink into the universe. A quiet moment to let go, no signup needed.";

      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="keywords"]')?.setAttribute("content", keywords);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");

      document.querySelector('meta[property="og:title"]')?.setAttribute("content", ogTitle);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", ogDesc);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", image);

      document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", twTitle);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", twDesc);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", image);
    } else if (path === '/about') {
      const title = "About Zuzu Club | The Story Behind a Safe Anonymous Space to Be Heard";
      const description = "Zuzu Club was built for the moments you feel alone even with everyone around. A gentle boredom buster and an antidote to toxic doomscrolling.";
      const keywords = "about zuzu club, about zuzu, antidote to doomscrolling, boredom buster, accessible, who built zuzu, anonymous emotional support app, why zuzu was built, self funded mental wellness app, connecting emotions, anonymous venting platform, free anonymous chat no login, zuzu club story, mental wellness no monetization, safe space to vent, anonymous chat platform india, emotional release app story";
      const image = "https://www.zuzu.club/og/zuzu-club-about.jpg?v=10";
      
      const ogTitle = "About Zuzu | Connecting Emotions";
      const ogDesc = "Zuzu Club was built for the moments you feel alone even with everyone around. A gentle boredom buster and an antidote to toxic doomscrolling.";
      
      const twTitle = "About Zuzu | The Story Behind the Sky";
      const twDesc = "No ads. No monetization. Just a space to exhale. Read why Zuzu was built and who it was built for.";
      
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="keywords"]')?.setAttribute("content", keywords);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", ogTitle);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", ogDesc);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", image);
      
      document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", twTitle);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", twDesc);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", image);
    } else if (path === '/') {
      const title = "Chat With Strangers, Let Go & Share Feelings | Zuzu Club";
      const description = "Zuzu Club is a whimsical website for your feelings: let a worry go in the night sky, share a feeling, or chat with a kind stranger. Free, anonymous, no signup.";
      const keywords = "anonymous stories, share your story anonymously, emotional release app, vent anonymously online, real people real stories, anonymous confession app, mental wellness app, sky of thoughts, share feelings anonymously, no login mental health, anonymous emotional support no sign up, vent to strangers online anonymously, free anonymous mental health app";
      const image = "https://www.zuzu.club/og/zuzu-club-home.jpg?v=10";
      
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="keywords"]')?.setAttribute("content", keywords);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", image);
      document.querySelector('meta[property="og:url"]')?.setAttribute("content", "https://www.zuzu.club/");
      
      document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", title);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", image);
    } else if (path === '/support') {
      const title = "Support Zuzu | Share, Donate or Volunteer | Zuzu Club";
      const description = "Help keep Zuzu free: share it with someone, buy me a coffee, volunteer, or offer your skills. Good vibes, no ads, no paywalls.";
      
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", "https://www.zuzu.club/og/zuzu-club-donate.jpg?v=10");
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", "https://www.zuzu.club/og/zuzu-club-donate.jpg?v=10");
    } else if (path === '/contact') {
      const title = "Contact Zuzu Club | Questions, Feedback or Report Abuse";
      const description = "Write to Zuzu Club: ask a question, share feedback or an idea, report abuse, or reach out about press and partnerships. Every message is read by a real person.";
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", PAGE_IMAGES['/contact'].image);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", PAGE_IMAGES['/contact'].image);
    } else if (path === '/ai-companion' || path === '/changelog' || path === '/listener-guide' || path === '/meet-again') {
      const { title, description, image } = path === '/changelog' ? CHANGELOG_META : path === '/listener-guide' ? LISTENER_GUIDE_META : path === '/meet-again' ? MEET_AGAIN_META : AI_COMPANION_META;
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", image);
      document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", title);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", image);
    } else if (path === '/feedback' || path === '/review' || path === '/press') {
      const page = path === '/feedback'
        ? { title: "Feedback | Help Make Zuzu a Little Better | Zuzu Club", description: "Share an idea, something you liked, something that felt off, a bug or a safety concern. Anonymous, no signup, email optional." }
        : path === '/press' ? { title: "Press Kit and Media Kit | Logos, Screenshots, Clips | Zuzu Club", description: "The Zuzu Club press kit, or media kit: key facts, founder quotes, story angles, official logos, real screenshots and clips, ready to download." }
        : { title: "Enjoying Zuzu? Help Others Find It | Zuzu Club", description: "If Zuzu helped you, share a kind word on Product Hunt, AlternativeTo, SaaSHub, Peerlist, X and more, so someone else can find it too." };
      document.title = page.title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", page.description);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", page.title);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", page.description);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", PAGE_IMAGES[path].image);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", PAGE_IMAGES[path].image);
    } else if (path === '/ask-ai') {
      const title = "Ask Your AI About Zuzu | ChatGPT, Claude, Gemini & More | Zuzu Club";
      const description = "Curious about Zuzu? See what your favorite AI & SI (Super Intelligence) thinks. One tap opens ChatGPT, Claude, Gemini, Copilot, Perplexity or Grok with a prompt about Zuzu Club.";
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="robots"]')?.setAttribute("content", "index, follow");
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
      document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", title);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", description);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", PAGE_IMAGES['/ask-ai'].image);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", PAGE_IMAGES['/ask-ai'].image);
    } else if (path === '/privacy' || path === '/safety') {
      const page = path === '/privacy'
        ? { title: "Privacy & Terms | Zuzu Club", image: "https://www.zuzu.club/og/zuzu-club-privacy.jpg?v=10", description: "What Zuzu keeps and what it does not. No accounts, chats not saved, and plain answers about Let Go, the Sky of Thoughts, the AI Companion and analytics." }
        : { title: "Community Safety | Zuzu Club", image: "https://www.zuzu.club/og/zuzu-club-safety.jpg?v=10", description: "Community Guidelines, reporting, blocking, blurred photos, how moderation works, age, and where to find help. Zuzu is peer support, not a crisis service." };
      document.title = page.title;
      document.querySelector('meta[name="description"]')?.setAttribute("content", page.description);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", page.description);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", page.description);
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", page.title);
      document.querySelector('meta[property="og:image"]')?.setAttribute("content", page.image);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", page.image);
    }
  }, [location.pathname]);

  return null;
};

// Home: a small pill while Zuzu's Chat Hour is on, or in the 20 minutes before.
const ChatHourPill = ({ isNight }: { isNight: boolean }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);
  const live = liveSession(new Date(now));
  const next = nextSession(new Date(now));
  const soon = Math.round((+next.start - now) / 60000);
  if (!live && soon > 20) return null;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <Link
      to={CHAT_HOUR_PATH}
      data-nosnippet
      className={`mb-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border backdrop-blur-md transition-colors ${
        isNight ? 'bg-white/10 border-white/15 text-white hover:bg-white/15' : 'bg-white/60 border-black/10 text-black hover:bg-white/80'
      }`}
    >
      <span className="w-2 h-2 rounded-full bg-[#f2c14e] animate-pulse" aria-hidden="true" />
      {live ? `${CHAT_HOUR_COPY.name} is on, until ${chatTimeIn(live.end, tz)}` : `${CHAT_HOUR_COPY.name} starts in ${soon} min`}
    </Link>
  );
};

// The Sky, or the Sky on one feeling; an unknown feeling is a 404.
const SkyRoute = (props: any) => {
  const { slug } = useParams();
  if (slug && !feelingPageBySlug(slug)) return <NotFound footer={<Footer isNight />} />;
  return <MainApp key="explore" {...props} initialScreen="UNIVERSE" />;
};

// Theme: the person's own pick (sun or moon button) always wins. Without one,
// the Sky of Thoughts is night, Whimsical websites is light (its screenshots and
// cards read best by day), and other pages follow the time of day.
const themeChoice = () => { try { return localStorage.getItem('zuzu_theme_choice'); } catch { return null; } };
const autoNight = (path: string) => {
  if (path === '/whimsical-websites') return false;
  const hour = new Date().getHours();
  return path.startsWith('/sky-of-thoughts') || path === CHAT_HOUR_PATH || hour < 6 || hour > 18;
};

// Follows the page when there is no pick: into the Sky it turns night, out of it back to the time of day.
const ThemeForPage = ({ setIsNight }: { setIsNight: (v: boolean) => void }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!themeChoice()) setIsNight(autoNight(pathname));
  }, [pathname, setIsNight]);
  return null;
};

export default function App() {
  // Fetch the info pages' file once the first screen has drawn and the browser is
  // idle, so opening About, FAQ or Support later shows the page straight away.
  useEffect(() => {
    let idle = 0;
    const start = () => {
      // Built site: only download the files (listed by vite.config.ts), so nothing runs
      // while the page settles. They run later, when a page or chat needs them.
      const files = document.querySelector('meta[name="zuzu-prefetch"]')?.getAttribute('content')?.split(' ').filter(Boolean);
      const run = files?.length
        ? () => {
            const probe = document.createElement('link');
            for (const href of files) {
              if (probe.relList?.supports?.('prefetch')) {
                const link = document.createElement('link');
                link.rel = 'prefetch';
                link.as = 'script';
                link.crossOrigin = '';
                link.href = href;
                document.head.appendChild(link);
              } else {
                // Safari has no prefetch: a quiet fetch puts the file in the cache too.
                fetch(href, { priority: 'low' } as RequestInit).catch(() => {});
              }
            }
          }
        : () => { void loadInfoPages().catch(() => {}); prefetchLater(); };
      idle = typeof requestIdleCallback === 'function' ? requestIdleCallback(run, { timeout: 5000 }) : setTimeout(run, 3000) as unknown as number;
    };
    const timer = window.setTimeout(start, 3000);
    return () => { clearTimeout(timer); if (typeof cancelIdleCallback === 'function') cancelIdleCallback(idle); else clearTimeout(idle); };
  }, []);
  const [isNight, setIsNightState] = useState(() => {
    const choice = themeChoice();
    return choice ? choice === 'dark' : autoNight(window.location.pathname);
  });
  // Every theme button goes through here, so it counts as the person's pick.
  const setIsNight = useCallback((v: boolean) => {
    try { localStorage.setItem('zuzu_theme_choice', v ? 'dark' : 'light'); } catch { /* private mode */ }
    setIsNightState(v);
  }, []);
  // isMuted means the ambient music is off: the Ambient music switch (saved on
  // the device), or paused while "Read messages aloud" is on so the voice is
  // clear. Notification sounds have their own switch (src/lib/sounds.ts).
  const soundPrefs = useSoundPrefs();
  const [readAloud, setReadAloud] = useState(() => getA11y().readAloud);
  const ambientHeld = useAmbientHeld();
  const isMuted = !soundPrefs.ambient || readAloud || ambientHeld;
  const onReadAloudChange = setReadAloud;

  // Safety net: any link to another website opens in a new tab, so Zuzu stays open.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const link = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!link || link.target || !/^https?:/i.test(link.href)) return;
      if (new URL(link.href).origin === window.location.origin) return;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Apply the theme to the page whenever it changes
  useEffect(() => {
    if (isNight) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isNight]);

  return (
    // Every animation follows the device's "reduce motion" setting.
    <MotionConfig reducedMotion="user">
    <BrowserRouter>
      <SEOUpdater />
      <PageTrail />
      <ThemeForPage setIsNight={setIsNightState} />
      {/* Keyboard and screen reader users can jump past the header. */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[90] focus:px-4 focus:py-2 focus:rounded-full focus:bg-white focus:text-black focus:shadow-lg">
        Skip to content
      </a>
      <AccessibilityPanel isNight={isNight} onReadAloudChange={onReadAloudChange} />
      <Routes>
        <Route path="/" element={<MainApp key="home" isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} />} />
        <Route path="/about" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <AboutZuzu isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/support" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <SupportZuzu isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/donate" element={<Navigate to="/support" replace />} />
        <Route path="/contact" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <ContactZuzu isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/ai-companion" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <AiCompanionPage isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/listener-guide" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <ListenerGuidePage isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/meet-again" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <MeetAgainPage isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/feedback" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <FeedbackZuzu isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/review" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <ReviewZuzu isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/press" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <PressZuzu isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/changelog" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <ChangelogPage isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/privacy" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <PrivacyTerms isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/safety" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <CommunitySafety isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path="/ask-ai" element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <AskAiPage isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        {CONTENT_PAGE_PATHS.map(path => (
          <Route key={path} path={path} element={
            <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false}>
              <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
                <ContentRoute path={path} isNight={isNight} />
              </Suspense>
            </Layout>
          } />
        ))}
        {/* Old preview links for the new backgrounds; they are the default now. */}
        <Route path="/v2" element={<Navigate to="/" replace />} />
        <Route path="/v2/sky-of-thoughts" element={<Navigate to="/sky-of-thoughts" replace />} />
        <Route path={CHAT_HOUR_PATH} element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false} transparentHeader>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <ChatHourPage isNight={isNight} />
            </Suspense>
          </Layout>
        } />
        <Route path={WMHD_PATH} element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false} transparentHeader>
            <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="animate-spin opacity-50" size={24} /></div>}>
              <WorldMentalHealthDayPage isNight={isNight} contactForm={<Suspense fallback={null}><ZuzuForm isNight={isNight} kind="contact" /></Suspense>} />
            </Suspense>
          </Layout>
        } />
        <Route path={FEELINGS_HUB.path} element={
          <Layout isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} showBackground={false} transparentHeader>
            <FeelingsHub isNight={isNight} />
          </Layout>
        } />
        {/* The Sky, and the Sky gathered on one feeling (/sky-of-thoughts/lonely). One route, so changing feeling keeps the Sky. */}
        <Route path="/sky-of-thoughts/:slug?" element={<SkyRoute isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} />} />
        <Route path="/let-go" element={<MainApp key="letgo" isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} initialScreen="LETGO" />} />
        <Route path="/dashboard" element={<MainApp key="dashboard" isNight={isNight} setIsNight={setIsNight} isMuted={isMuted} initialScreen="DASHBOARD" />} />
        {/* Old or mistyped links */}
        <Route path="*" element={<NotFound footer={<Footer isNight />} />} />
      </Routes>
    </BrowserRouter>
    </MotionConfig>
  );
}

// Share your Story: five quick picks, then the picker's groups (no repeats).
const SHARE_QUICK = ['😊', '❤️', '😂', '😢', '💔'];
const SHARE_MORE: [string, string[]][] = [
  ['Happy', ['😄', '🥹', '🥰', '🤗', '😌', '☀️', '🌻', '🌈']],
  ['Love', ['🫶', '🤍', '💛', '💜', '💕', '🙏', '✨', '🕊️']],
  ['Heavy', ['😔', '😭', '😞', '🥺', '😩', '😣', '😪', '🥀']],
  ['Tense', ['😡', '😠', '😤', '🤯', '😰', '😬', '😮‍💨', '🫨']],
];

// What a held post has that its writer can simply take out.
const FIX_LABEL: Record<string, string> = { link: 'a link', email: 'an email address', phone: 'a phone number', handle: 'a social media handle' };

const COMMON_EMOJIS = [
  '😊','😂','🥰','😭','😅','😔','😤','😮',
  '😢','😡','😳','😌','🤗','🤔','😎','😇',
  '🙏','❤️','🔥','✨','💙','💜','💯','🥺',
  '👏','🤝','💪','🙌','👍','🫂','🫶','🥲',
  '☮️','🕊️','💬','🌸','🌿','☀️','🌈','💫',
  '🏖️','🌴','🌍','🪐','🌙','🦋','🍃','🌺',
];

const formatTime = (timestamp?: number): string => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Keep the chat inside the part of the screen the keyboard does not cover.
// Android (Chrome, Firefox) already shrinks the page thanks to
// `interactive-widget=resizes-content` in index.html. iOS Safari does not, so
// there we size the chat to the visual viewport and follow its offset.
function useKeyboardSafeHeight(active: boolean) {
  const [box, setBox] = useState<{ height: number; top: number } | null>(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!active || !vv) return;
    const update = () => {
      const covered = window.innerHeight - vv.height;
      // Only step in when something (the keyboard) actually covers the page.
      setBox(covered > 80 ? { height: vv.height, top: vv.offsetTop } : null);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setBox(null);
    };
  }, [active]);
  return active ? box : null;
}

function MainApp({ isNight, setIsNight, isMuted, initialScreen = 'LANDING' }: any) {
  const navigate = useNavigate();
  // A chat with a person that this tab had open before a reload (phones often
  // reload a tab after you switch apps): it opens again and rejoins.
  const [restoredChat] = useState(() =>
    window.location.pathname === '/dashboard' || initialScreen === 'DASHBOARD' ? null : loadOpenChat()
  );
  const [screen, setScreen] = useState<Screen>(() =>
    window.location.pathname === '/dashboard' ? 'DASHBOARD' : restoredChat?.role ?? initialScreen
  );
  const isSharing = useRef(false);
  const [identity, setIdentity] = useState(() => {
    const stored = localStorage.getItem('zuzu_identity');
    const storedTime = localStorage.getItem('zuzu_identity_time');
    const now = Date.now();
    const ONE_DAY = 24 * 60 * 60 * 1000;
    if (stored && storedTime && (now - parseInt(storedTime)) < ONE_DAY) return stored;

    const pool: string[] = [];
    for (const adj of ADJECTIVES) for (const el of ELEMENTS) pool.push(`${adj} ${el}`);

    let used = new Set<string>(JSON.parse(localStorage.getItem('zuzu_used_names') || '[]'));
    let available = pool.filter(n => !used.has(n));
    if (available.length === 0) { used = new Set(); available = pool; }

    const newIdentity = available[Math.floor(Math.random() * available.length)];
    used.add(newIdentity);
    localStorage.setItem('zuzu_identity', newIdentity);
    localStorage.setItem('zuzu_identity_time', now.toString());
    localStorage.setItem('zuzu_used_names', JSON.stringify([...used]));
    return newIdentity;
  });
  const [otherName, setOtherName] = useState(() => {
    if (restoredChat) return restoredChat.otherName;
    const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
    const el = ELEMENTS[Math.floor(Math.random() * ELEMENTS.length)];
    return `${adj} ${el}`;
  });
  const [messages, setMessages] = useState<Message[]>(() => restoredChat?.messages ?? []);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [universeThoughts, setUniverseThoughts] = useState<Thought[]>([]);
  const [selectedThought, setSelectedThought] = useState<Thought | null>(null);
  // Shareable links: ?t=<id> opens that thought. `linkNotice` shows when it has drifted away.
  const [linkNotice, setLinkNotice] = useState(false);
  const [thoughtLinkCopied, setThoughtLinkCopied] = useState(false);
  const deepLinkRef = useRef<string | null>(new URLSearchParams(window.location.search).get('t'));
  // Replies on the open thought. New replies wait for approval before anyone sees them.
  const [replyText, setReplyText] = useState('');
  const REPLY_MAX_CHARS = 1000;   // also enforced on the server
  const THOUGHT_MAX_CHARS = 5000; // also enforced on the server
  const [replyStatus, setReplyStatus] = useState<'idle' | 'sending' | 'sent' | 'live' | 'error'>('idle');
  // A reply held for something simple (a link, a phone number…): its id and what to take out.
  const [replyHeld, setReplyHeld] = useState<{ id: string; fix: string } | null>(null);
  useEffect(() => { setReplyText(''); setReplyStatus('idle'); setReplyEmoji(null); setReplyHeld(null); }, [selectedThought?.id]);
  // Emojis in a reply: the quick grid, or all of them ('full'). Added where the cursor is.
  const [replyEmoji, setReplyEmoji] = useState<null | 'quick' | 'full'>(null);
  const replyBoxRef = useRef<HTMLTextAreaElement | null>(null);
  const thoughtBoxRef = useRef<HTMLTextAreaElement | null>(null);
  const addReplyEmoji = (emoji: string) => {
    const box = replyBoxRef.current;
    const at = box?.selectionStart ?? replyText.length, end = box?.selectionEnd ?? at;
    const next = (replyText.slice(0, at) + emoji + replyText.slice(end)).slice(0, REPLY_MAX_CHARS);
    setReplyText(next);
    setReplyEmoji(null);
    requestAnimationFrame(() => { box?.focus(); const c = Math.min(next.length, at + emoji.length); box?.setSelectionRange(c, c); });
  };
  const [isAddingThought, setIsAddingThought] = useState(false);
  const [newThoughtText, setNewThoughtText] = useState("");
  const [likedThoughts, setLikedThoughts] = useState<Set<string>>(new Set());
  const [selectedEmoji, setSelectedEmoji] = useState<string>("😊");
  const [showSharePicker, setShowSharePicker] = useState(false);
  const [releaseBurst, setReleaseBurst] = useState<ReleaseBurst | null>(null);
  // "Reach out": which recent thoughts have their writer here right now, a
  // request someone sent to this person, and this person's own request.
  const [reachableThoughts, setReachableThoughts] = useState<Set<string>>(() => new Set());
  const [incomingRequest, setIncomingRequest] = useState<IncomingChatRequest | null>(null);
  const [acceptingRequest, setAcceptingRequest] = useState<string | null>(null); // request id being accepted
  const [partnerRecording, setPartnerRecording] = useState(false); // the other person is recording a voice note
  const [rejoining, setRejoining] = useState(false); // reconnecting to the chat (e.g. during a deploy)
  const [outgoingReach, setOutgoingReach] = useState<{ thoughtId: string; requestId?: string; status: 'sending' | 'waiting' | 'unavailable' } | null>(null);
  const [myThoughtIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('zuzu_my_thoughts') || '[]')); } catch { return new Set(); }
  });
  const skyRef = useRef<HTMLDivElement | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [onlineUsers, setOnlineUsers] = useState(1);
  const [randomBias] = useState(() => Math.floor(Math.random() * (50 - 30 + 1)) + 30);
  const inflatedUserCount = onlineUsers <= 1 ? randomBias : 50 + (onlineUsers - 1);
  const [isAiChat, setIsAiChat] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  // Kept for the hook; the waiting card no longer shows the id or raw errors.
  const [, setSocketId] = useState<string | null>(null);
  const [, setConnectionError] = useState<string | null>(null);
  const chatRef = useRef<any>(null);
  const [roomId, setRoomId] = useState<string | null>(() => restoredChat?.roomId ?? null);
  const [matchingRole, setMatchingRole] = useState<'VENT' | 'LISTEN' | null>(() => restoredChat?.role ?? null);
  const [partnerRole, setPartnerRole] = useState<'VENT' | 'LISTEN' | null>(() => restoredChat?.partnerRole ?? null);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  // "Notify me when someone joins": the browser's answer, so the button can hide.
  const [notifyState, setNotifyState] = useState(() => notifyPermission());
  const [showSocialPopup, setShowSocialPopup] = useState(false);
  // Gentle share nudges: after a chat (in the popup) and after posting to the Sky.
  const [shareCopied, setShareCopied] = useState(false);
  const [showSkyShare, setShowSkyShare] = useState(false);
  // After posting: what the thought is about (Zuzu's guess, one tap to change it)
  // and how many others felt the same in the last 30 days.
  const [feelingCard, setFeelingCard] = useState<{ thoughtId: string; picked?: string[] } | null>(null);
  const [feelingCounts, setFeelingCounts] = useState<Record<string, number> | null>(null);
  // The Sky shown by one feeling ('all' = everything).
  // The picked feeling lives in the address (/sky-of-thoughts/lonely), so links,
  // the back button and the chips always agree. 'all' is the full Sky.
  const { pathname: routePath, state: routeState } = useLocation();
  const skyFeeling = routePath.startsWith('/sky-of-thoughts') ? (feelingPageBySlug(routePath.split('/')[2])?.key ?? 'all') : 'all';
  // On a feeling page: a slim line under the chips (heading, count, Add yours).
  // Light enough to show each time a feeling is picked; ✕ hides it.
  const [introFor, setIntroFor] = useState<string | null>(() => (skyFeeling !== 'all' ? skyFeeling : null));
  const setSkyFeeling = useCallback((key: string) => {
    setIntroFor(key !== 'all' ? key : null);
    navigate((key === 'all' ? '/sky-of-thoughts' : feelingPath(key)) + window.location.search, { replace: true });
  }, [navigate]);
  const [introMoreFor, setIntroMoreFor] = useState<string | null>(null);
  const feelingIntroClosed = introFor !== skyFeeling;
  const feelingIntroMore = introMoreFor === skyFeeling;
  const setFeelingIntroClosed = (v: boolean) => { if (v) setIntroFor(null); };
  const setFeelingIntroMore = (f: (v: boolean) => boolean) => setIntroMoreFor(f(feelingIntroMore) ? skyFeeling : null);
  // All-time Let Go worries per feeling (the strip adds them after the Sky count).
  const [letGoTotals, setLetGoTotals] = useState<Record<string, number> | null>(null);
  // Bottom edge of that card on screen, so gathered thoughts sit below it.
  const [introBottom, setIntroBottom] = useState(0);
  const introRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const ro = new ResizeObserver(() => setIntroBottom(el.getBoundingClientRect().bottom));
    ro.observe(el);
    return () => { ro.disconnect(); setIntroBottom(0); };
  }, []);
  // Thoughts this visit said "Me too" to (this tab only).
  const [meTooMine, setMeTooMine] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('zuzu_me_too') || '[]')); } catch { return new Set(); }
  });
  const shareFrom = async (text: string, url?: string) => {
    if (await shareZuzu(text, url)) { setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }
  };
  const [showHeardAbout, setShowHeardAbout] = useState(false);
  // Chat "•••" menu: help, report and block.
  const [chatMenu, setChatMenu] = useState<null | 'menu' | 'report' | 'block'>(null);
  const [reportReason, setReportReason] = useState<ReportReason | null>(null);
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  // "Where did you hear about Zuzu?" — show once; if skipped, re-ask next session;
  // stop permanently once answered. Answered flag persists across sessions.
  const maybeShowHeardAbout = useCallback((): boolean => {
    try {
      if (localStorage.getItem('zuzu_heard_about_answered') === '1') return false;
      if (sessionStorage.getItem('zuzu_heard_about_seen') === '1') return false;
      sessionStorage.setItem('zuzu_heard_about_seen', '1');
      setShowHeardAbout(true);
      return true;
    } catch {
      // Storage unavailable (private mode): fail closed, don't prompt.
      return false;
    }
  }, []);

  const handleHeardAboutSelect = useCallback((source: string) => {
    try { localStorage.setItem('zuzu_heard_about_answered', '1'); } catch { /* ignore */ }
    socketRef.current?.emit('heard_about', source);
    setShowHeardAbout(false);
  }, []);
  const [searchSeconds, setSearchSeconds] = useState(0);
  const matchingRoleRef = useRef<'VENT' | 'LISTEN' | null>(restoredChat?.role ?? null);
  const matchingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const identityRef = useRef(identity);
  const roomIdRef = useRef<string | null>(restoredChat?.roomId ?? null);
  useEffect(() => { roomIdRef.current = roomId; }, [roomId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ownerToken = params.get('owner');
    if (ownerToken) {
      localStorage.setItem('zuzu_owner_token', ownerToken);
      params.delete('owner');
      const newUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);
  // Reply to one message: the quote waits above the input until the next send.
  const [replyingTo, setReplyingTo] = useState<ReplyQuote | null>(null);
  const replyRef = useRef<ReplyQuote | null>(null);
  const setReply = (q: ReplyQuote | null) => { replyRef.current = q; setReplyingTo(q); };
  // Use the waiting quote for this message (once): for the bubble here, and for the wire.
  const takeReply = () => {
    const q = replyRef.current;
    if (q) setReply(null);
    return { quote: q ?? undefined, wire: q ? { id: q.id, text: q.text, type: q.type, fromSender: q.mine } : undefined };
  };
  const quotePreview = (q: ReplyQuote) => q.type === 'image' ? '🖼️ Photo' : q.type === 'audio' ? '🎤 Voice note' : (q.text ?? '');
  // Jump to the quoted message and light it up for a moment.
  const [flashId, setFlashId] = useState<string | null>(null);
  const jumpTo = (id: string) => {
    const el = scrollRef.current?.querySelector(`[data-msg-id="${CSS.escape(id)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashId(id);
    setTimeout(() => setFlashId(f => (f === id ? null : f)), 1400);
  };

  // Connection trouble in a human chat: this device, or the other person, is reconnecting.
  const [connIssue, setConnIssue] = useState<{ who: 'me' | 'partner'; since: number } | null>(() => (restoredChat ? { who: 'me', since: Date.now() } : null));
  // Editing one of your own text messages (up to 15 minutes after sending).
  const [editingId, setEditingId] = useState<string | null>(null);
  const EDIT_WINDOW_MS = 15 * 60 * 1000;
  // Meet Again: the chat it is about (kept after the chat ends, for the 10 minutes
  // a tap still counts), this device's pairs, and an offer when a saved person is here.
  // `keptNote`: the note from a pair let go in this chat, kept in case they pair again in it.
  type MeetCtx = { roomId: string; name: string; role: 'VENT' | 'LISTEN' | null; state: MeetTapState; pairId?: string; startedAt: number; keptNote?: string };
  const [meetCtx, setMeetCtx] = useState<MeetCtx | null>(null);
  const [meetChatPair, setMeetChatPair] = useState<string | null>(null); // this chat is a reunion
  const [meetOffer, setMeetOffer] = useState<{ offerId: string; pairId: string; expiresIn: number; receivedAt: number; waiting: boolean } | null>(null);
  // Local preview of the Meet Again card (?meetDemo, localhost only): nothing is sent.
  useEffect(() => {
    if (!/^(localhost|127\.0\.0\.1)$/.test(location.hostname) || !new URLSearchParams(location.search).has('meetDemo')) return;
    const t = setTimeout(() => setMeetOffer({ offerId: 'demo', pairId: 'demo', expiresIn: 60_000, receivedAt: Date.now(), waiting: false }), 1500);
    return () => clearTimeout(t);
  }, []);
  const [meetOpen, setMeetOpen] = useState(false);
  const { socketRef, sendChat, editChat, endChat } = useSocket({
    matchingRoleRef,
    matchingTimeoutRef,
    identityRef,
    roomIdRef,
    setOnlineUsers,
    setRoomId,
    setOtherName,
    setIsAiChat,
    setMessages,
    setScreen,
    setIsTyping,
    setUniverseThoughts,
    setIsConnected: setIsSocketConnected,
    setSocketId,
    setConnectionError,
    setIdentity,
    setPartnerRole,
    setMatchingRole,
    setReachableThoughts,
    setIncomingRequest,
    onReachOutResult: (requestId: string) =>
      setOutgoingReach(prev => (prev && prev.requestId === requestId ? { ...prev, status: 'unavailable' } : prev)),
    // A new chat started: close any open thought and forget the old request.
    setPartnerRecording,
    setRejoining,
    onFeelingReceived: (key: string) => {
      const f = FEELINGS.find(x => x.key === key);
      if (f?.note) setFeelingNote(`${f.emoji} ${f.note}`);
    },
    onMatched: (m) => {
      setSelectedThought(null); setOutgoingReach(null); setAcceptingRequest(null); setShowHeardAbout(false); setShowSocialPopup(false); setConnIssue(null); setReply(null); setEditingId(null);
      const reunion = m.via === 'meet' && m.pairId ? m.pairId : undefined;
      setMeetOffer(null); setMeetOpen(false);
      setMeetChatPair(reunion ?? null);
      setMeetCtx({ roomId: m.roomId, name: m.partnerName, role: matchingRoleRef.current, state: reunion ? 'matched' : 'idle', pairId: reunion, startedAt: Date.now() });
    },
    onConnectionLost: () => setConnIssue(prev => (prev?.who === 'me' ? prev : { who: 'me', since: Date.now() })),
    onRejoined: () => setConnIssue(prev => (prev?.who === 'me' ? null : prev)),
    onPartnerAway: () => setConnIssue(prev => (prev?.who === 'me' ? prev : { who: 'partner', since: Date.now() })),
    onPartnerBack: () => setConnIssue(null),
    onEditFailed: () => addSystemMessage("Your edit couldn't be sent. They still see the message as it was."),
    initialChat: restoredChat ? {
      partnerName: restoredChat.otherName,
      messageIds: restoredChat.messages.map(m => m.id),
      resend: restoredChat.messages
        .filter(m => m.sender === 'user' && m.status === 'sending' && m.type === 'text' && m.text)
        .map(m => ({ id: m.id, roomId: restoredChat.roomId, type: 'text' as const, text: m.text, authorName: identity,
          replyTo: m.replyTo ? { id: m.replyTo.id, text: m.replyTo.text, type: m.replyTo.type, fromSender: m.replyTo.mine } : undefined })),
    } : null,
  });

  // Keep the open chat with a person in this tab, so a reload can bring it back.
  // Saved again when the tab is hidden, so a quiet chat still counts as recent.
  const openChatRef = useRef<Parameters<typeof saveOpenChat>[0] | null>(null);
  useEffect(() => {
    const role = screen === 'VENT' || screen === 'LISTEN' ? screen : null;
    openChatRef.current = roomId && role && !isAiChat ? { roomId, role, otherName, partnerRole, messages } : null;
    if (openChatRef.current) saveOpenChat(openChatRef.current);
    else clearOpenChat();
  }, [roomId, screen, isAiChat, otherName, partnerRole, messages]);
  useEffect(() => {
    const save = () => { if (openChatRef.current) saveOpenChat(openChatRef.current); };
    const onHide = () => { if (document.visibilityState === 'hidden') save(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', save);
    return () => { document.removeEventListener('visibilitychange', onHide); window.removeEventListener('pagehide', save); };
  }, []);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const keyboardBox = useKeyboardSafeHeight(screen === 'VENT' || screen === 'LISTEN');
  const skyLayout = useSkyLayout(universeThoughts, skyRef, screen === 'UNIVERSE');
  const skyApiRef = useRef<SkyApi | null>(null);
  // Picking a feeling gathers its thoughts into a soft cluster in the middle of
  // the screen you are looking at (a sunflower spiral), between the header and
  // the Share button. Back to "All" and every thought drifts home to its own spot.
  const [gatherCam, setGatherCam] = useState<Pt>({ x: 0, y: 0 });
  useEffect(() => { setGatherCam(skyApiRef.current?.cam() ?? { x: 0, y: 0 }); }, [skyFeeling]);
  const gatherSpots = useMemo(() => {
    if (skyFeeling === 'all' || !skyLayout) return null;
    const box = skyRef.current?.getBoundingClientRect();
    if (!box) return null;
    const picked = universeThoughts.filter(t => t.feelings?.includes(skyFeeling)).slice(-60);
    if (!picked.length) return null;
    const header = Math.max(
      introBottom ? introBottom - box.top : 0,
      Array.from(document.querySelectorAll('[data-sky-header]')).reduce((m, n) => Math.max(m, n.getBoundingClientRect().bottom - box.top), 0),
    );
    const top = Math.max(header + 40, box.height * 0.18);
    const bottom = Math.min(box.height, window.innerHeight) - 190;
    const cx = box.width / 2, cy = (top + Math.max(top + 120, bottom)) / 2;
    const maxR = Math.max(60, Math.min(box.width / 2 - 36, (bottom - top) / 2));
    const sc = skyLayout.scale;
    // Never closer than this, so gathered emojis do not overlap; a big cluster
    // may reach past the screen edge (the Sky can be dragged).
    const step = Math.max(56 * sc, Math.min(64 * sc, maxR / Math.sqrt(picked.length + 0.5)));
    const out = new Map<string, Pt>();
    picked.forEach((t, i) => {
      const r = step * Math.sqrt(i + 0.5), a = i * 2.39996; // golden angle
      // Screen px to sky px, from where the camera was when the feeling was picked.
      out.set(t.id, { x: gatherCam.x + (cx + r * Math.cos(a)) / sc, y: gatherCam.y + (cy + r * Math.sin(a)) / sc });
    });
    return out;
  }, [skyFeeling, universeThoughts, skyLayout, introBottom, gatherCam]);

  // All-time Let Go counts for the feeling strip.
  useEffect(() => {
    if (screen !== 'UNIVERSE' || skyFeeling === 'all' || letGoTotals) return;
    fetch(`${API_BASE}/api/feelings`).then(r => r.json()).then(d => setLetGoTotals(d.letGo ?? {})).catch(() => setLetGoTotals({}));
  }, [screen, skyFeeling, letGoTotals]);

  // Chat scrolling: follow new messages only while the reader is at the bottom.
  // If they scrolled up to re-read, hold their place and count what they missed.
  const atBottomRef = useRef(true);
  const seenCountRef = useRef(0);
  const settlingRef = useRef(false);
  const [missed, setMissed] = useState(0);
  const jumpToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = true;
    settlingRef.current = true;
    el.scrollTop = el.scrollHeight;
    setMissed(0);
  }, []);
  const onChatScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (settlingRef.current) {
      // Ignore the in-between positions of our own smooth scroll.
      if (nearBottom) settlingRef.current = false;
      return;
    }
    atBottomRef.current = nearBottom;
    if (nearBottom) setMissed(0);
  }, []);

  useEffect(() => {
    const prev = seenCountRef.current;
    seenCountRef.current = messages.length;
    const added = messages.length >= prev ? messages.slice(prev) : [];
    const mine = added.some(m => m.sender === 'user');
    if (atBottomRef.current || mine || messages.length < prev || prev === 0) {
      jumpToBottom();
    } else {
      const unread = added.filter(m => m.sender === 'other' && m.authorName !== 'System').length;
      if (unread) setMissed(n => n + unread);
    }
  }, [messages, jumpToBottom]);

  useEffect(() => {
    if (atBottomRef.current) jumpToBottom();
  }, [isTyping, keyboardBox?.height, jumpToBottom]);

  // Entering a chat always starts at the latest message.
  useEffect(() => { jumpToBottom(); }, [screen, jumpToBottom]);

  // When the keyboard opens or closes (the page resizes), keep the latest message in view.
  useEffect(() => {
    if (screen !== 'VENT' && screen !== 'LISTEN') return;
    const onResize = () => { if (atBottomRef.current) jumpToBottom(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [screen, jumpToBottom]);


  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Search timer for matching screen
  useEffect(() => {
    if (screen !== 'MATCHING') return;
    const timer = setInterval(() => setSearchSeconds(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, [screen]);

  // Re-emit start_matching when socket connects while on the matching screen.
  // Covers the case where the phone connects after a slow initial handshake.
  useEffect(() => {
    if (isSocketConnected && screen === 'MATCHING' && matchingRole && !roomId) {
      socketRef.current?.emit('start_matching', { role: matchingRole, name: identity });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocketConnected]);

  useEffect(() => {
    // Socket connection managed by useSocket hook (see src/hooks/useSocket.ts)
  }, []);

  useEffect(() => {
    const onRemove = (e: Event) => {
      const { id } = (e as CustomEvent<{ id: string }>).detail;
      setUniverseThoughts(prev => prev.filter(t => t.id !== id));
    };
    const onAdd = (e: Event) => {
      const thought = (e as CustomEvent).detail;
      setUniverseThoughts(prev => prev.some(t => t.id === thought.id) ? prev : [...prev, thought]);
      // One of my waiting thoughts was approved while I am here.
      if (postStatus(thought.id) === 'waiting') {
        setPostStatus(thought.id, 'live');
        setStatusNudges(q => [...q, { kind: 'thought', outcome: 'live', id: thought.id, text: thought.text, thoughtId: thought.id }]);
      }
    };
    window.addEventListener('zuzu:remove_thought', onRemove);
    window.addEventListener('zuzu:add_thought', onAdd);
    return () => {
      window.removeEventListener('zuzu:remove_thought', onRemove);
      window.removeEventListener('zuzu:add_thought', onAdd);
    };
  }, []);

  const startMatching = (role: 'VENT' | 'LISTEN') => {
    if (matchingTimeoutRef.current) {
      clearTimeout(matchingTimeoutRef.current);
      matchingTimeoutRef.current = null;
    }
    setScreen('MATCHING');
    setMatchingRole(role);
    matchingRoleRef.current = role;
    setMessages([]);
    setRoomId(null);
    setIsAiChat(false);
    setShowAiPrompt(false);
    setSearchSeconds(0);

    console.log('[Zuzu] start_matching emitted', { role, name: identity, socketConnected: socketRef.current?.connected });
    socketRef.current?.emit('start_matching', { role, name: identity });

    matchingTimeoutRef.current = setTimeout(() => {
      setShowAiPrompt(true);
    }, 10000);
  };

  const acceptAiMatch = (direct = false) => {
    socketRef.current?.emit('stop_matching', { reason: 'ai' });
    if (matchingTimeoutRef.current) { clearTimeout(matchingTimeoutRef.current); matchingTimeoutRef.current = null; }
    setIsAiChat(true);
    setShowAiPrompt(false);
    const role = matchingRoleRef.current || 'VENT';
    const aiIdentity = `${ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]} ${ELEMENTS[Math.floor(Math.random() * ELEMENTS.length)]}`;
    setOtherName(aiIdentity);
    setScreen(role); // Transition to chat screen (VENT or LISTEN)
    
    // Say plainly what this chat is, so nobody is confused about who's talking.
    const aboutNote = role === 'VENT'
      ? `You're chatting with Zuzu's AI Companion. It ${direct ? 'is here to listen' : 'listens until someone is around to chat'}.`
      : `Zuzu's AI Companion is playing someone who needs to vent, so you can listen${direct ? '' : ' until someone is around to chat'}. The story is made up.`;
    setMessages([{ id: 'system-ai-about', text: aboutNote, type: 'text', sender: 'other', authorName: 'System' }]);
    // A new id for each AI chat: it keeps the AI venter the same person throughout.
    aiChatIdRef.current = newMessageId();
    const seq = ++aiReplySeqRef.current;
    setIsTyping(true);
    replyFromCompanion({ messages: [], opening: true, ...(direct ? { direct: true } : {}) }, seq, aiIdentity);
  };

  // The AI Companion page can open an AI chat straight away, without the search
  // for a person (navigate('/', { state: { startAi: 'VENT' | 'LISTEN' } })).
  // Topic pages (Vent anonymously, Talk to a stranger) start the search for a
  // person the same way: { startChat: 'VENT' | 'LISTEN' }.
  const aiStartedRef = useRef(false);
  useEffect(() => {
    const st = routeState as { startAi?: string; startChat?: string } | null;
    const role = st?.startAi ?? st?.startChat;
    if ((role !== 'VENT' && role !== 'LISTEN') || aiStartedRef.current) return;
    aiStartedRef.current = true;
    navigate('/', { replace: true, state: null }); // a reload or Back does not start another
    if (st?.startChat) { startMatching(role); return; }
    matchingRoleRef.current = role;
    setMatchingRole(role);
    acceptAiMatch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNight) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isNight]);

  useEffect(() => {
    if (audioRef.current) {
      // On Let Go, only that page's own BGM should play — pause the site ambient.
      if (screen === 'LETGO') { audioRef.current.pause(); return; }
      audioRef.current.muted = isMuted;
      audioRef.current.volume = 0.3;
      if (!isMuted) {
        audioRef.current.play().catch(() => {});
      }
    }
  }, [isMuted, screen]);

  const [greeting] = useState(() => {
    const hour = new Date().getHours();
    
    // Late Night / Insomnia (12 AM - 4 AM)
    if (hour >= 0 && hour < 4) {
      const options = [
        { text: "Late night thoughts?", emoji: "🌙" },
        { text: "The world is quiet.", emoji: "✨" },
        { text: "Still awake?", emoji: "🦉" },
        { text: "Midnight musings.", emoji: "🌌" }
      ];
      return options[Math.floor(Math.random() * options.length)];
    }
    
    // Early Dawn (4 AM - 6 AM)
    if (hour >= 4 && hour < 6) {
      return { text: "The dawn is near.", emoji: "🌅" };
    }
    
    // Morning (6 AM - 11 AM)
    if (hour >= 6 && hour < 11) {
      const options = [
        { text: "Hi Sunshine", emoji: "🌞" },
        { text: "A fresh start.", emoji: "🌱" },
        { text: "Rise & shine.", emoji: "✨" },
        { text: "Good morning.", emoji: "☀️" }
      ];
      return options[Math.floor(Math.random() * options.length)];
    }
    
    // Midday (11 AM - 1 PM)
    if (hour >= 11 && hour < 13) {
      return { text: "Namaste", emoji: "☀️" };
    }
    
    // Afternoon (1 PM - 5 PM)
    if (hour >= 13 && hour < 17) {
      const options = [
        { text: "Hi cutie", emoji: "😊" },
        { text: "Good afternoon.", emoji: "🍃" },
        { text: "Taking a break?", emoji: "☕" },
        { text: "Hello there.", emoji: "🌤️" }
      ];
      return options[Math.floor(Math.random() * options.length)];
    }
    
    // Evening (5 PM - 8 PM)
    if (hour >= 17 && hour < 20) {
      const options = [
        { text: "How was your day?", emoji: "🌇" },
        { text: "Unwinding?", emoji: "🍷" },
        { text: "The day settles.", emoji: "🌆" },
        { text: "Good evening.", emoji: "🌇" }
      ];
      return options[Math.floor(Math.random() * options.length)];
    }
    
    // Night (8 PM - 12 AM)
    if (hour >= 20) {
      const options = [
        { text: "Good night", emoji: "🌙" },
        { text: "Reflecting?", emoji: "🕯️" },
        { text: "Quiet time.", emoji: "🌌" },
        { text: "Rest well.", emoji: "🌙" }
      ];
      return options[Math.floor(Math.random() * options.length)];
    }

    return { text: "Hi there", emoji: "😊" };
  });

  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordStartRef = useRef(0);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const captionRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // Fetch the full emoji picker as soon as a chat opens (see loadFullEmojiPicker).
  useEffect(() => {
    if (screen === 'VENT' || screen === 'LISTEN') loadFullEmojiPicker().catch(() => { /* ChunkBoundary handles it */ });
    if (screen === 'MATCHING' || screen === 'VENT' || screen === 'LISTEN' || screen === 'UNIVERSE') prefetchLater();
  }, [screen]);
  // Emoji+ swaps the quick grid for the full picker inside the same panel.
  const [emojiFull, setEmojiFull] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  // Which AI Companion reply is the newest one (older ones are dropped).
  const aiReplySeqRef = useRef(0);
  const aiChatIdRef = useRef('');

  // --- AI Companion: ask, then show the reply like a person typing. ---
  // The server builds the prompt (#30); this only sends the mode and the chat.
  const askCompanion = async (body: Record<string, unknown>): Promise<string> => {
    const reqBody = JSON.stringify({
      ...body,
      mode: matchingRoleRef.current === 'LISTEN' ? 'venter' : 'listener',
      chatId: aiChatIdRef.current,
      locale: navigator.language,
      // Only on the first message: the temporary name (for the owner's alert) and,
      // on the owner's own device, the owner token so test chats send no alert.
      ...(body.opening ? { name: identityRef.current, ownerToken: localStorage.getItem('zuzu_owner_token') || undefined } : {}),
    });
    // Retry up to 4 times: the first request sometimes fails on mobile.
    let response: Response | null = null;
    let lastErr: unknown;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1200 * attempt));
        response = await fetch(`${API_BASE}/api/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: reqBody });
        break;
      } catch (err) { lastErr = err; }
    }
    if (!response) throw lastErr;
    if (!response.ok) throw new Error(`Server error ${response.status}`);
    let text = "";
    for (const line of (await response.text()).split("\n")) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6).trim();
      if (data === "[DONE]") break;
      try { const d = JSON.parse(data); if (d.text) text += d.text; } catch { /* skip a broken chunk */ }
    }
    return text;
  };
  // One reply can be up to three separate texts (the model puts ||| between them).
  const splitCompanionText = (text: string) => text
    .replace(/\s*\u2014\s*/g, ', ')
    .split(/\n?[ \t]*\|\|\|[ \t]*\n?/)
    .map(t => t.trim())
    .filter(Boolean)
    .slice(0, 3);
  // Roughly how long a person takes to type it: quick for a few words, longer for more.
  const typingTime = (text: string) => {
    const words = text.trim().split(/\s+/).length;
    const base = words <= 4 ? 0.9 + Math.random() : words < 20 ? 2 + Math.random() * 2 : words <= 50 ? 4 + Math.random() * 3 : 7 + Math.random() * 4;
    return base * 1000;
  };
  const LISTENER_OPENERS = ["hey. what's going on?", "hi, I'm here. what's on your mind tonight?", "hey you. how's today been, honestly?", "hi. want to talk about it?"];
  const VENTER_OPENERS = ["hey. can I just rant for a sec", "ok so I don't even know how to start this", "hi. weird day. can I tell you about it?", "this is kind of random but I need to get something off my chest"];
  const replyFromCompanion = async (body: Record<string, unknown>, seq: number, name: string) => {
    const wait = (ms: number) => new Promise(r => setTimeout(r, ms));
    const add = (text: string) => {
      setMessages(prev => [...prev, { id: newMessageId(), text, type: 'text', sender: 'other', authorName: name, timestamp: Date.now() }]);
      readAloud(text);
      announceMessage();
    };
    try {
      const parts = splitCompanionText(await askCompanion(body));
      if (!parts.length) throw new Error("Empty response from companion");
      for (let i = 0; i < parts.length; i++) {
        if (seq !== aiReplySeqRef.current) return; // a newer message is being answered
        setIsTyping(true);
        await wait(i === 0 ? typingTime(parts[i]) : 500 + typingTime(parts[i]) * 0.7);
        if (seq !== aiReplySeqRef.current) return;
        add(parts[i]);
        if (i < parts.length - 1) { setIsTyping(false); await wait(400 + Math.random() * 600); }
      }
      setIsTyping(false);
      // The reply mentioned another part of Zuzu: a small chip to go there.
      if (body.nudge) {
        await wait(600);
        if (seq !== aiReplySeqRef.current) return;
        setMessages(prev => [...prev, { id: 'nudge-' + Date.now(), type: 'text', sender: 'other', authorName: 'System', kind: 'nudge', nudge: body.nudge as AiNudge }]);
      }
    } catch (error: any) {
      console.error("Companion Error:", error);
      if (seq !== aiReplySeqRef.current) return;
      setIsTyping(false);
      if (body.opening) {
        const pool = matchingRoleRef.current === 'LISTEN' ? VENTER_OPENERS : LISTENER_OPENERS;
        add(pool[Math.floor(Math.random() * pool.length)]);
        return;
      }
      setMessages(prev => [...prev, {
        id: 'ai-error-' + Date.now(),
        text: `I'm sorry, something went wrong (${error?.message || 'unknown error'}). Try again?`,
        type: 'text',
        sender: 'other',
        authorName: name,
        timestamp: Date.now(),
      }]);
    }
  };
  // The prompts above the chat box follow the moment in the chat (#64): a new set
  // only when the moment changes, so they don't shuffle while you look at them.
  const chatLines = messages.filter(m => !m.kind && m.authorName !== 'System' && !m.id.startsWith('ai-error-'));
  const promptStageNow = promptStage(chatLines, currentTime.getTime());
  const myLineCount = chatLines.filter(m => m.sender === 'user').length;
  const chatSnippets = useMemo(
    () => promptsFor(matchingRole === 'LISTEN' ? 'LISTEN' : 'VENT', promptStageNow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchingRole, screen, promptStageNow, roomId, isAiChat],
  );
  const promptsVisible = showPrompts(promptStageNow, myLineCount, !!inputText.trim());

  // Keep the chat input auto-sized to its content (up to a max), and scroll to the
  // caret once it hits that max so long messages stay visible while typing on mobile.
  const CHAT_INPUT_MAX_H = 160;
  const CHAT_MAX_CHARS = 5000;       // also enforced on the server
  const VOICE_MAX_SECONDS = 180;     // voice notes stop and send after 3 minutes
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, CHAT_INPUT_MAX_H);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > CHAT_INPUT_MAX_H ? 'auto' : 'hidden';
  }, [inputText]);

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(t) && !emojiButtonRef.current?.contains(t)) {
        setShowEmojiPicker(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setShowEmojiPicker(false);
      emojiButtonRef.current?.focus();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', onKey); };
  }, [showEmojiPicker]);

  const pickEmoji = (emoji: string) => {
    setInputText(prev => (prev + emoji).slice(0, CHAT_MAX_CHARS));
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };

  // A photo to send, from the photo button or pasted (Cmd/Ctrl + V): always a preview
  // first, with an optional caption, and it only goes out from the preview (like WhatsApp).
  // A paste or a tap can be a mistake, so nothing is ever sent straight away.
  const [pendingImage, setPendingImage] = useState<{ file: File; url: string } | null>(null);
  const [photoCaption, setPhotoCaption] = useState('');
  const choosePendingImage = (file: File) => {
    if (!file.type.startsWith('image/') || isAiChat) return;
    setShowEmojiPicker(false);
    setShowGifs(false);
    setPhotoCaption('');
    setPendingImage(prev => {
      if (prev) URL.revokeObjectURL(prev.url);
      return { file, url: URL.createObjectURL(file) };
    });
    // On a computer, the caption box is ready to type in (Enter sends, Esc cancels).
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) setTimeout(() => document.getElementById('photo-caption')?.focus(), 60);
  };
  const clearPendingImage = () => {
    setPendingImage(prev => { if (prev) URL.revokeObjectURL(prev.url); return null; });
    setPhotoCaption('');
    textareaRef.current?.focus();
  };
  const sendPendingImage = () => {
    if (!pendingImage) return;
    handleImageUpload(pendingImage.file, photoCaption);
    clearPendingImage();
  };
  // The photo button opens a small menu: a photo from the device, or a GIF.
  const [attachMenu, setAttachMenu] = useState(false);
  const [showGifs, setShowGifs] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const attachRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!attachMenu) return;
    const close = (e: PointerEvent) => { if (!attachRef.current?.contains(e.target as Node)) setAttachMenu(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [attachMenu]);
  // A GIF goes out like a photo (so it arrives blurred, the same as photos).
  const sendGif = (gif: Gif) => {
    setShowGifs(false);
    if (!roomId || isAiChat) return;
    const id = newMessageId();
    const reply = takeReply();
    setMessages(prev => [...prev, { id, type: 'image', url: gif.url, sender: 'user', authorName: identity, timestamp: Date.now(), replyTo: reply.quote }]);
    sendChat({ id, roomId, type: 'image', url: gif.url, authorName: identity, replyTo: reply.wire });
  };
  // A chat that ends (or a new one) drops the unsent photo and closes the pickers.
  useEffect(() => { if (!roomId) { clearPendingImage(); setShowGifs(false); setAttachMenu(false); } }, [roomId]);
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isAiChat || !roomId) return;
    const item = Array.from(e.clipboardData?.items ?? []).find(i => i.kind === 'file' && i.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (!file) return; // plain text pastes as usual
    e.preventDefault();
    choosePendingImage(file);
  };

  const handleImageUpload = async (file: File, caption = '') => {
    if (isUploadingImage || !roomId) return;
    setIsUploadingImage(true);
    try {
      const compressedBlob = await compressImage(file);
      const base64Image = await fileToBase64(compressedBlob);
      const id = newMessageId();
      const reply = takeReply();
      const text = caption.trim().slice(0, CHAT_MAX_CHARS) || undefined; // an optional caption under the photo
      if (text && looksLikeCrisis(text)) {
        setMessages(prev => prev.some(m => m.kind === 'care-self') ? prev : [
          ...prev,
          { id: 'care-' + Date.now(), type: 'text', sender: 'other', authorName: 'System', kind: 'care-self' },
        ]);
      }
      setMessages(prev => [...prev, {
        id,
        type: 'image',
        url: base64Image,
        text,
        sender: 'user',
        authorName: identity,
        timestamp: Date.now(),
        replyTo: reply.quote,
      }]);
      if (!sendChat({ id, roomId, type: 'image', url: base64Image, text, authorName: identity, replyTo: reply.wire })) {
        addSystemMessage('This photo is too large to send. Try a smaller one.');
      }
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Pick a format this browser can record and every browser can play:
  // MP4 (AAC) first (plays on iPhone too), then WebM (Opus).
  const pickAudioType = () => {
    const types = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];
    return types.find(t => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t)) ?? '';
  };

  const setRecordingSignal = (recording: boolean) => {
    if (roomIdRef.current && !isAiChat) socketRef.current?.emit('recording', { roomId: roomIdRef.current, recording });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickAudioType();
      const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const type = (mediaRecorder.mimeType || mimeType || 'audio/webm').split(';')[0];
        const audioBlob = new Blob(audioChunksRef.current, { type });
        if (audioBlob.size === 0) return;
        const audioUrl = URL.createObjectURL(audioBlob);
        const base64Audio = await fileToBase64(audioBlob);
        // The length, measured here: a browser often cannot read it from a fresh recording.
        const seconds = Math.max(1, Math.round((performance.now() - recordStartRef.current) / 100) / 10);

        const reply = takeReply();
        const newMessage: Message = {
          id: newMessageId(),
          type: 'audio',
          url: audioUrl,
          duration: seconds,
          sender: 'user',
          authorName: identity,
          timestamp: Date.now(),
          replyTo: reply.quote,
        };

        setMessages(prev => [...prev, newMessage]);
        const room = roomIdRef.current;
        if (room) {
          if (!sendChat({ id: newMessage.id, roomId: room, type: 'audio', url: base64Audio, duration: seconds, authorName: identity, replyTo: reply.wire })) {
            addSystemMessage('This voice note is too long to send. Try a shorter one.');
          }
        }
      };

      mediaRecorder.start();
      recordStartRef.current = performance.now();
      setIsRecording(true);
      setRecordingSignal(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      addSystemMessage("Zuzu couldn't use your microphone. Please allow microphone access in your browser settings and try again.");
    }
  };

  const stopRecording = (action: 'send' | 'delete') => {
    if (mediaRecorderRef.current && isRecording) {
      if (action === 'delete') {
        mediaRecorderRef.current.onstop = null; // Prevent sending
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingSignal(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      setRecordingTime(0);
    }
  };

  // Long notes stop and send on their own.
  useEffect(() => {
    if (isRecording && recordingTime >= VOICE_MAX_SECONDS) stopRecording('send');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, recordingTime]);

  const addSystemMessage = (text: string) => {
    setMessages(prev => [...prev, { id: 'system-' + Date.now(), text, type: 'text', sender: 'other', authorName: 'System' }]);
  };

  const reactionSentAt = useRef(0);
  const pendingReactions = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // React to one message (or take the reaction back). Human chats only.
  const reactTo = (messageId: string, emoji: string | null) => {
    if (isAiChat || !roomId) return;
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: { ...m.reactions, mine: emoji ?? undefined } } : m));
    // The server drops reactions sent within 250ms of the last one. Send at once when
    // we can, otherwise send the latest choice once the gap has passed, so the
    // partner always ends up seeing what the sender sees.
    const room = roomId;
    const send = () => {
      reactionSentAt.current = Date.now();
      socketRef.current?.emit('react', { roomId: room, messageId, emoji });
    };
    const pending = pendingReactions.current;
    if (pending.has(messageId)) clearTimeout(pending.get(messageId));
    const wait = reactionSentAt.current + 300 - Date.now();
    if (wait <= 0) { pending.delete(messageId); send(); return; }
    pending.set(messageId, setTimeout(() => { pending.delete(messageId); send(); }, wait));
  };

  // "How did you feel?" after a chat with a person ends. Asked once per chat,
  // only if both people wrote something, and never after a block.
  const [feelingPopup, setFeelingPopup] = useState<string | null>(null); // room id
  const [feelingNote, setFeelingNote] = useState<string | null>(null); // note from the other person
  const feelingAskedRef = useRef<Set<string>>(new Set());
  const blockedRoomRef = useRef<string | null>(null);
  const lastRoomRef = useRef<string | null>(null);
  const chatWasMutual = () => {
    const real = messages.filter(m => !m.kind && m.authorName !== 'System');
    return real.some(m => m.sender === 'user') && real.some(m => m.sender === 'other');
  };
  const canAskFeeling = (room: string | null) =>
    !!room && !isAiChat && room !== blockedRoomRef.current && !feelingAskedRef.current.has(room) && chatWasMutual();
  const sendFeeling = (room: string, feeling: FeelingKey) => {
    socketRef.current?.emit('chat_feeling', { roomId: room, feeling });
  };
  // The other person left: ask gently, inside the chat.
  useEffect(() => {
    const prev = lastRoomRef.current;
    lastRoomRef.current = roomId;
    if (roomId || !prev || (screen !== 'VENT' && screen !== 'LISTEN')) return;
    if (!canAskFeeling(prev)) return;
    feelingAskedRef.current.add(prev);
    setMessages(m => [...m, { id: 'feeling-' + prev, type: 'text', sender: 'other', authorName: 'System', kind: 'feeling', roomId: prev }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);
  useEffect(() => {
    if (!feelingNote) return;
    const t = setTimeout(() => setFeelingNote(null), 6000);
    return () => clearTimeout(t);
  }, [feelingNote]);

  const closeChatMenu = () => {
    setChatMenu(null);
    setReportReason(null);
    setAlsoBlock(false);
  };

  // A new chat starts with a fresh menu.
  useEffect(() => {
    if (roomId) {
      setReportSent(false);
      closeChatMenu();
    }
  }, [roomId]);

  const blockPartner = () => {
    if (!roomId) return;
    blockedRoomRef.current = roomId;
    socketRef.current?.emit('block_partner', { roomId });
    setRoomId(null);
    setIsTyping(false);
  };

  const submitReport = () => {
    if (!roomId || !reportReason) return;
    socketRef.current?.emit('report_partner', { roomId, reason: reportReason, block: alsoBlock });
    setReportSent(true);
    if (alsoBlock) {
      blockPartner();
      addSystemMessage('Thank you for letting Zuzu know. You blocked this person and the chat has ended.');
    } else {
      addSystemMessage('Thank you for letting Zuzu know. It will be looked into.');
    }
    closeChatMenu();
  };

  const confirmBlock = () => {
    blockPartner();
    addSystemMessage("You blocked this person. The chat has ended.");
    closeChatMenu();
  };

  // Reconnection: "Trying to reconnect..." at once, "End chat" after 30s, and the
  // chat ends at 5 minutes (the server keeps a place for that long).
  const CONN_END_OFFER_MS = 30_000;
  const CONN_GRACE_MS = CHAT_GRACE_MS;
  const activeIssue = roomId && !isAiChat ? connIssue : null;
  const [issueNow, setIssueNow] = useState(() => Date.now());
  useEffect(() => {
    if (!activeIssue) return;
    const t = setInterval(() => {
      const now = Date.now();
      setIssueNow(now);
      // Still offline after the grace time: the chat is over on the server too.
      if (activeIssue.who === 'me' && now - activeIssue.since >= CONN_GRACE_MS && roomId) {
        endChat(roomId);
        setRoomId(null);
        setConnIssue(null);
        addSystemMessage('The connection was lost for too long, so this chat has ended.');
      }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIssue?.who, activeIssue?.since, roomId]);
  // Leave the chat yourself (the X, or "Leave" after the back arrow).
  const [confirmLeave, setConfirmLeave] = useState(false);
  useEffect(() => { if (!roomId) setConfirmLeave(false); }, [roomId]);
  const leaveChat = () => {
    const endedRoom = roomId;
    const askFeeling = canAskFeeling(endedRoom);
    if (endedRoom) endChat(endedRoom);
    lastRoomRef.current = null; // leaving yourself: the popup asks, not the chat
    setRoomId(null); // the chat is over here too, so a reconnect does not try to rejoin it
    setPartnerRole(null);
    setScreen('LANDING');
    if (askFeeling && endedRoom) {
      feelingAskedRef.current.add(endedRoom);
      setFeelingPopup(endedRoom);
      return;
    }
    // Ask attribution if due; otherwise fall back to the social popup.
    if (!maybeShowHeardAbout()) setShowSocialPopup(true);
  };
  const endChatNow = () => {
    if (!roomId) return;
    endChat(roomId);
    setRoomId(null);
    setConnIssue(null);
    addSystemMessage('You ended the chat.');
  };

  // --- Meet Again ---------------------------------------------------------------
  const [meetBusy, setMeetBusy] = useState(false);
  const [meetError, setMeetError] = useState<string | null>(null);
  const [meetPairs, setMeetPairs] = useState<MeetPairInfo[]>([]);
  const [meetMax, setMeetMax] = useState(3);
  const [meetLocal, setMeetLocal] = useState<Record<string, MeetLocal>>(loadMeetLocal);
  const [meetToast, setMeetToast] = useState(false); // "You'll meet again" while the sheet is closed
  const meetCtxRef = useRef(meetCtx);
  meetCtxRef.current = meetCtx;
  const meetOpenRef = useRef(meetOpen);
  meetOpenRef.current = meetOpen;
  const updateMeetLocal = (f: (all: Record<string, MeetLocal>) => Record<string, MeetLocal>) =>
    setMeetLocal(prev => { const next = f(prev); saveMeetLocal(next); return next; });
  // What this device remembers about a new pair: their name then, your role, the date.
  const rememberPair = (pairId: string, ctx: MeetCtx | null) => updateMeetLocal(all => all[pairId] ? all : {
    ...all, [pairId]: { name: ctx?.name || 'Someone', role: ctx?.role ?? null, date: new Date().toISOString(), note: ctx?.keptNote ?? '' },
  });
  // The server's list decides which pairs exist. Ended ones drop from this device too.
  const refreshMeetPairs = () => {
    socketRef.current?.timeout(8000).emit('meet_list', (err: Error | null, res?: { ok: boolean; max: number; pairs: MeetPairInfo[] }) => {
      if (err || !res?.ok) return;
      setMeetPairs(res.pairs);
      setMeetMax(res.max);
      const ids = new Set(res.pairs.map(p => p.id));
      updateMeetLocal(all => Object.fromEntries(Object.entries(all).filter(([id]) => ids.has(id))));
    });
  };
  useEffect(() => {
    const s = socketRef.current;
    if (!s || !isSocketConnected) return;
    refreshMeetPairs();
    const onPair = ({ roomId: room, pairId }: { roomId: string; pairId: string }) => {
      const ctx = meetCtxRef.current;
      if (ctx?.roomId !== room) return;
      rememberPair(pairId, ctx);
      setMeetCtx({ ...ctx, state: 'matched', pairId });
      if (!meetOpenRef.current) setMeetToast(true);
    };
    const onChanged = () => refreshMeetPairs();
    const onOffer = (o: { offerId: string; pairId: string; expiresIn: number }) => setMeetOffer({ ...o, receivedAt: Date.now(), waiting: false });
    const onOfferClosed = ({ offerId }: { offerId: string }) => setMeetOffer(prev => (prev?.offerId === offerId ? null : prev));
    s.on('meet_matched', onPair);
    s.on('meet_changed', onChanged);
    s.on('meet_offer', onOffer);
    s.on('meet_offer_closed', onOfferClosed);
    return () => { s.off('meet_matched', onPair); s.off('meet_changed', onChanged); s.off('meet_offer', onOffer); s.off('meet_offer_closed', onOfferClosed); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocketConnected]);
  // A chat restored after a reload has no Meet Again state yet.
  useEffect(() => {
    if (roomId && !isAiChat && meetCtx?.roomId !== roomId) setMeetCtx({ roomId, name: otherName, role: matchingRole, state: 'idle', startedAt: Date.now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, isAiChat]);
  // Ask Zuzu once per chat where this person stands (a reload forgets an earlier tap or pair).
  const meetSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    const room = meetCtx?.roomId;
    if (!room || room !== roomId || isAiChat || !isSocketConnected || meetSyncedRef.current === room) return;
    meetSyncedRef.current = room;
    socketRef.current?.timeout(8000).emit('meet_status', { roomId: room }, (err: Error | null, res?: { ok: boolean; state: MeetTapState | 'off'; pairId?: string }) => {
      if (err || !res?.ok || (res.state !== 'matched' && res.state !== 'waiting')) return;
      const state = res.state;
      // Only fill in a chat that still shows nothing (a tap meanwhile wins).
      setMeetCtx(c => (c && c.roomId === room && c.state === 'idle' ? { ...c, state, pairId: res.pairId } : c));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetCtx?.roomId, roomId, isAiChat, isSocketConnected]);
  useEffect(() => {
    if (!meetToast) return;
    const t = setTimeout(() => setMeetToast(false), 10_000);
    return () => clearTimeout(t);
  }, [meetToast]);
  // Meet again shows for a chat with a person once both have written (or it is a reunion),
  // while it lasts and on its end card.
  const meetAvailable = !isAiChat && !!meetCtx && (roomId === null || roomId === meetCtx.roomId)
    && (meetCtx.state === 'matched' || chatWasMutual());
  const openMeet = () => { setMeetError(null); setMeetToast(false); setMeetOpen(true); markMeetHintSeen(); };

  // "Meet again?": in a person's first 3 chats, once both have written 3 messages and
  // the chat is 2 minutes old, the button shows its name for 8 seconds. Never again once
  // they have tapped it. Only counts ("shown", "tapped") go to the server.
  const MEET_HINT_KEY = 'zuzu_meet_hint';
  // The first time, a short tip card in the chat explains it too (`intro`: shown once, ever).
  type MeetHint = { shown?: number; tapped?: boolean; intro?: boolean };
  const readMeetHint = (): MeetHint => { try { return JSON.parse(localStorage.getItem(MEET_HINT_KEY) || '{}'); } catch { return {}; } };
  const writeMeetHint = (h: MeetHint) => { try { localStorage.setItem(MEET_HINT_KEY, JSON.stringify(h)); } catch { /* private mode */ } };
  const [meetHintRoom, setMeetHintRoom] = useState<string | null>(null); // the chat the label shows in, while it shows
  const meetHintDoneRef = useRef<string | null>(null);                   // the chat it already showed in
  function markMeetHintSeen() {
    if (meetHintRoom && meetHintRoom === roomId) socketRef.current?.emit('meet_hint', { event: 'tap' });
    setMeetHintRoom(null);
    if (!readMeetHint().tapped) writeMeetHint({ ...readMeetHint(), tapped: true });
  }
  useEffect(() => {
    if (!roomId || isAiChat || !meetCtx || meetCtx.roomId !== roomId || meetCtx.state !== 'idle') return;
    if (meetHintDoneRef.current === roomId) return;
    const h = readMeetHint();
    if (h.tapped || (h.shown ?? 0) >= 3) return;
    const real = messages.filter(m => !m.kind && m.authorName !== 'System');
    if (real.filter(m => m.sender === 'user').length < 3 || real.filter(m => m.sender === 'other').length < 3) return;
    const room = roomId;
    const t = setTimeout(() => {
      if (roomIdRef.current !== room || meetHintDoneRef.current === room) return;
      meetHintDoneRef.current = room;
      const first = !readMeetHint().shown && !readMeetHint().intro;
      writeMeetHint({ ...readMeetHint(), shown: (readMeetHint().shown ?? 0) + 1, ...(first ? { intro: true } : {}) });
      if (first) setMessages(prev => prev.some(m => m.kind === 'meet-intro') ? prev : [...prev, { id: 'meet-intro-' + room, type: 'text', sender: 'other', authorName: 'System', kind: 'meet-intro', roomId: room }]);
      setMeetHintRoom(room);
      socketRef.current?.emit('meet_hint', { event: 'shown' });
    }, Math.max(0, meetCtx.startedAt + 120_000 - Date.now()));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, roomId, isAiChat, meetCtx?.roomId, meetCtx?.state]);
  useEffect(() => {
    if (!meetHintRoom) return;
    const t = setTimeout(() => setMeetHintRoom(null), 8000);
    return () => clearTimeout(t);
  }, [meetHintRoom]);
  // Closing the popup sends a new or changed note to Zuzu (once, not on every key).
  const sentNotesRef = useRef<Record<string, string>>({});
  const closeMeet = () => {
    setMeetOpen(false);
    const pairId = meetCtx?.pairId;
    const note = pairId ? meetLocal[pairId]?.note.trim() : '';
    if (pairId && note && sentNotesRef.current[pairId] !== note) {
      sentNotesRef.current[pairId] = note;
      socketRef.current?.emit('meet_note', { pairId, note });
    }
  };
  const meetTap = (on: boolean) => {
    const ctx = meetCtx;
    if (!ctx || !socketRef.current) return;
    setMeetBusy(true);
    setMeetError(null);
    socketRef.current.timeout(8000).emit('meet_tap', { roomId: ctx.roomId, on }, (err: Error | null, res?: { ok: boolean; state: MeetTapState | 'off'; pairId?: string }) => {
      setMeetBusy(false);
      if (err || !res) { setMeetError("Couldn't reach Zuzu. Try again."); return; }
      const state: MeetTapState = res.state === 'off' ? 'idle' : res.state;
      if (state === 'matched' && res.pairId) { rememberPair(res.pairId, ctx); refreshMeetPairs(); }
      setMeetCtx(c => (c && c.roomId === ctx.roomId ? { ...c, state, pairId: res.pairId ?? c.pairId } : c));
    });
  };
  const meetLetGo = (pairId: string) => {
    setMeetBusy(true);
    setMeetError(null);
    socketRef.current?.timeout(8000).emit('meet_remove', { pairId }, (err: Error | null) => {
      setMeetBusy(false);
      if (err) { setMeetError("Couldn't reach Zuzu. Try again."); return; }
      setMeetPairs(ps => ps.filter(p => p.id !== pairId));
      updateMeetLocal(all => { const next = { ...all }; delete next[pairId]; return next; });
      if (meetCtx?.pairId === pairId) setMeetCtx(c => c && { ...c, state: 'idle', pairId: undefined, keptNote: meetLocal[pairId]?.note || c.keptNote });
      // Made room: the tap that was waiting for a spot goes through now.
      if (meetCtx?.state === 'full') meetTap(true);
    });
  };
  const meetNote = (pairId: string, note: string) => updateMeetLocal(all => ({
    ...all, [pairId]: { ...(all[pairId] ?? { name: meetCtx?.name || 'Someone', role: meetCtx?.role ?? null, date: new Date().toISOString() }), note },
  }));
  const answerMeetOffer = (accept: boolean) => {
    const o = meetOffer;
    if (!o) return;
    if (o.offerId === 'demo') { setMeetOffer(null); return; }
    if (!accept) { setMeetOffer(null); socketRef.current?.emit('meet_offer_answer', { offerId: o.offerId, accept: false }); return; }
    setMeetOffer({ ...o, waiting: true });
    socketRef.current?.timeout(8000).emit('meet_offer_answer', { offerId: o.offerId, accept: true, role: matchingRoleRef.current ?? 'VENT' },
      (err: Error | null, res?: { ok: boolean }) => { if (err || !res?.ok) setMeetOffer(prev => (prev?.offerId === o.offerId ? null : prev)); });
  };

  const cancelEdit = () => { setEditingId(null); setInputText(''); };
  // The message box is taller than one line (the photo button hides meanwhile).
  const [inputTall, setInputTall] = useState(false);
  const inputTallAt = useRef<number | null>(null);
  useEffect(() => { if (!inputText) { inputTallAt.current = null; setInputTall(false); } }, [inputText]);
  // Save an edit: the new text shows here at once, and goes to the other person
  // with a small "edited" (unless the original had not gone out yet).
  const saveEdit = () => {
    const id = editingId;
    const text = inputText.trim().slice(0, CHAT_MAX_CHARS);
    cancelEdit();
    const original = messages.find(m => m.id === id);
    if (!id || !roomId || !text || !original || original.text === text) return;
    const how = editChat({ id, roomId, text });
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, text, edited: how === 'sent' ? true : m.edited } : m)));
    if (looksLikeCrisis(text)) {
      setMessages(prev => prev.some(m => m.kind === 'care-self') ? prev : [
        ...prev,
        { id: 'care-' + Date.now(), type: 'text', sender: 'other', authorName: 'System', kind: 'care-self' },
      ]);
    }
  };

  const handleSendMessage = () => {
    if (editingId) { saveEdit(); return; }
    const messageText = inputText.trim().slice(0, CHAT_MAX_CHARS);
    if (!messageText) return;
 
    const reply = takeReply();
    const newMessage: Message = {
      id: newMessageId(),
      text: messageText,
      type: 'text',
      sender: 'user',
      timestamp: Date.now(),
      replyTo: reply.quote,
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputText('');

    // If this sounds like the person may be in danger, show a gentle support
    // card once per chat (only on this device), in human and AI chats alike.
    if (looksLikeCrisis(messageText)) {
      setMessages(prev => prev.some(m => m.kind === 'care-self') ? prev : [
        ...prev,
        { id: 'care-' + Date.now(), type: 'text', sender: 'other', authorName: 'System', kind: 'care-self' },
      ]);
    }

    if (isAiChat) {

      // Several quick messages get one reply, like a person reading them all:
      // only the newest request is answered, and it sees the whole chat.
      const seq = ++aiReplySeqRef.current;
      setIsTyping(true);
      const nudge = pickNudge({
        text: messageText,
        ventingToAi: matchingRole !== 'LISTEN',
        myEarlier: messages.filter(m => m.sender === 'user' && m.type === 'text' && !m.kind).length,
        nudged: messages.flatMap(m => m.kind === 'nudge' && m.nudge ? [m.nudge] : []),
        heavy: looksLikeCrisis(messageText) || messages.some(m => m.kind === 'care-self'),
      });
      replyFromCompanion({
        ...(nudge ? { nudge } : {}),
        messages: [
          ...messages.filter(m => m.type === 'text' && !m.kind && m.authorName !== 'System' && m.text && !m.id.startsWith('ai-error-')),
          { id: 'current', text: messageText, sender: 'user', type: 'text' as const, replyTo: reply.quote },
        ].map(m => m.replyTo
          ? { ...m, text: `[replying to ${m.sender === 'user' ? (m.replyTo.mine ? 'my own' : 'your') : (m.replyTo.mine ? 'your' : 'my own')} earlier message: "${quotePreview(m.replyTo).slice(0, 140)}"] ${m.text}` }
          : m),
      }, seq, otherName);
    } else if (roomId) {
      // Waits in the outbox while reconnecting, and goes out once the chat is back.
      sendChat({ id: newMessage.id, roomId, text: messageText, type: 'text', authorName: identity, replyTo: reply.wire });
      socketRef.current?.emit('typing', { roomId, typing: false });
    }
  };

  // Handle typing indicator
  useEffect(() => {
    if (roomId && inputText.trim()) {
      socketRef.current?.emit('typing', { roomId, typing: true });
      const timeout = setTimeout(() => {
        socketRef.current?.emit('typing', { roomId, typing: false });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [inputText, roomId]);

  // Ask to talk with the person behind a thought. Nothing starts unless they say yes.
  const reachOut = (thoughtId: string) => {
    setOutgoingReach({ thoughtId, status: 'sending' });
    socketRef.current?.timeout(8000).emit('reach_out', { thoughtId }, (err: Error | null, res?: { ok: boolean; requestId?: string }) => {
      if (err || !res?.ok) setOutgoingReach({ thoughtId, status: 'unavailable' });
      else setOutgoingReach(prev => (prev?.thoughtId === thoughtId ? { thoughtId, requestId: res.requestId, status: 'waiting' } : prev));
    });
  };

  // Closing a thought also withdraws a request that is still waiting.
  // Opening a thought puts its link in the address bar, so it can be shared.
  // The writer loves a reply (or takes it back): shown at once, undone if the
  // server says no (for example a thought posted before keys existed).
  const loveReply = (thoughtId: string, replyId: string, on: boolean, key: string) => {
    const setLoved = (loved: boolean) => setUniverseThoughts(prev => prev.map(t => t.id === thoughtId
      ? { ...t, replies: (t.replies ?? []).map(r => (r.id === replyId ? { ...r, loved } : r)) } : t));
    setLoved(on);
    socketRef.current?.timeout(8000).emit('love_reply', { thoughtId, replyId, key, on }, (err: Error | null, ok?: boolean) => {
      if (err || !ok) setLoved(!on);
    });
  };
  const [thoughtFromList, setThoughtFromList] = useState(false);
  const openThought = (thought: Thought, fromList = false) => {
    setSelectedThought(thought);
    setThoughtFromList(fromList);
    setThoughtLinkCopied(false);
    if (window.location.pathname.startsWith('/sky-of-thoughts')) window.history.replaceState(null, '', `${window.location.pathname}?t=${encodeURIComponent(thought.id)}`);
  };

  const closeThought = () => {
    if (outgoingReach?.status === 'waiting' && outgoingReach.requestId) {
      socketRef.current?.emit('reach_out_cancel', { requestId: outgoingReach.requestId });
    }
    setOutgoingReach(null);
    setSelectedThought(null);
    if (window.location.pathname.startsWith('/sky-of-thoughts') && window.location.search) window.history.replaceState(null, '', window.location.pathname);
  };

  // Where my posts are: right after posting, a held thought says it is waiting
  // (not lost), and what to take out if it is something simple. Later, a card
  // says when a waiting thought or reply went live, or was not published.
  type StatusNudge = { kind: 'thought' | 'reply'; outcome: 'live' | 'not_published'; id: string; text: string; thoughtId: string };
  const [statusNudges, setStatusNudges] = useState<StatusNudge[]>([]);
  const [heldNotice, setHeldNotice] = useState<{ id: string; fix?: string } | null>(null);
  const replacingRef = useRef<string | null>(null);
  // Closed the form without posting: nothing is replaced.
  useEffect(() => { if (!isAddingThought) replacingRef.current = null; }, [isAddingThought]);
  useEffect(() => {
    const onStatus = (e: Event) => {
      const { id, fix } = (e as CustomEvent).detail as { id: string; fix?: string };
      setUniverseThoughts(prev => prev.map(t => {
        if (t.id !== id) return t;
        setPostWaiting(id, t.text, t.emoji);
        return { ...t, pending: true, isNew: false };
      }));
      setHeldNotice({ id, fix });
    };
    window.addEventListener('zuzu:post_status', onStatus);
    return () => window.removeEventListener('zuzu:post_status', onStatus);
  }, []);
  // Once per visit, ask where my waiting posts are now.
  const statusAsked = useRef(false);
  useEffect(() => {
    if (statusAsked.current || !universeThoughts.length || !socketRef.current?.connected) return;
    const ask = pendingChecks();
    statusAsked.current = true;
    if (!ask.thoughts.length && !ask.replies.length) return;
    socketRef.current.timeout(8000).emit('my_post_status', ask, (err: Error | null, res?: { thoughts: Record<string, string>; replies: Record<string, string> }) => {
      if (err || !res) return;
      const found: StatusNudge[] = [];
      for (const [id, st] of Object.entries(res.thoughts)) {
        const mine = universeThoughts.find(t => t.id === id);
        if (st === 'live') { setPostStatus(id, 'live'); found.push({ kind: 'thought', outcome: 'live', id, text: mine?.text ?? '', thoughtId: id }); }
        else if (st === 'not_published' || st === 'removed') {
          found.push({ kind: 'thought', outcome: 'not_published', id, text: mine?.text ?? '', thoughtId: id });
          setPostStatus(id, 'not_published');
          setUniverseThoughts(prev => prev.filter(t => !(t.id === id && t.pending)));
        }
      }
      for (const [id, st] of Object.entries(res.replies)) {
        const info = replyInfo(id);
        if (!info) continue;
        if (st === 'live') { setMyReplyStatus(id, 'live'); found.push({ kind: 'reply', outcome: 'live', id, text: info.text ?? '', thoughtId: info.thoughtId }); }
        else if (st === 'not_published' || st === 'removed') { forgetReply(id); found.push({ kind: 'reply', outcome: 'not_published', id, text: info.text ?? '', thoughtId: info.thoughtId }); }
      }
      if (found.length) setStatusNudges(q => [...q, ...found]);
    });
  }, [universeThoughts, isSocketConnected, socketRef]);
  const statusNudge = (screen === 'LANDING' || screen === 'UNIVERSE') && !selectedThought && !isAddingThought ? statusNudges[0] ?? null : null;
  const closeStatusNudge = () => {
    if (statusNudge?.kind === 'thought' && statusNudge.outcome === 'not_published') forgetPost(statusNudge.id);
    setStatusNudges(q => q.slice(1));
  };
  const openStatusNudge = () => {
    const n = statusNudge;
    if (!n) return;
    closeStatusNudge();
    if (n.outcome === 'not_published' && n.kind === 'thought') {
      // Write it again: the form opens with the old words, ready to change.
      setNewThoughtText(n.text);
      if (screen === 'UNIVERSE') setIsAddingThought(true); else navigate('/sky-of-thoughts');
      return;
    }
    const t = universeThoughts.find(x => x.id === n.thoughtId);
    if (!t) return;
    if (screen === 'UNIVERSE') {
      const p = skyLayout?.spots.get(t.id);
      if (p) skyApiRef.current?.reveal(p, true);
      openThought(t);
    } else navigate(`/sky-of-thoughts?t=${encodeURIComponent(t.id)}`);
  };
  const editHeldThought = () => {
    const n = heldNotice;
    setHeldNotice(null);
    const t = n && universeThoughts.find(x => x.id === n.id);
    if (!t) return;
    replacingRef.current = t.id;
    setSelectedEmoji(t.emoji || selectedEmoji);
    setNewThoughtText(t.text);
    setIsAddingThought(true);
  };

  // "Someone replied to your thought": on a later visit (or live), if one of
  // the thoughts posted from this device has replies not seen yet. Shown on
  // the home page and the Sky only, a moment after arriving, never over a
  // chat or an open thought. "Later" hides it for this visit.
  const [replyNudgeReady, setReplyNudgeReady] = useState(false);
  const [myPosts, setMyPosts] = useState(myPostIds);
  const [replyNudgeLater, setReplyNudgeLater] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReplyNudgeReady(true), 2500);
    return () => clearTimeout(t);
  }, []);
  // Reading your own thought counts as seeing its replies.
  useEffect(() => {
    if (!selectedThought) return;
    const live = universeThoughts.find(t => t.id === selectedThought.id) ?? selectedThought;
    markRepliesSeen(live.id, live.replies?.length ?? 0);
    markLoveSeen(live);
  }, [selectedThought, universeThoughts]);
  const replyNudge = useMemo(() => {
    if (!replyNudgeReady || replyNudgeLater || selectedThought || isAddingThought || incomingRequest || statusNudges.length) return null;
    if (screen !== 'LANDING' && screen !== 'UNIVERSE') return null;
    const replies = unseenReply(universeThoughts);
    if (replies) return { ...replies, love: null };
    // Or: the writer of a thought loved a reply this person wrote.
    const love = unseenLove(universeThoughts);
    return love ? { thought: love.thought, count: 1, love: love.reply } : null;
  }, [replyNudgeReady, replyNudgeLater, selectedThought, isAddingThought, incomingRequest, screen, universeThoughts, statusNudges.length]);
  const openReplyNudge = () => {
    if (!replyNudge) return;
    const { thought } = replyNudge;
    if (screen === 'UNIVERSE') {
      const p = skyLayout?.spots.get(thought.id);
      if (p) skyApiRef.current?.reveal(p, true);
      openThought(thought);
    } else navigate(`/sky-of-thoughts?t=${encodeURIComponent(thought.id)}`);
  };
  // Thoughts posted from this tab or, earlier, from this device ("Mine" in the list).
  const myIdsAll = useMemo(() => new Set([...myThoughtIds, ...myPosts]), [myThoughtIds, myPosts]);

  // For the Sky: faded when another feeling is picked; a soft glow when the
  // writer is here right now (never for your own thoughts).
  const skyDimmed = useCallback((t: Thought) => skyFeeling !== 'all' && !t.feelings?.includes(skyFeeling) && !t.isNew, [skyFeeling]);
  const skyReachable = useCallback((t: Thought) => reachableThoughts.has(t.id) && !myThoughtIds.has(t.id), [reachableThoughts, myThoughtIds]);

  // Share a thought: a short quote plus a link that opens it in the Sky.
  const shareThought = async (thought: Thought) => {
    const url = `https://www.zuzu.club/sky-of-thoughts?t=${encodeURIComponent(thought.id)}`;
    const quote = thought.text.length > 120 ? thought.text.slice(0, 120).replace(/\s+\S*$/, '') + '…' : thought.text;
    const text = `${thought.emoji ?? '💭'} "${quote}" (from the Sky of Thoughts on Zuzu)`;
    try {
      if (navigator.share) { await navigator.share({ title: 'A thought in the Sky', text, url }); return; }
    } catch { return; } // closed the share sheet
    try { await navigator.clipboard.writeText(url); setThoughtLinkCopied(true); setTimeout(() => setThoughtLinkCopied(false), 2500); } catch { /* ignore */ }
  };

  // A shared link opens its thought, and the Sky moves to where it lives, so
  // closing it leaves you right next to it.
  const centerOnRef = useRef<string | null>(null);
  useEffect(() => {
    const id = centerOnRef.current, p = id ? skyLayout?.spots.get(id) : null;
    if (!p || !skyApiRef.current) return;
    centerOnRef.current = null;
    skyApiRef.current.reveal(p, true);
  }, [skyLayout, selectedThought]);

  // A shared link: once the thoughts arrive, open that one (or say it has drifted away).
  useEffect(() => {
    const id = deepLinkRef.current;
    if (!id || screen !== 'UNIVERSE' || universeThoughts.length === 0) return;
    deepLinkRef.current = null;
    const found = universeThoughts.find(t => t.id === id);
    if (found) { setSelectedThought(found); centerOnRef.current = found.id; }
    else {
      setLinkNotice(true);
      window.history.replaceState(null, '', window.location.pathname);
      setTimeout(() => setLinkNotice(false), 5000);
    }
  }, [screen, universeThoughts]);

  const answerRequest = (accept: boolean) => {
    if (!incomingRequest) return;
    socketRef.current?.emit('chat_request_response', { requestId: incomingRequest.requestId, accept });
    if (accept) setAcceptingRequest(incomingRequest.requestId);
    else setIncomingRequest(null);
  };

  const addToUniverse = (text: string, emoji: string) => {
    const newThought: Thought = {
      id: Date.now().toString(),
      text,
      x: Math.random() * 80 + 10,
      y: Math.random() * 60 + 15, // Keep between 15% and 75% from top
      size: Math.random() * 20 + 10,
      emoji,
      isNew: true,
    };
    // The release moment: where it lands (its spot in the Sky) and its three
    // nearest thoughts. Spots are in sky coordinates; the burst uses the screen.
    // New thoughts always land at home (the newest screen): if you are out
    // exploring, the Sky glides back so you see yours arrive.
    const origin = skyRef.current?.getBoundingClientRect() ?? { left: 0, top: 0 };
    const api = skyApiRef.current;
    const spot = skyLayout?.reserve(newThought.id) ?? null;
    const cam = spot && api ? api.reveal(spot) : null;
    const toScreen = (p: Pt) => {
      const q = api && cam ? api.toScreen(p, cam) : p;
      return { x: origin.left + q.x, y: origin.top + q.y };
    };
    const land = spot ? toScreen(spot) : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const neighbours = spot ? universeThoughts
      .map(t => skyLayout?.spots.get(t.id))
      .filter((p): p is Pt => !!p)
      .sort((a, b) => Math.hypot(a.x - spot.x, a.y - spot.y) - Math.hypot(b.x - spot.x, b.y - spot.y))
      .slice(0, 3)
      .map(toScreen) : [];
    setReleaseBurst({ key: newThought.id, tx: land.x, ty: land.y, neighbours });

    // Remember this tab's own thoughts, so it never offers "Reach out" to itself.
    myThoughtIds.add(newThought.id);
    try { sessionStorage.setItem('zuzu_my_thoughts', JSON.stringify([...myThoughtIds])); } catch { /* private mode */ }
    // On this device only: to say when someone replies, and the thought's own
    // key, so its writer (and nobody else) can love replies.
    const authorKey = newPostKey();
    rememberPost(newThought.id, authorKey);
    // Edited after it was held: the older, waiting version is taken back.
    const replacing = replacingRef.current;
    replacingRef.current = null;
    if (replacing) {
      const oldKey = postKey(replacing);
      if (oldKey) socketRef.current?.emit('withdraw_post', { kind: 'thought', id: replacing, key: oldKey });
      forgetPost(replacing);
      setUniverseThoughts(prev => prev.filter(t => t.id !== replacing));
    }
    setMyPosts(myPostIds());

    // Posted from a feeling page: that feeling is picked for it.
    if (skyFeeling !== 'all') newThought.feelings = [skyFeeling];
    setUniverseThoughts(prev => [...prev, newThought]);
    playThoughtPosted();
    socketRef.current?.emit('send_thought', { ...newThought, authorKey });
    if (skyFeeling !== 'all') socketRef.current?.emit('set_thought_feeling', { thoughtId: newThought.id, feelings: [skyFeeling] });
    
    // Remove isNew flag after animation completes
    setTimeout(() => {
      setUniverseThoughts(prev => prev.map(t => t.id === newThought.id ? { ...t, isNew: false } : t));
    }, 3000);

    // Once the release moment is over: what is it about, and who else felt it.
    // "Where did you hear about Zuzu?" comes after this card closes.
    setTimeout(() => {
      setFeelingCounts(null);
      setFeelingCard({ thoughtId: newThought.id });
      fetch(`${API_BASE}/api/feelings?not=${encodeURIComponent(newThought.id)}`)
        .then(r => r.json()).then(d => setFeelingCounts(d.counts ?? {})).catch(() => setFeelingCounts({}));
    }, RELEASE_MS + 200);
  };

  const closeFeelingCard = () => {
    setFeelingCard(null);
    if (!maybeShowHeardAbout()) {
      setShowSkyShare(true);
      setTimeout(() => setShowSkyShare(false), 9000);
    }
  };

  // The writer changes Zuzu's guess.
  // Tap a feeling on or off: one to three, the first stays the main one.
  const pickFeeling = (thoughtId: string, feeling: string) => {
    const current = feelingCard?.picked ?? universeThoughts.find(t => t.id === thoughtId)?.feelings ?? [];
    const next = current.includes(feeling)
      ? current.filter(f => f !== feeling)
      : current.length < MAX_FEELINGS ? [...current, feeling] : current;
    if (!next.length || next === current) return;
    setFeelingCard(c => (c && c.thoughtId === thoughtId ? { ...c, picked: next } : c));
    setUniverseThoughts(prev => prev.map(t => (t.id === thoughtId ? { ...t, feelings: next } : t)));
    socketRef.current?.emit('set_thought_feeling', { thoughtId, feelings: next });
  };

  const toggleMeToo = (thoughtId: string) => {
    const on = !meTooMine.has(thoughtId);
    const next = new Set(meTooMine);
    if (on) next.add(thoughtId); else next.delete(thoughtId);
    setMeTooMine(next);
    try { sessionStorage.setItem('zuzu_me_too', JSON.stringify([...next])); } catch { /* private mode */ }
    setUniverseThoughts(prev => prev.map(t => (t.id === thoughtId ? { ...t, meToo: Math.max(0, (t.meToo ?? 0) + (on ? 1 : -1)) } : t)));
    socketRef.current?.emit('me_too', { thoughtId, on }, (res: { ok: boolean; count?: number }) => {
      if (typeof res?.count === 'number') setUniverseThoughts(prev => prev.map(t => (t.id === thoughtId ? { ...t, meToo: res.count } : t)));
    });
  };

  return (
    <div
      className={`relative h-[100dvh] w-screen overflow-hidden transition-colors duration-1000 flex flex-col ${
        isNight ? 'bg-night-bg text-night-ink' : 'bg-day-bg text-black'
      }`}
      style={keyboardBox ? { height: keyboardBox.height, transform: `translateY(${keyboardBox.top}px)` } : undefined}
    >
      {/* A warm note: the person you just talked with felt better */}
      <AnimatePresence>
        {feelingNote && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            role="status"
            onClick={() => setFeelingNote(null)}
            className={`fixed z-[170] top-4 left-1/2 -translate-x-1/2 w-max max-w-[calc(100%-2rem)] text-center px-4 py-2.5 rounded-2xl text-[0.8125rem] font-medium shadow-xl border backdrop-blur-xl cursor-pointer ${
              isNight ? 'bg-black/70 border-white/10 text-white' : 'bg-white/90 border-black/10 text-black'
            }`}
          >
            {feelingNote}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Elements - Only on Landing */}
      {screen === 'LANDING' && (
        <>
          {/* The home page's one main heading, for search and screen readers (the logo is not a heading). */}
          <h1 className="sr-only">Zuzu Club: a whimsical website to let go of worries, share feelings and chat anonymously</h1>
          <MeadowBackground isNight={isNight} className="absolute inset-0 z-0" />
          <div className={`absolute inset-0 z-[1] pointer-events-none bg-gradient-to-b transition-colors duration-1000 ${
            isNight ? 'from-black/25 via-black/5 to-transparent' : 'from-white/60 via-white/10 to-transparent'
          }`} />
        </>
      )}

      {/* Nature Sound */}
      <audio 
        ref={audioRef}
        loop
        preload="none"
        src="/zuzu-club-ambient-sound.mp3" 
      />

      {/* A waiting post went live, or was not published (on a later visit) */}
      <AnimatePresence>
        {statusNudge && (
          <motion.div
            key={statusNudge.kind + statusNudge.id}
            role="dialog"
            aria-live="polite"
            aria-label={statusNudge.outcome === 'live' ? `Your ${statusNudge.kind} is live now` : `Your ${statusNudge.kind} was not published`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`fixed z-[160] left-1/2 -translate-x-1/2 bottom-6 w-[calc(100%-2rem)] max-w-sm overflow-hidden rounded-[28px] border p-5 shadow-2xl backdrop-blur-xl ${
              isNight ? 'bg-[#0d1b2a]/90 border-white/10 text-white shadow-black/50' : 'bg-white/95 border-black/10 text-black shadow-black/15'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none mt-0.5" aria-hidden="true">{statusNudge.outcome === 'live' ? '✨' : '🌧️'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">
                  {statusNudge.outcome === 'live'
                    ? (statusNudge.kind === 'thought' ? 'Your thought is in the Sky now' : 'Your reply is live now')
                    : (statusNudge.kind === 'thought' ? "Your thought wasn't published" : "Your reply wasn't published")}
                </p>
                {statusNudge.text && <p className="text-xs italic mt-1 line-clamp-2 opacity-60">"{statusNudge.text}"</p>}
                <p className="text-xs opacity-70 mt-2">
                  {statusNudge.outcome === 'live'
                    ? 'A person checked it, and it is there for others to read.'
                    : statusNudge.kind === 'thought'
                      ? 'A person checked it, and it did not fit Zuzu\'s guidelines, for example because of contact details or words that could hurt someone. You are welcome to write it again in your own way.'
                      : 'A person checked it, and it could have hurt the person who shared. Kind and honest words are always welcome.'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              {!(statusNudge.outcome === 'not_published' && statusNudge.kind === 'reply') && (
                <button
                  onClick={openStatusNudge}
                  className={`flex-1 py-2.5 rounded-full text-xs font-semibold transition-all active:scale-95 ${
                    isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-[#1a1a1a] text-white hover:bg-black/90'
                  }`}
                >
                  <span className="inline-flex items-center justify-center gap-1.5">
                    {statusNudge.outcome === 'live' ? <><MessageCircle size={14} aria-hidden="true" /> Read it</> : <><Pencil size={14} aria-hidden="true" /> Write it again</>}
                  </span>
                </button>
              )}
              <button
                onClick={closeStatusNudge}
                className={`flex-1 py-2.5 rounded-full text-xs font-medium border transition-all ${
                  isNight ? 'border-white/15 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'
                }`}
              >
                OK
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Someone replied to a thought posted from this device */}
      <AnimatePresence>
        {replyNudge && (
          <motion.div
            key={replyNudge.thought.id}
            role="dialog"
            aria-live="polite"
            aria-label={replyNudge.love ? 'The writer loved your reply' : 'Someone replied to your thought'}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`fixed z-[160] left-1/2 -translate-x-1/2 bottom-6 w-[calc(100%-2rem)] max-w-sm overflow-hidden rounded-[28px] border p-5 shadow-2xl backdrop-blur-xl ${
              isNight ? 'bg-[#0d1b2a]/90 border-white/10 text-white shadow-black/50' : 'bg-white/95 border-black/10 text-black shadow-black/15'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none mt-0.5" aria-hidden="true">{replyNudge.love ? '❤️' : '💬'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">
                  {replyNudge.love ? 'The writer loved your reply'
                    : replyNudge.count > 1 ? `${replyNudge.count} new replies to your thought` : 'Someone replied to your thought'}
                </p>
                <p className="text-xs italic mt-1 line-clamp-2 opacity-60">
                  {replyNudge.love
                    ? `Your reply: "${replyNudge.love.text}"`
                    : `${replyNudge.thought.emoji ? `${replyNudge.thought.emoji} ` : ''}"${replyNudge.thought.text}"`}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={openReplyNudge}
                className={`flex-1 py-2.5 rounded-full text-xs font-semibold transition-all active:scale-95 ${
                  isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-[#1a1a1a] text-white hover:bg-black/90'
                }`}
              >
                <span className="inline-flex items-center justify-center gap-1.5">
                  <MessageCircle size={14} aria-hidden="true" /> Read {replyNudge.count > 1 ? 'them' : 'it'}
                </span>
              </button>
              <button
                onClick={() => setReplyNudgeLater(true)}
                className={`flex-1 py-2.5 rounded-full text-xs font-medium border transition-all ${
                  isNight ? 'border-white/15 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'
                }`}
              >
                <span className="inline-flex items-center justify-center gap-1.5">
                  <Clock size={14} aria-hidden="true" /> Later
                </span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Someone would like to talk about this person's thought */}
      <AnimatePresence>
        {incomingRequest && screen !== 'VENT' && screen !== 'LISTEN' && (
          <motion.div
            key={incomingRequest.requestId}
            role="dialog"
            aria-live="polite"
            aria-label="Someone would like to talk"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`fixed z-[160] left-1/2 -translate-x-1/2 bottom-6 w-[calc(100%-2rem)] max-w-sm overflow-hidden rounded-[28px] border p-5 shadow-2xl backdrop-blur-xl ${
              isNight ? 'bg-[#0d1b2a]/90 border-white/10 text-white shadow-black/50' : 'bg-white/95 border-black/10 text-black shadow-black/15'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none mt-0.5">{incomingRequest.emoji || '✨'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Someone was moved by your thought</p>
                <p className={`text-xs italic mt-1 line-clamp-2 ${isNight ? 'opacity-60' : 'opacity-60'}`}>"{incomingRequest.preview}"</p>
                <p className="text-xs opacity-70 mt-2">They'd like to talk, just the two of you. Your names stay hidden, and you can leave anytime.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => answerRequest(true)}
                disabled={acceptingRequest === incomingRequest.requestId}
                className={`flex-1 py-2.5 rounded-full text-xs font-semibold transition-all active:scale-95 disabled:opacity-60 ${
                  isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-[#1a1a1a] text-white hover:bg-black/90'
                }`}
              >
                {acceptingRequest === incomingRequest.requestId ? 'Connecting…' : "Let's talk"}
              </button>
              <button
                onClick={() => answerRequest(false)}
                disabled={acceptingRequest === incomingRequest.requestId}
                className={`flex-1 py-2.5 rounded-full text-xs font-medium border transition-all ${
                  isNight ? 'border-white/15 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'
                }`}
              >
                Not now
              </button>
            </div>
            {/* A quiet line that empties while the invitation is open */}
            <motion.div
              aria-hidden="true"
              className={`absolute left-0 bottom-0 h-0.5 ${isNight ? 'bg-amber-200/50' : 'bg-amber-400/60'}`}
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: Math.max(1, (incomingRequest.expiresIn - (Date.now() - incomingRequest.receivedAt)) / 1000), ease: 'linear' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Meet Again: the sheet, "🌻 they're here", and "you'll meet again" */}
      <AnimatePresence>
        {meetOpen && meetCtx && (
          <MeetAgainSheet
            key="meet-sheet"
            isNight={isNight}
            state={meetCtx.state}
            busy={meetBusy}
            error={meetError}
            pairId={meetCtx.pairId}
            pairs={meetPairs}
            max={meetMax}
            locals={meetLocal}
            onTap={meetTap}
            onLetGo={meetLetGo}
            onNote={meetNote}
            onClose={closeMeet}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {meetOffer && !(roomId && !isAiChat) && (
          <MeetOfferCard
            key={meetOffer.offerId}
            isNight={isNight}
            label={meetLabel(meetLocal[meetOffer.pairId], meetPairs.find(p => p.id === meetOffer.pairId))}
            expiresIn={meetOffer.expiresIn}
            waiting={meetOffer.waiting}
            onAnswer={answerMeetOffer}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {meetToast && !meetOpen && meetCtx?.state === 'matched' && (
          <motion.div
            key="meet-toast"
            role="status"
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className={`fixed z-[165] left-1/2 -translate-x-1/2 top-4 w-[calc(100%-2rem)] max-w-sm flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-xl backdrop-blur-xl ${
              isNight ? 'bg-[#0d1b2a]/90 border-white/10 text-white' : 'bg-white/95 border-black/10 text-black'
            }`}
          >
            <p className="flex-1 text-sm font-medium inline-flex items-center gap-1.5">You'll meet again <MeetIcon className={isNight ? 'text-emerald-300' : 'text-emerald-600'} /></p>
            <button type="button" onClick={openMeet} className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`}>
              Add a note
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Navigation / Toggles. z-[60]: above the Sky's own top bar (z-50), so the sound menu opens over it. */}
      {(screen === 'LANDING' || screen === 'UNIVERSE' || screen === 'MATCHING' || screen === 'VENT' || screen === 'LISTEN') && (
        <div role="banner" className={`zuzu-header z-[60] ${screen === 'UNIVERSE' ? 'sky-pass' : ''} transition-all duration-500 pl-8 pr-4 sm:px-8 py-6 max-w-7xl mx-auto w-full ${
          (screen === 'VENT' || screen === 'LISTEN') ? 'relative' : 'absolute top-0 left-0 right-0'
        }`}>
          <div className="flex items-center justify-between w-full">
            {/* The logo is not a heading: each page has its own h1. */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              data-sky-avoid
              // A glass chip (like the clock), so cards scrolling under it never clash.
              // While waiting for a match the logo does nothing: leaving the queue is the card's Cancel.
              className={`zuzu-logo text-3xl leading-none tracking-tight m-0 -ml-4 px-4 pt-1 pb-1.5 rounded-full font-normal backdrop-blur-md border ${
                screen === 'MATCHING' ? 'pointer-events-none' : 'cursor-pointer'
              } ${
                isNight ? 'text-night-ink bg-white/5 border-white/10' : 'text-black bg-white/40 border-black/5'
              }`}
              style={{ fontFamily: "'Instrument Serif', serif" }}
              onClick={() => { if (screen !== 'MATCHING') setScreen('LANDING'); }}
            >
              Zuzu
            </motion.div>

            <div className="hdr-group relative flex items-center gap-2 sm:gap-4" data-sky-avoid>
              <SoundMenu isNight={isNight} buttonClassName={`hdr-btn p-2 sm:p-2.5 rounded-full transition-all cursor-pointer ${
                isNight
                  ? 'liquid-glass text-night-ink'
                  : 'bg-black/5 hover:bg-black/10 text-black border border-black/10'
              } hover:scale-[1.03]`} />
              <button aria-label={isNight ? 'Switch to day mode' : 'Switch to night mode'} 
                onClick={() => setIsNight(!isNight)}
                className={`hdr-btn p-2 sm:p-2.5 rounded-full transition-all cursor-pointer ${
                  isNight 
                    ? 'liquid-glass text-night-ink' 
                    : 'bg-black/5 hover:bg-black/10 text-black border border-black/10'
                } hover:scale-[1.03]`}
              >
                {isNight ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <AccessibilityButton className={`hdr-btn p-2 sm:p-2.5 rounded-full transition-all cursor-pointer ${
                  isNight
                    ? 'liquid-glass text-night-ink'
                    : 'bg-black/5 hover:bg-black/10 text-black border border-black/10'
                } hover:scale-[1.03]`} />
            </div>
          </div>

          {/* Top Center Clock */}
          <motion.div
            data-nosnippet
            data-sky-avoid
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`zuzu-clock absolute left-[calc(50%-12px)] sm:left-1/2 -translate-x-1/2 text-[0.625rem] md:text-xs tracking-[0.2em] sm:tracking-[0.3em] font-medium opacity-60 uppercase z-50 top-1/2 -translate-y-1/2 px-3 sm:px-4 py-2 rounded-full backdrop-blur-md border ${
              isNight 
                ? 'text-night-ink bg-white/5 border-white/10' 
                : 'text-black bg-white/40 border-black/5'
            }`}
          >
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
          </motion.div>
        </div>
      )}

      <main id="main-content" tabIndex={-1} className="flex-1 relative overflow-hidden outline-none">
        <AnimatePresence mode="wait">
        {screen === 'DASHBOARD' && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] overflow-y-auto"
          >
            <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="animate-spin" size={32} /></div>}>
              <Dashboard
                isNight={isNight}
                setIsNight={setIsNight}
                onClose={() => { navigate('/'); setScreen('LANDING'); }}
              />
            </Suspense>
          </motion.div>
        )}

        {screen === 'LANDING' && (
          <motion.div
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            className="flex flex-col items-center justify-between h-full px-6 text-center overflow-y-auto no-scrollbar relative z-10 pt-24 md:pt-24 pb-12 md:pb-5"
          >
            <div className="flex-grow flex flex-col items-center justify-center w-full">
              <ChatHourPill isNight={isNight} />
              {/* The home screen fades in as one piece (no one-by-one delays), so the main
                  text counts as shown (LCP) as soon as the app draws. */}
              <p
                className={`mt-4 md:mt-4 mb-4 max-w-[300px] md:max-w-none text-sm md:text-base opacity-70 tracking-tight leading-relaxed ${isNight ? 'text-night-ink' : 'text-black'}`}
              >
                Good vibes only. Anonymous, no signup.<br />
                Chat, share a feeling, or let it go!
              </p>

              {/* data-nosnippet: the greeting, the random name, the online count and the clock
                  change on every visit, so search results should not quote them. */}
              <h2
                data-nosnippet
                className={`zuzu-greeting font-light whitespace-nowrap text-[clamp(1.5rem,9vw,2.25rem)] md:text-5xl xl:text-6xl mb-3 ${isNight ? 'text-night-ink' : 'text-black'}`}
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                {/* One line on phones: the size follows the screen width, capped at the old size. */}
                <span className="italic">{greeting.text}</span>
                <span className="not-italic ml-3">{greeting.emoji}</span>
              </h2>

              <p
                data-nosnippet
                className={`serif italic leading-relaxed text-xl md:text-2xl mb-6 md:mb-4 opacity-90 ${isNight ? 'text-night-ink' : 'text-black'}`}
              >
                Today, you are <br />
                <span className={`font-semibold underline underline-offset-8 decoration-1 text-3xl md:text-4xl block mt-2 ${isNight ? 'text-night-ink' : 'text-black'}`}>{identity}</span>
              </p>

              <div
                data-nosnippet
                className={`mb-8 md:mb-6 px-5 py-2 rounded-full text-[0.625rem] md:text-xs tracking-tight font-medium flex items-center gap-2 ${
                  isNight
                    ? 'bg-white/10 text-white/80 border border-white/10'
                    : 'bg-white/60 text-black border border-black/10 backdrop-blur-xl shadow-[0_4px_24px_-8px_rgba(0,0,0,0.1)]'
                }`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                {inflatedUserCount} users online, globally
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  className="inline-block"
                >
                  🌎
                </motion.span>
              </div>

              <div className="flex flex-col md:flex-row gap-3 md:gap-5 w-full max-w-5xl mb-8 md:mb-4">
                <GlassButton isNight={isNight} isLanding={true} href="/vent-anonymously" onClick={() => startMatching('VENT')}>
                  <div className="flex flex-col items-start gap-1.5 w-full text-left">
                    <div className="flex items-center gap-2.5">
                      <Wind className="opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" size={22} />
                      <h2 className="text-xl font-semibold font-['Inter',sans-serif] whitespace-nowrap">Vent</h2>
                    </div>
                    <span className="text-[0.6875rem] uppercase tracking-wider opacity-70 leading-snug text-balance">Chat with a stranger</span>
                  </div>
                </GlassButton>
                <GlassButton isNight={isNight} isLanding={true} href="/talk-to-strangers" onClick={() => startMatching('LISTEN')}>
                  <div className="flex flex-col items-start gap-1.5 w-full text-left">
                    <div className="flex items-center gap-2.5">
                      <Heart className="opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" size={22} />
                      <h2 className="text-xl font-semibold font-['Inter',sans-serif] whitespace-nowrap">Listen</h2>
                    </div>
                    <span className="text-[0.6875rem] uppercase tracking-wider opacity-70 leading-snug text-balance">Hear someone</span>
                  </div>
                </GlassButton>
                <GlassButton isNight={isNight} isLanding={true} href="/sky-of-thoughts" onClick={() => navigate('/sky-of-thoughts')}>
                  <div className="flex flex-col items-start gap-1.5 w-full text-left">
                    <div className="flex items-center gap-2.5">
                      <Sparkles className="opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" size={22} />
                      <h2 className="text-xl font-semibold font-['Inter',sans-serif] whitespace-nowrap">Explore</h2>
                    </div>
                    <span className="text-[0.6875rem] uppercase tracking-wider opacity-70 leading-snug text-balance">The Sky of Thoughts</span>
                  </div>
                </GlassButton>
                <GlassButton isNight={isNight} isLanding={true} href="/let-go" onClick={() => navigate('/let-go')}>
                  <div className="flex flex-col items-start gap-1.5 w-full text-left">
                    <div className="flex items-center gap-2.5">
                      <Star className="opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" size={22} />
                      <h2 className="text-xl font-semibold font-['Inter',sans-serif] whitespace-nowrap">Let Go</h2>
                    </div>
                    <span className="text-[0.6875rem] uppercase tracking-wider opacity-70 leading-snug text-balance">Watch a worry fade away</span>
                  </div>
                </GlassButton>
              </div>
            </div>

            <Footer isNight={isNight} />
          </motion.div>
        )}

        {/* "How did you feel?" after leaving a chat with a person */}
        {feelingPopup && (
          <motion.div
            key="feeling-popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center px-6 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              role="dialog"
              aria-label="How did you feel?"
              className={`p-7 rounded-[32px] max-w-sm w-full shadow-2xl border border-white/20 backdrop-blur-xl ${
                isNight ? 'bg-black/40 text-white' : 'bg-white/60 text-black'
              }`}
            >
              <Later>
              <PostChatFeeling
                isNight={isNight}
                onPick={(f) => sendFeeling(feelingPopup, f)}
                onShare={() => shareZuzu('Zuzu Club 🤍 a free, anonymous place to vent, listen or simply feel heard. No signup.')}
                onSkip={() => { setFeelingPopup(null); maybeShowHeardAbout(); }}
                onDone={() => { setFeelingPopup(null); maybeShowHeardAbout(); }}
              />
              </Later>
              {meetCtx?.roomId === feelingPopup && meetCtx.state !== 'matched' && meetCtx.state !== 'closed' && (
                <MeetEndCard isNight={isNight} waiting={meetCtx.state === 'waiting'} onOpen={openMeet} />
              )}
            </motion.div>
          </motion.div>
        )}

        {showSocialPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center px-6 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`p-8 rounded-[32px] max-w-sm w-full text-center shadow-2xl border ${
                isNight ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-black/10 text-black'
              }`}
            >
              <h3 className="text-xl font-medium mb-2">Stay Connected for more</h3>
              {matchingRole && (
                <p className="text-sm opacity-70 mb-6 italic" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  {matchingRole === 'VENT' 
                    ? "You're courageous. Breathe in! Breathe out!" 
                    : "You're kind to help someone feel heard. Thanks!"}
                </p>
              )}
              {isWmhdSeason() && (
                <p className="text-xs opacity-70 -mt-3 mb-6">
                  🌻 <Link to={WMHD_PATH} onClick={() => setShowSocialPopup(false)} className="underline underline-offset-4">Resources for World Mental Health Day</Link>
                </p>
              )}
              {/* After an AI chat: other quiet places on Zuzu, and when people are around. */}
              {isAiChat && (
                <div className="-mt-3 mb-6">
                  <p className="text-xs opacity-60 mb-2.5">{matchingRole === 'LISTEN' ? 'Real people who need to vent come by too' : 'Next time, a person might be here'}</p>
                  <NudgeChip to="chatHour" where="end" isNight={isNight} onGo={() => setShowSocialPopup(false)} />
                  {matchingRole !== 'LISTEN' && (
                    <div className="flex justify-center gap-2 mt-2">
                      <NudgeChip to="letgo" where="end" isNight={isNight} onGo={() => setShowSocialPopup(false)} />
                      <NudgeChip to="sky" where="end" isNight={isNight} onGo={() => setShowSocialPopup(false)} />
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-center gap-4 mb-8">
                {SOCIAL_LINKS.map((social, i) => (
                  <a
                    key={i}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    className={`p-3 rounded-full transition-all ${
                      isNight ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'
                    }`}
                  >
                    <SocialIcon name={social.key} size={20} />
                  </a>
                ))}
              </div>
              <div className={`mb-6 pt-5 border-t ${isNight ? 'border-white/10' : 'border-black/10'}`}>
                <p className="text-sm opacity-70 mb-3">Know someone who needs a place to talk?</p>
                <button
                  onClick={() => shareFrom('Zuzu Club: a free, anonymous place to vent, be heard, or let a worry go. No signup.')}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                    isNight ? 'border-white/15 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'
                  }`}
                >
                  <Share size={15} /> {shareCopied ? 'Link copied' : 'Share Zuzu'}
                </button>
              </div>
              <button 
                onClick={() => setShowSocialPopup(false)}
                className={`w-full py-3 rounded-xl font-medium transition-all ${
                  isNight ? 'bg-white text-black' : 'bg-black text-white'
                }`}
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}

        {showHeardAbout && (
          <HeardAboutPrompt
            isNight={isNight}
            onSelect={handleHeardAboutSelect}
            onSkip={() => setShowHeardAbout(false)}
          />
        )}

        {screen === 'MATCHING' && (
          <motion.div
            key="matching"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            // z-[55]: under the header (z-[60]), so sound, day/night and accessibility stay usable while waiting.
            className="fixed inset-0 z-[55] flex items-center justify-center px-6 max-[400px]:px-4 pt-[5.5rem] [@media(max-height:700px)]:pt-[4.75rem] [@media(max-height:600px)]:pt-[4.5rem]! pb-4 [@media(max-height:700px)]:pb-3"
          >
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              className={`relative z-10 p-6 md:p-8 max-[400px]:px-4! [@media(max-height:700px)]:py-5 [@media(max-height:600px)]:py-4! max-h-full overflow-y-auto no-scrollbar rounded-[32px] max-w-md w-full text-center shadow-2xl border border-white/20 ${
                isNight ? 'bg-black/40 text-white' : 'bg-white/40 text-black'
              } backdrop-blur-xl`}
            >
              {/* Short screens: a smaller icon, so the whole card still fits without scrolling. */}
              <div className="relative mb-5 [@media(max-height:700px)]:mb-3 [@media(max-height:600px)]:mb-2!">
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 bg-current rounded-full blur-2xl opacity-20"
                />
                <div className="relative z-10 flex justify-center">
                  <div className="p-4 [@media(max-height:700px)]:p-2.5 rounded-full bg-white/10 border border-white/20">
                    <Sparkles className="animate-pulse w-7 h-7 [@media(max-height:700px)]:w-5 [@media(max-height:700px)]:h-5" />
                  </div>
                </div>
              </div>

              {/* Font shrinks a little on narrow phones so the title stays on one line. */}
              <h3 className="text-[clamp(1.25rem,6.4vw,1.5rem)] leading-tight font-medium mb-2 [@media(max-height:600px)]:mb-1 italic serif text-balance" style={{ fontFamily: "'Instrument Serif', serif" }}>
                <span aria-hidden="true" className="not-italic text-[0.8em] mr-1.5">⏳</span>
                {matchingRole === 'VENT' ? "Finding someone to listen..." : "Finding someone who needs to vent..."}
              </h3>
              {/* First visit: who they will meet (a real person, not AI, who might not be a therapist) and that they stay safe. */}
              <p className="text-[0.8125rem] opacity-70 leading-relaxed max-w-xs mx-auto text-balance">
                {matchingRole === 'VENT'
                  ? "A real person will listen, a kind stranger who might not be a therapist. You stay anonymous and can leave anytime."
                  : "No need to fix anything, just listen. If they seem in danger, gently point them to a helpline."}
              </p>
              {/* Quiet status: what is happening and for how long. Amber "Reconnecting…" only when the connection drops. */}
              <div className="flex items-center justify-center gap-1.5 mt-4 [@media(max-height:700px)]:mt-3 [@media(max-height:600px)]:mt-2! text-[0.6875rem] tabular-nums" role="status">
                <span className={`w-2 h-2 rounded-full animate-pulse ${isSocketConnected ? 'bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.6)]' : 'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]'}`} />
                <span className="opacity-50">
                  {isSocketConnected
                    ? <>Waiting for someone<span aria-hidden="true"> · {Math.floor(searchSeconds / 60)}:{String(searchSeconds % 60).padStart(2, '0')}</span></>
                    : 'Reconnecting…'}
                </span>
              </div>

              {/* After 10 seconds: one calm section. Switch tabs, the AI, or come back for Chat Hour. */}
              <AnimatePresence>
                {showAiPrompt && (() => {
                  const live = liveSession();
                  const next = nextSession();
                  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                  const mins = Math.max(0, Math.round((+next.start - Date.now()) / 60000));
                  const leave = (hash = '') => {
                    socketRef.current?.emit('stop_matching', { reason: 'chatHour' });
                    if (matchingTimeoutRef.current) clearTimeout(matchingTimeoutRef.current);
                    navigate(CHAT_HOUR_PATH + hash);
                  };
                  const link = 'underline underline-offset-2 opacity-90 hover:opacity-100';
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mt-3 flex flex-col items-center gap-3 [@media(max-height:600px)]:gap-2"
                    >
                      {/* Keep your place: the chime always plays; a browser notification only if they ask. */}
                      {/* Not asked yet: the chip says it all. Otherwise a quiet line about the sound. */}
                      {notifyState === 'default' ? (
                        <button
                          onClick={async () => setNotifyState(await askToNotify())}
                          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[0.75rem] font-medium border transition-colors ${
                            isNight ? 'border-white/15 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'
                          }`}
                        >
                          🔔 Notify me when someone joins
                        </button>
                      ) : (
                        <p className="text-[0.75rem] opacity-60 max-w-xs text-balance">
                          You can switch tabs. A soft sound plays when someone joins.
                        </p>
                      )}
                      {/* Separator: the AI is a different choice from waiting for a person. */}
                      <div className={`w-full border-t mt-2 [@media(max-height:600px)]:mt-0 ${isNight ? 'border-white/10' : 'border-black/5'}`} />
                      <p className="text-[0.8125rem] opacity-70">Don't want to wait?</p>
                      <button
                        onClick={() => acceptAiMatch()}
                        className={`w-full inline-flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-[clamp(0.8125rem,3.9vw,0.875rem)] whitespace-nowrap font-medium border backdrop-blur-md transition-all hover:scale-[1.01] active:scale-[0.99] ${
                          isNight ? 'bg-white/10 border-white/15 hover:bg-white/15' : 'bg-white/60 border-black/10 hover:bg-white/80'
                        }`}
                      >
                        <img src="/zuzu-club-chat-firefly-glow-v3.svg" alt="" className={`w-5 h-5 object-contain scale-[1.3] ${matchingRole === 'LISTEN' ? AI_VENTER_TINT : ''}`} />
                        Chat with Zuzu AI Companion
                      </button>
                      {/* Chat Hour: a compact line on most screens, the box with buttons when there is room (800px+ tall). */}
                      <div className={`w-full border-t mt-2 [@media(max-height:600px)]:mt-0 [@media(min-height:800px)]:hidden ${isNight ? 'border-white/10' : 'border-black/5'}`} />
                      <p className="text-[0.75rem] opacity-60 max-w-xs text-balance [@media(min-height:800px)]:hidden">
                        {live
                          ? <><button onClick={() => leave()} className={link}>{CHAT_HOUR_COPY.name}</button> is on. People are arriving until {chatTimeIn(live.end, tz)} your time. Stay a moment, someone should be here soon.</>
                          : <>It's a quiet moment on Zuzu.<br /><button onClick={() => leave()} className={link}>{CHAT_HOUR_COPY.name}</button> starts at <strong className="whitespace-nowrap">{chatTimeIn(next.start, tz)}</strong> your time, in {Math.floor(mins / 60) ? `${Math.floor(mins / 60)}h ` : ''}{mins % 60}m. More people will be here then. <button onClick={() => leave('#remind')} className={link}>Remind me</button></>}
                      </p>
                      <div className={`hidden [@media(min-height:800px)]:block w-full mt-2 rounded-2xl p-4 text-left border ${isNight ? 'bg-white/[0.06] border-white/12' : 'bg-white/60 border-black/10'}`}>
                        {live ? (
                          <>
                            <p className="text-lg leading-snug" style={{ fontFamily: "'Instrument Serif', serif" }}>{CHAT_HOUR_COPY.name} is on.</p>
                            <p className="text-[0.8125rem] opacity-75 mt-1 leading-relaxed">People are arriving until {chatTimeIn(live.end, tz)} your time. Stay a moment, someone should be here soon.</p>
                          </>
                        ) : (
                          <>
                            <p className="text-lg leading-snug" style={{ fontFamily: "'Instrument Serif', serif" }}>It's a quiet moment on Zuzu.</p>
                            <p className="text-[0.8125rem] opacity-75 mt-1 leading-relaxed">
                              {CHAT_HOUR_COPY.name} starts at <strong className="whitespace-nowrap">{chatTimeIn(next.start, tz)}</strong> your time, in {Math.floor(mins / 60) ? `${Math.floor(mins / 60)}h ` : ''}{mins % 60}m. More people will be here then.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button onClick={() => leave('#remind')} className={`px-4 py-2 rounded-full text-xs font-semibold ${isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'}`}>Remind me</button>
                              <button onClick={() => leave()} className={`px-4 py-2 rounded-full text-xs border ${isNight ? 'border-white/20' : 'border-black/15'}`}>See Chat Hour</button>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  );
                })()}
              </AnimatePresence>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  socketRef.current?.emit('stop_matching');
                  if (matchingTimeoutRef.current) clearTimeout(matchingTimeoutRef.current);
                  setScreen('LANDING');
                }}
                className="mt-6 [@media(max-height:700px)]:mt-4 [@media(max-height:600px)]:mt-3! text-xs uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity"
              >
                Cancel Search
              </motion.button>
            </motion.div>
          </motion.div>
        )}

        {(screen === 'VENT' || screen === 'LISTEN') && (
          <motion.div
            key="chat"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex flex-col h-full max-w-lg mx-auto w-full relative bg-inherit overflow-hidden"
          >
            {/* Instagram Style Top Bar */}
            <div className={`relative flex items-center gap-3 px-4 py-3 border-b ${
              isNight ? 'border-white/10' : 'border-black/5'
            }`}>
              <button aria-label="Go back" 
                onClick={() => {
                  // Going back from a live chat with a person ends it (after asking),
                  // so nobody is left writing to a chat that is not on screen.
                  if (roomId && !isAiChat) { closeChatMenu(); setConfirmLeave(true); }
                  else setScreen('LANDING');
                }}
                className="p-1 opacity-60 hover:opacity-100 transition-all"
              >
                <ArrowLeft size={24} />
              </button>
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAiChat ? '' : (isNight ? 'bg-white/10' : 'bg-black/5')}`}>
                  {isAiChat ? (
                    <img src="/zuzu-club-chat-firefly-glow-v3.svg" alt="Zuzu AI Companion" className={`w-full h-full object-contain scale-[1.2] ${matchingRole === 'LISTEN' ? AI_VENTER_TINT : ''}`} />
                  ) : (
                    <User size={18} className="opacity-40" />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold tracking-tight leading-none">{otherName}</span>
                  <span className={`text-[0.625rem] uppercase tracking-widest mt-1 opacity-60`}>
                    {isAiChat ? "Zuzu AI Companion" : meetChatPair && roomId ? <span className="inline-flex items-center gap-1">Met again <MeetIcon size={11} /></span> : (partnerRole === 'VENT' ? "Venter" : partnerRole === 'LISTEN' ? "Listener" : (screen === 'VENT' ? "Listener" : "Venter"))}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {meetAvailable && meetCtx && (
                  <MeetMoonButton isNight={isNight} on={meetCtx.state === 'waiting' || meetCtx.state === 'matched'} label={meetHintRoom === roomId && !!roomId && meetCtx.state === 'idle'} onClick={openMeet} />
                )}
                <button aria-label="More options"
                  aria-haspopup="menu"
                  aria-expanded={chatMenu !== null}
                  onClick={() => chatMenu ? closeChatMenu() : setChatMenu('menu')}
                  className={`p-1.5 rounded-full transition-all ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                >
                  <MoreHorizontal size={20} className="opacity-60 hover:opacity-100" />
                </button>
                <button aria-label="Leave chat" 
                  onClick={leaveChat}
                  className={`p-1.5 rounded-full transition-all ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                >
                  <XIcon size={20} className="opacity-60 hover:opacity-100" />
                </button>
              </div>
            </div>

            {confirmLeave && roomId && !isAiChat && (
              <>
                <div className="absolute inset-0 z-20" onClick={() => setConfirmLeave(false)} aria-hidden="true" />
                <div role="alertdialog" aria-labelledby="leave-chat-title"
                  className={`absolute left-3 top-14 z-30 w-[min(20rem,calc(100%-1.5rem))] rounded-2xl border p-4 shadow-xl backdrop-blur-xl ${
                    isNight ? 'bg-[#10202e]/95 border-white/10 text-white shadow-black/40' : 'bg-white/95 border-black/10 text-black shadow-black/10'
                  }`}>
                  <p id="leave-chat-title" className="text-sm font-semibold">Leave this chat?</p>
                  <p className="mt-1 text-xs opacity-70">The chat ends for both of you, and {otherName || 'the other person'} will see that you left.</p>
                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" autoFocus onClick={() => setConfirmLeave(false)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`}>
                      Stay
                    </button>
                    <button type="button" onClick={() => { setConfirmLeave(false); leaveChat(); }}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium ${isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-black text-white hover:bg-black/85'}`}>
                      Leave
                    </button>
                  </div>
                </div>
              </>
            )}

            {chatMenu && (
              <>
                <div className="absolute inset-0 z-20" onClick={closeChatMenu} aria-hidden="true" />
                <div
                  role="menu"
                  className={`absolute right-3 top-14 z-30 w-[min(20rem,calc(100%-1.5rem))] rounded-2xl border p-2 shadow-xl backdrop-blur-xl ${
                    isNight ? 'bg-[#10202e]/95 border-white/10 text-white shadow-black/40' : 'bg-white/95 border-black/10 text-black shadow-black/10'
                  }`}
                >
                  {chatMenu === 'menu' && (
                    <>
                      <a
                        role="menuitem"
                        href={HELPLINE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeChatMenu}
                        className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                      >
                        <LifeBuoy size={18} className="mt-0.5 shrink-0 opacity-70" />
                        <span className="flex flex-col">
                          <span className="text-sm font-medium">Get help now</span>
                          <span className="text-xs opacity-60">Free, private helplines in your country</span>
                        </span>
                      </a>
                      {!isAiChat && roomId && (
                        <>
                          <button
                            role="menuitem"
                            disabled={reportSent}
                            onClick={() => setChatMenu('report')}
                            className={`w-full flex items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors disabled:opacity-40 ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                          >
                            <Flag size={18} className="mt-0.5 shrink-0 opacity-70" />
                            <span className="flex flex-col">
                              <span className="text-sm font-medium">{reportSent ? 'Reported' : 'Report'}</span>
                              <span className="text-xs opacity-60">Let Zuzu know if something is not okay</span>
                            </span>
                          </button>
                          <button
                            role="menuitem"
                            onClick={() => setChatMenu('block')}
                            className={`w-full flex items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                          >
                            <Ban size={18} className="mt-0.5 shrink-0 text-red-500" />
                            <span className="flex flex-col">
                              <span className="text-sm font-medium text-red-500">Block</span>
                              <span className="text-xs opacity-60">End the chat and never match again</span>
                            </span>
                          </button>
                        </>
                      )}
                    </>
                  )}

                  {chatMenu === 'report' && (
                    <div className="p-2">
                      <p className="text-sm font-semibold">What happened?</p>
                      <p className="text-xs opacity-60 mt-1 mb-3">Your report is anonymous. {otherName} will not be told.</p>
                      <div className="flex flex-col gap-1.5">
                        {REPORT_REASONS.map(r => (
                          <button
                            key={r.key}
                            onClick={() => setReportReason(r.key)}
                            aria-pressed={reportReason === r.key}
                            className={`w-full flex items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                              reportReason === r.key
                                ? (isNight ? 'border-white bg-white/10 font-medium' : 'border-black bg-black/5 font-medium')
                                : (isNight ? 'border-white/10 hover:bg-white/5' : 'border-black/10 hover:bg-black/5')
                            }`}
                          >
                            {r.label}
                            {reportReason === r.key && <Check size={16} className="shrink-0" />}
                          </button>
                        ))}
                      </div>
                      <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer select-none">
                        <input type="checkbox" checked={alsoBlock} onChange={e => setAlsoBlock(e.target.checked)} className="accent-current" />
                        Also block and end the chat
                      </label>
                      <div className="flex justify-end gap-2 mt-4">
                        <button onClick={closeChatMenu} className="px-3 py-2 rounded-xl text-xs font-medium opacity-60 hover:opacity-100">Cancel</button>
                        <button
                          onClick={submitReport}
                          disabled={!reportReason}
                          className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-30 ${isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'}`}
                        >
                          Send report
                        </button>
                      </div>
                    </div>
                  )}

                  {chatMenu === 'block' && (
                    <div className="p-2">
                      <p className="text-sm font-semibold">Block {otherName}?</p>
                      <p className="text-xs opacity-60 mt-1">The chat will end, and you will not be matched with them again during this visit. They will only see that the chat ended.</p>
                      <div className="flex justify-end gap-2 mt-4">
                        <button onClick={closeChatMenu} className="px-3 py-2 rounded-xl text-xs font-medium opacity-60 hover:opacity-100">Cancel</button>
                        <button onClick={confirmBlock} className="px-4 py-2 rounded-xl text-xs font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors">
                          Block
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Messages Area */}
            <div 
              ref={scrollRef}
              onScroll={onChatScroll}
              role="log"
              aria-live="polite"
              aria-relevant="additions"
              aria-label={`Chat with ${otherName || 'someone'}`}
              className="flex-1 overflow-y-auto px-4 md:px-6 space-y-1 pr-1 scroll-smooth no-scrollbar pt-4 pb-8"
            >
              <div className={`text-center italic text-[0.625rem] mb-12 tracking-widest uppercase opacity-60`}>
                {isAiChat ? "Your safe space with Zuzu" : <>Encrypted Chat between<br />{otherName} & {identity}</>}
              </div>
              
              {messages.map((msg, idx) => {
                const isUser = msg.sender === 'user';
                const isSystem = msg.authorName === 'System';
                const isLastInGroup = idx === messages.length - 1 || messages[idx+1]?.sender !== msg.sender || messages[idx+1]?.authorName === 'System';
                const isFirstInGroup = idx === 0 || messages[idx-1]?.sender !== msg.sender || messages[idx-1]?.authorName === 'System';

                if (msg.kind === 'feeling') {
                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="w-full flex justify-center my-5"
                    >
                      <div className={`w-full max-w-[92%] rounded-2xl border px-4 py-4 ${
                        isNight ? 'bg-white/5 border-white/10' : 'bg-white/70 border-black/10'
                      }`}>
                        <Later><PostChatFeeling isNight={isNight} onPick={(f) => msg.roomId && sendFeeling(msg.roomId, f)} onShare={() => shareZuzu('Zuzu Club 🤍 a free, anonymous place to vent, listen or simply feel heard. No signup.')} /></Later>
                        {meetAvailable && meetCtx?.roomId === msg.roomId && meetCtx.state !== 'matched' && meetCtx.state !== 'closed' && (
                          <MeetEndCard isNight={isNight} waiting={meetCtx.state === 'waiting'} onOpen={openMeet} />
                        )}
                      </div>
                    </motion.div>
                  );
                }

                if (msg.kind === 'meet-intro') {
                  return (
                    <motion.div key={msg.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="w-full flex justify-center my-5">
                      <MeetIntroCard isNight={isNight} canTap={meetAvailable && meetCtx?.roomId === msg.roomId && meetCtx.state === 'idle'} onOpen={openMeet} />
                    </motion.div>
                  );
                }

                if (msg.kind === 'nudge') {
                  return (
                    <motion.div key={msg.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="w-full flex justify-start -mt-1 mb-3">
                      <NudgeChip to={msg.nudge === 'bye' ? 'chatHour' : msg.nudge ?? 'sky'} where="chat" isNight={isNight} />
                    </motion.div>
                  );
                }

                if (msg.kind) {
                  const isSelf = msg.kind === 'care-self';
                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="w-full flex justify-center my-5"
                    >
                      <div className={`max-w-[92%] rounded-2xl border px-4 py-3.5 text-left ${
                        isNight ? 'bg-white/5 border-white/10 text-white/85' : 'bg-white/70 border-black/10 text-black/80'
                      }`}>
                        <div className="flex items-start gap-3">
                          <LifeBuoy size={18} className="mt-0.5 shrink-0 opacity-70" />
                          <div className="text-[0.8125rem] leading-relaxed">
                            {isSelf ? (
                              <p>That sounds really heavy. You deserve support from someone who can help right now. If you might be in danger, please reach out to a helpline or local emergency services. You can keep talking here too.</p>
                            ) : (
                              <p>Your chat partner may be going through something very hard. Listen gently and don't try to fix it. If it feels right, you can share a free helpline with them.</p>
                            )}
                            <a href={HELPLINE_URL} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 font-medium underline underline-offset-4">
                              {isSelf ? 'Find a free helpline near you' : 'findahelpline.com'}
                            </a>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                }

                if (isSystem) {
                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="w-full flex justify-center my-6"
                    >
                      <div className={`max-w-[92%] text-center px-4 py-1.5 rounded-2xl text-[0.6875rem] font-medium tracking-wide border ${
                        isNight 
                          ? 'bg-white/5 border-white/10 text-white/40' 
                          : 'bg-black/5 border-black/5 text-black/40'
                      }`}>
                        {/* The AI note: who you're talking to on one line, what it does on the next. */}
                        {msg.id === 'system-ai-about' && msg.text
                          ? (() => { const cut = msg.text.indexOf('. ') + 1; return cut > 0 ? <>{msg.text.slice(0, cut)}<br />{msg.text.slice(cut + 1)}</> : msg.text; })()
                          : msg.text}
                        {msg.id === 'system-ai-about' && (
                          <> <Link to="/ai-companion" className="underline underline-offset-2 hover:opacity-100 whitespace-nowrap">How it works?</Link></>
                        )}
                      </div>
                    </motion.div>
                  );
                }
                
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, scale: 0.95, y: 5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    data-msg-id={msg.id}
                    className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} ${isFirstInGroup || msg.replyTo ? 'mt-4' : 'mt-0.5'}`}
                  >
                    <ReactionWrap
                      isUser={isUser}
                      isNight={isNight}
                      reactions={msg.reactions}
                      canReact={!isAiChat && !!roomId}
                      copyText={msg.type === 'text' ? msg.text : undefined}
                      doubleTapHeart={msg.type === 'text'}
                      onReact={(emoji) => reactTo(msg.id, emoji)}
                      onCopy={copyText}
                      onReply={isAiChat || roomId ? () => {
                        if (editingId) cancelEdit();
                        setReply({ id: msg.id, text: msg.type === 'text' ? msg.text?.slice(0, 140) : undefined, type: msg.type, mine: isUser });
                        textareaRef.current?.focus();
                      } : undefined}
                      onEdit={!isAiChat && roomId && isUser && msg.type === 'text' && !msg.kind && msg.status !== 'failed'
                        && msg.timestamp && currentTime.getTime() - msg.timestamp < EDIT_WINDOW_MS ? () => {
                        setReply(null);
                        setEditingId(msg.id);
                        setInputText(msg.text ?? '');
                        textareaRef.current?.focus();
                      } : undefined}
                    >
                    {/* The message this one replies to: tap to jump to it */}
                    {msg.replyTo && (
                      <button type="button" onClick={() => jumpTo(msg.replyTo!.id)}
                        className={`flex flex-col max-w-full mb-1 ${isUser ? 'items-end ml-auto' : 'items-start mr-auto'}`}>
                        <span className="text-[0.625rem] opacity-50 mb-0.5 px-1">
                          {isUser ? (msg.replyTo.mine ? 'You replied to yourself' : `You replied to ${otherName || 'them'}`) : (msg.replyTo.mine ? 'Replied to you' : 'Replied to themselves')}
                        </span>
                        <span className={`max-w-full truncate px-3 py-1.5 rounded-2xl text-[0.8125rem] opacity-70 border ${isNight ? 'bg-white/5 border-white/10' : 'bg-black/[0.04] border-black/10'}`}>
                          {quotePreview(msg.replyTo)}
                        </span>
                      </button>
                    )}
                    <div className={`max-w-full transition-all duration-300 ${flashId === msg.id ? 'ring-2 ring-[#3797F0]/70 ring-offset-2 ring-offset-transparent' : ''} ${
                      msg.type === 'text' ? 'px-4 py-2.5' : 'p-1'
                    } ${
                      isUser 
                        ? `bg-gradient-to-tr from-[#3797F0] to-[#9B51E0] text-white ${
                            isFirstInGroup && isLastInGroup ? 'rounded-2xl' :
                            isFirstInGroup ? 'rounded-t-2xl rounded-bl-2xl rounded-br-md' :
                            isLastInGroup ? 'rounded-b-2xl rounded-tl-2xl rounded-tr-md' :
                            'rounded-l-2xl rounded-r-md'
                          }` 
                        : `${isNight ? 'bg-[#262626]' : 'bg-[#EFEFEF]'} ${
                            isFirstInGroup && isLastInGroup ? 'rounded-2xl' :
                            isFirstInGroup ? 'rounded-t-2xl rounded-br-2xl rounded-bl-md' :
                            isLastInGroup ? 'rounded-b-2xl rounded-tr-2xl rounded-tl-md' :
                            'rounded-r-2xl rounded-l-md'
                          }`
                    }`}>
                      {msg.type === 'text' && (
                        <div className="text-[0.9375rem] leading-snug font-normal whitespace-pre-wrap break-words">
                          <ChatText text={msg.text ?? ''} />
                        </div>
                      )}
                      {msg.type === 'image' && <ChatImage src={msg.url} blurred={!isUser}
                        onReveal={!isUser && !isAiChat && roomId ? () => socketRef.current?.emit('photo_viewed', { roomId, messageId: msg.id }) : undefined} />}
                      {msg.type === 'image' && msg.text && (
                        <div className="px-3 pt-2 pb-1.5 text-[0.9375rem] leading-snug whitespace-pre-wrap break-words"><ChatText text={msg.text} /></div>
                      )}
                      {msg.type === 'audio' && (
                        <div className="px-1 py-0.5"><VoiceNote src={msg.url} duration={msg.duration} mine={isUser} /></div>
                      )}
                    </div>
                    </ReactionWrap>
                    {((msg.timestamp && isLastInGroup) || msg.edited || (isUser && msg.viewed) || (isUser && msg.status)) && (
                      <div className={`text-[0.625rem] mt-0.5 ${isUser ? 'text-right' : 'text-left'}`}>
                        {isUser && msg.status === 'failed'
                          ? <span className="text-red-400 opacity-80">Not delivered</span>
                          : <span className="opacity-50">{[isLastInGroup ? formatTime(msg.timestamp) : '', msg.edited ? 'edited' : '', isUser && msg.viewed ? 'Viewed' : ''].filter(Boolean).join(' · ')}</span>}
                        {/* Still on its way: only shown if it takes a moment, so quick sends do not flicker */}
                        {isUser && msg.status === 'sending' && (
                          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} transition={{ delay: 1.2 }}>{isLastInGroup && msg.timestamp ? ' · ' : ''}Sending…</motion.span>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}

              {/* The other person is recording a voice note */}
              {partnerRecording && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-start mt-2"
                  role="status"
                >
                  <div className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl rounded-bl-sm text-[0.8125rem] ${isNight ? 'bg-[#262626]' : 'bg-[#EFEFEF]'}`}>
                    <motion.span animate={{ opacity: [0.35, 1, 0.35] }} transition={{ duration: 1.2, repeat: Infinity }} className="flex">
                      <Mic size={14} className="text-red-500" />
                    </motion.span>
                    <span className="opacity-70">Recording a voice note…</span>
                  </div>
                </motion.div>
              )}

              {/* Post-Chat Social Cue */}
              {isTyping && !partnerRecording && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-start mt-2"
                >
                  <div className={`px-4 py-3 rounded-2xl rounded-bl-sm ${isNight ? 'bg-[#262626]' : 'bg-[#EFEFEF]'}`}>
                    <span className="sr-only">{otherName || 'They'} is typing</span>
                    <div className="flex gap-1" aria-hidden="true">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                          className="w-1.5 h-1.5 bg-current rounded-full opacity-40"
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Instagram Style Input Area */}
            <div className={`p-4 pb-8 md:pb-6 flex-shrink-0 relative ${isNight ? 'bg-night-bg' : 'bg-day-bg'} border-t ${isNight ? 'border-white/5' : 'border-black/5'}`}>
              {missed > 0 && (
                <button
                  onClick={jumpToBottom}
                  className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-3 z-10 px-4 py-2 rounded-full text-xs font-semibold shadow-lg ${isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'}`}
                >
                  {missed} new {missed === 1 ? 'message' : 'messages'} ⬇️
                </button>
              )}
              {/* Prompts for this moment in the chat; hidden while writing freely */}
              {promptsVisible && (
              <div role="group" aria-label="Suggested messages" className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-1">
                {chatSnippets.map((snippet) => (
                  <motion.button
                    key={snippet}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setInputText(snippet);
                      textareaRef.current?.focus();
                    }}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-[0.6875rem] font-medium transition-all border ${
                      isNight
                        ? 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                        : 'bg-black/5 border-black/5 text-black/60 hover:text-black hover:bg-black/10'
                    }`}
                  >
                    {snippet}
                  </motion.button>
                ))}
              </div>
              )}

              {/* Character count, only when a message gets long */}
              {inputText.length >= CHAT_MAX_CHARS * 0.9 && (
                <p className={`text-right text-[0.6875rem] mb-1 pr-2 ${inputText.length >= CHAT_MAX_CHARS ? 'text-red-500' : 'opacity-50'}`} aria-live="polite">
                  {inputText.length.toLocaleString()} / {CHAT_MAX_CHARS.toLocaleString()}
                  {inputText.length >= CHAT_MAX_CHARS && ' · send this, then keep going in a new message'}
                </p>
              )}

              {/* GIF search */}
              {showGifs && !isAiChat && (
                <Later><GifPicker isNight={isNight} onPick={sendGif} onClose={() => setShowGifs(false)} /></Later>
              )}

              {/* Photo preview: check it, then send */}
              <AnimatePresence>
                {pendingImage && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    role="group" aria-label="Photo to send"
                    className={`mb-2 rounded-2xl border overflow-hidden ${isNight ? 'bg-[#1a1a2e] border-white/10' : 'bg-white border-black/10'}`}
                  >
                    <div className={`relative flex justify-center ${isNight ? 'bg-black/30' : 'bg-black/5'}`}>
                      <img src={pendingImage.url} alt="Photo to send" className="max-h-56 w-auto max-w-full object-contain" />
                      <button onClick={clearPendingImage} aria-label="Cancel, don't send this photo"
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-black/75">
                        <XIcon size={16} />
                      </button>
                    </div>
                    <div className="p-2 flex items-center gap-2">
                      <input id="photo-caption" ref={captionRef} type="text" value={photoCaption} maxLength={CHAT_MAX_CHARS}
                        onChange={e => setPhotoCaption(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); if (!isUploadingImage) sendPendingImage(); }
                          if (e.key === 'Escape') { e.preventDefault(); clearPendingImage(); }
                        }}
                        placeholder="Add a caption (optional)"
                        aria-label="Caption (optional)"
                        className={`flex-1 min-w-0 rounded-xl border px-3 py-2 text-[0.9375rem] outline-none ${isNight ? 'bg-white/5 border-white/10 focus:border-white/30 placeholder:text-white/35' : 'bg-black/[0.03] border-black/10 focus:border-black/30 placeholder:text-black/35'}`} />
                      <EmojiShortcodes target={captionRef} isNight={isNight} />
                      <button onClick={sendPendingImage} disabled={isUploadingImage}
                        className="shrink-0 px-4 py-2 rounded-xl text-xs font-semibold bg-[#3797F0] text-white hover:bg-[#2b7bc6] disabled:opacity-40">
                        Send
                      </button>
                    </div>
                    <p className="px-3 pb-2 text-[0.6875rem] opacity-50">It shows blurred until they tap it.</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Reconnecting: this device, or the other person */}
              {(activeIssue || (rejoining && roomId && !isAiChat)) && (() => {
                const mine = !activeIssue || activeIssue.who === 'me';
                const canEnd = !!activeIssue && issueNow - activeIssue.since >= CONN_END_OFFER_MS;
                return (
                  <div role="status" aria-live="polite"
                    className={`mb-2 flex items-center gap-3 rounded-2xl border px-3 py-2 text-xs ${isNight ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/[0.03]'}`}>
                    <Loader2 size={14} className="animate-spin opacity-60 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{mine ? 'Trying to reconnect…' : `${otherName || 'The other person'} lost connection`}</p>
                      <p className="opacity-60">{mine ? 'Your messages will send when you are back.' : 'Waiting for them to come back…'}</p>
                    </div>
                    {canEnd && (
                      <button onClick={endChatNow}
                        className={`shrink-0 rounded-full border px-3 py-1.5 font-medium transition-colors ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`}>
                        End chat
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Editing one of your messages */}
              {editingId && roomId && (
                <div className={`mb-2 flex items-center gap-3 rounded-2xl border px-3 py-2 text-xs ${isNight ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/[0.03]'}`}>
                  <Pencil size={14} className="opacity-60 shrink-0" />
                  <button type="button" onClick={() => jumpTo(editingId)} className="flex-1 min-w-0 text-left">
                    <p className="font-medium">Editing message</p>
                    <p className="opacity-60 truncate">{messages.find(m => m.id === editingId)?.text}</p>
                  </button>
                  <button type="button" onClick={cancelEdit} aria-label="Cancel edit"
                    className={`shrink-0 p-1.5 rounded-full ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
                    <XIcon size={14} />
                  </button>
                </div>
              )}

              {/* Replying to a message */}
              {replyingTo && (roomId || isAiChat) && (
                <div className={`mb-2 flex items-center gap-3 rounded-2xl border px-3 py-2 text-xs ${isNight ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/[0.03]'}`}>
                  <ReplyIcon size={14} className="opacity-60 shrink-0" />
                  <button type="button" onClick={() => jumpTo(replyingTo.id)} className="flex-1 min-w-0 text-left">
                    <p className="font-medium">Replying to {replyingTo.mine ? 'yourself' : (otherName || 'them')}</p>
                    <p className="opacity-60 truncate">{quotePreview(replyingTo)}</p>
                  </button>
                  <button type="button" onClick={() => setReply(null)} aria-label="Cancel reply"
                    className={`shrink-0 p-1.5 rounded-full ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
                    <XIcon size={14} />
                  </button>
                </div>
              )}

              {/* Emoji Picker */}
              <AnimatePresence>
                {showEmojiPicker && (
                  <motion.div
                    ref={emojiPickerRef}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className={`mb-2 p-3 rounded-2xl border ${
                      isNight ? 'bg-[#1a1a2e] border-white/10' : 'bg-white border-black/10'
                    }`}
                  >
                    {emojiFull ? (
                      <ChunkBoundary fallback={
                        <div className="h-32 flex flex-col items-center justify-center gap-2 text-center text-xs opacity-70 px-4">
                          <p>Couldn't load all emojis. Zuzu was just updated, so refresh the page after your chat.</p>
                          <button type="button" onClick={() => setEmojiFull(false)} className="underline underline-offset-2">Back to quick emojis</button>
                        </div>
                      }>
                        <Suspense fallback={<div className="h-[min(340px,45dvh)] flex items-center justify-center"><Loader2 className="animate-spin opacity-50" size={20} /></div>}>
                          <FullEmojiPicker isNight={isNight} onPick={pickEmoji} onBack={() => setEmojiFull(false)} />
                        </Suspense>
                      </ChunkBoundary>
                    ) : (
                      <div className="grid grid-cols-8 gap-0.5">
                        {COMMON_EMOJIS.slice(0, -1).map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => pickEmoji(emoji)}
                            aria-label={`Add ${emoji}`}
                            className="text-xl hover:scale-125 transition-transform p-1.5 rounded"
                          >
                            {emoji}
                          </button>
                        ))}
                        <button
                          onClick={() => setEmojiFull(true)}
                          aria-label="More emojis"
                          title="More emojis"
                          className={`rounded-lg flex items-center justify-center transition-colors ${
                            isNight ? 'bg-white/10 hover:bg-white/20' : 'bg-black/5 hover:bg-black/10'
                          }`}
                        >
                          <SmilePlus size={20} className="opacity-80" />
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* The chat with a person has ended: nothing can be sent into it. */}
              {!isAiChat && !roomId ? (
                <div role="status" className={`rounded-2xl border px-4 py-3 flex flex-col gap-2.5 text-sm ${isNight ? 'border-white/20' : 'border-black/10'}`}>
                  <p className="opacity-70">This chat has ended.</p>
                  <div className="flex items-center gap-2">
                  {meetAvailable && meetCtx && meetCtx.state !== 'matched' && (
                    <button type="button" onClick={openMeet}
                      className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold transition-colors ${isNight ? 'bg-emerald-300/15 text-emerald-300 hover:bg-emerald-300/25' : 'bg-emerald-500/15 text-emerald-800 hover:bg-emerald-500/25'}`}>
                      <span className="inline-flex items-center gap-1.5"><MeetIcon size={13} /> {meetCtx.state === 'waiting' ? 'On' : 'Meet again'}</span>
                    </button>
                  )}
                  <button type="button" onClick={() => startMatching(screen === 'LISTEN' ? 'LISTEN' : 'VENT')}
                    className={`flex-1 rounded-full border px-3 py-2 text-xs font-medium transition-colors ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`}>
                    Start a new chat
                  </button>
                  </div>
                </div>
              ) : (
              <div className={`rounded-2xl p-1 flex items-center gap-2 border ${
                isNight ? 'bg-transparent border-white/20' : 'bg-transparent border-black/10'
              }`}>
                {isRecording ? (
                  <div className="flex-1 flex items-center justify-between px-2 py-1">
                    <button
                      onClick={() => stopRecording('delete')}
                      className="p-2 rounded-full hover:bg-black/5 hover:dark:bg-white/10 text-red-500 transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>

                    <AudioVisualizer isNight={isNight} />

                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${isNight ? 'text-white/70' : 'text-black/70'}`}>
                        {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:{(recordingTime % 60).toString().padStart(2, '0')}
                      </span>
                      <button
                        onClick={() => stopRecording('send')}
                        className="p-2 rounded-full bg-[#3797F0] text-white hover:bg-[#2b7bc6] transition-colors"
                      >
                        <ArrowUp size={20} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="ml-2 flex items-center gap-1 flex-shrink-0">
                      {!isAiChat && !inputTall && (
                        <div ref={attachRef} className="relative">
                          <button
                            type="button"
                            onClick={() => setAttachMenu(o => !o)}
                            aria-label="Send a photo or GIF"
                            aria-haspopup="menu"
                            aria-expanded={attachMenu}
                            className={`p-2 rounded-full transition-colors ${
                              attachMenu || showGifs
                                ? isNight ? 'bg-white/20 opacity-100' : 'bg-black/15 opacity-100'
                                : isNight ? 'bg-white/10 hover:bg-white/20 opacity-60 hover:opacity-100' : 'bg-black/5 hover:bg-black/10 opacity-60 hover:opacity-100'
                            }`}
                          >
                            <Image size={18} />
                          </button>
                          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" disabled={isUploadingImage} onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) choosePendingImage(file);
                            e.target.value = '';
                          }} />
                          {attachMenu && (
                            <div role="menu" className={`absolute bottom-full left-0 mb-2 z-30 w-36 p-1 rounded-2xl border shadow-xl ${isNight ? 'bg-[#1f1f1f] border-white/10' : 'bg-white border-black/10'}`}>
                              <button role="menuitem" onClick={() => { setAttachMenu(false); photoInputRef.current?.click(); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
                                <Image size={16} className="opacity-70" /> Photo
                              </button>
                              <button role="menuitem" onClick={() => { setAttachMenu(false); setShowEmojiPicker(false); setShowGifs(true); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
                                <ImagePlay size={16} className="opacity-70" /> GIF
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        ref={emojiButtonRef}
                        onClick={() => { setEmojiFull(false); setShowEmojiPicker(prev => !prev); }}
                        aria-expanded={showEmojiPicker}
                        className={`p-2 rounded-full transition-colors ${
                          showEmojiPicker
                            ? isNight ? 'bg-white/20 opacity-100' : 'bg-black/15 opacity-100'
                            : isNight ? 'bg-white/10 hover:bg-white/20 opacity-60 hover:opacity-100' : 'bg-black/5 hover:bg-black/10 opacity-60 hover:opacity-100'
                        }`}
                        aria-label="Emoji"
                      >
                        <Smile size={18} />
                      </button>
                    </div>
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      value={inputText}
                      maxLength={CHAT_MAX_CHARS}
                      aria-label="Message"
                      onChange={(e) => {
                        setInputText(e.target.value.slice(0, CHAT_MAX_CHARS));
                        const el = e.target;
                        el.style.height = 'auto';
                        el.style.height = `${Math.min(el.scrollHeight, CHAT_INPUT_MAX_H)}px`;
                        el.style.overflowY = el.scrollHeight > CHAT_INPUT_MAX_H ? 'auto' : 'hidden';
                        // Past one line, the photo button steps aside so the text gets more room.
                        // It comes back once the text is clearly shorter (no flicker at the edge).
                        const cs = getComputedStyle(el);
                        const oneLine = parseFloat(cs.lineHeight) * 1.5 + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
                        const len = e.target.value.length;
                        if (inputTallAt.current === null && el.scrollHeight > oneLine) { inputTallAt.current = len; setInputTall(true); setAttachMenu(false); }
                        else if (inputTallAt.current !== null && len < inputTallAt.current * 0.7) { inputTallAt.current = null; setInputTall(false); }
                      }}
                      onPaste={handlePaste}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' && editingId && !showEmojiPicker) { e.preventDefault(); cancelEdit(); return; }
                        if (e.key === 'Escape' && replyingTo && !showEmojiPicker) { e.preventDefault(); setReply(null); return; }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          // A photo waiting in the preview is only sent from the preview itself.
                          if (pendingImage && !inputText.trim()) document.getElementById('photo-caption')?.focus();
                          else handleSendMessage();
                        }
                      }}
                      placeholder="Message..."
                      style={{ resize: 'none' }}
                      className="flex-1 bg-transparent border-none outline-none py-2 px-1 text-[0.9375rem] placeholder:opacity-40 leading-snug"
                    />
                    <EmojiShortcodes target={textareaRef} isNight={isNight} />
                    {inputText.trim() ? (
                      <motion.button 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={handleSendMessage}
                        className="pr-4 text-[#3797F0] font-semibold text-sm"
                      >
                        Send
                      </motion.button>
                    ) : (
                      <div className="flex items-center gap-3 pr-4 opacity-60">
                        {!isAiChat && (
                          <button aria-label="Record audio"
                            onClick={startRecording}
                            className="p-2 rounded-full hover:scale-110 transition-all"
                          >
                            <Mic size={20} />
                          </button>
                        )}
                        <button aria-label="Send a heart" onClick={() => {
                          const reply = takeReply();
                          const newMessage: Message = {
                            id: newMessageId(),
                            text: "❤️",
                            type: 'text',
                            sender: 'user',
                            authorName: identity,
                            timestamp: Date.now(),
                            replyTo: reply.quote,
                          };
                          setMessages(prev => [...prev, newMessage]);
                          if (roomId) sendChat({ id: newMessage.id, roomId, text: "❤️", type: 'text', authorName: identity, replyTo: reply.wire });
                        }} className="hover:scale-110 transition-transform cursor-pointer text-red-500">
                          <Heart size={20} fill="currentColor" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              )}
            </div>
          </motion.div>
        )}

        {screen === 'LETGO' && (
          <Suspense fallback={<div className="fixed inset-0 bg-[#010205] z-0" />}>
            <LetGo
              onExit={() => { setScreen('LANDING'); navigate('/'); }}
              onRelease={(p) => socketRef.current?.emit('let_go', p)}
              onHeardAbout={handleHeardAboutSelect}
            />
          </Suspense>
        )}

        {screen === 'UNIVERSE' && (
          <motion.div
            key="universe"
            ref={skyRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`relative min-h-screen w-full overflow-hidden ${
              isNight ? 'bg-black text-white' : 'bg-sky-400 text-black'
            }`}
          >
            {/* Real Sky Background Images */}
            <div className="absolute inset-0 z-0">
              {isNight ? <StarrySky className="absolute inset-0" /> : <CloudSky className="absolute inset-0" />}
              {isNight && <div className="absolute inset-0 bg-black/10" />}
            </div>

            {/* Background Decorations */}
            <div className="absolute inset-0 pointer-events-none z-[1]">
            </div>

            {/* Top Bar for UNIVERSE */}
            <div className="absolute top-20 md:top-24 left-4 md:left-8 right-4 md:right-8 flex items-center justify-center z-50 pointer-events-none">
              <Link 
                to="/"
                aria-label="Back to Zuzu home"
                data-sky-avoid
                className={`absolute left-0 p-3 rounded-full opacity-60 hover:opacity-100 transition-all pointer-events-auto cursor-pointer ${
                  isNight ? 'bg-white/10 border border-white/10 backdrop-blur-md' : 'bg-white/40 border border-black/5 backdrop-blur-sm'
                }`}
              >
                <ArrowLeft size={20} />
              </Link>
              {/* The thoughts as a list: search, feelings, replies. Mirrors the back button. */}
              <button
                onClick={() => skyApiRef.current?.openList()}
                aria-label="Read the thoughts as a list"
                title="Read as a list"
                data-sky-avoid
                data-sky-list-button
                className={`absolute right-0 p-3 rounded-full opacity-60 hover:opacity-100 transition-all pointer-events-auto cursor-pointer ${
                  isNight ? 'bg-white/10 border border-white/10 backdrop-blur-md' : 'bg-white/40 border border-black/5 backdrop-blur-sm'
                }`}
              >
                <ListIcon size={20} />
              </button>
              <div data-sky-avoid className={`px-5 md:px-6 py-2.5 md:py-3 rounded-full backdrop-blur-md border pointer-events-auto ${
                isNight ? 'bg-black/40 border-white/10' : 'bg-white/40 border-black/5'
              }`}>
                <h1 className={`text-xs md:text-lg uppercase tracking-[0.2em] md:tracking-[0.3em] font-bold whitespace-nowrap ${isNight ? 'text-white' : 'text-black'}`} style={{ fontFamily: "'Instrument Serif', serif" }}>
                  The Sky of Thoughts
                </h1>
              </div>
            </div>

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" />

            {/* The Sky by feeling: chips with counts. Other thoughts fade back. */}
            {(() => {
              const counts = new Map<string, number>();
              for (const t of universeThoughts) for (const f of t.feelings ?? []) counts.set(f, (counts.get(f) ?? 0) + 1);
              const chips = [
                { key: 'all', label: 'All', count: universeThoughts.length },
                ...SKY_FEELINGS.filter(f => (counts.get(f.key) ?? 0) > 0 || f.key === skyFeeling)
                  .map(f => ({ key: f.key, label: f.label, count: counts.get(f.key) ?? 0 })),
              ];
              return (
                <div className="absolute top-36 md:top-40 left-0 right-0 z-40 flex justify-center px-4 pointer-events-none">
                  <div data-sky-avoid data-sky-header className="min-w-0 max-w-full">
                    <FeelingChips chips={chips} value={skyFeeling} onChange={setSkyFeeling} isNight={isNight} trailing={{ label: 'All feelings', onClick: () => navigate(FEELINGS_HUB.path) }} />
                  </div>
                </div>
              );
            })()}

            {/* A feeling page: one slim line under the chips, so the Sky stays in view.
                The heading opens the longer note; the count matches the chip. */}
            {(() => {
              const page = skyFeeling !== 'all' ? feelingPageByKey(skyFeeling) : undefined;
              if (!page || feelingIntroClosed) return null;
              const Icon = FEELING_ICONS[page.key];
              const here = universeThoughts.filter(t => t.feelings?.includes(page.key)).length;
              const letGo = letGoTotals?.[page.key] ?? 0;
              // "3 thoughts here & 5 more expressed in Let Go": Let Go worries are private, so only the count (and a link).
              const countText = here ? `${here} ${here === 1 ? 'thought' : 'thoughts'} here` : 'Be one of the first to share this feeling';
              const letGoText = letGo >= FEELING_COUNT_MIN ? `${letGo} more` : letGo > 0 ? 'more' : '';
              return (
                <div ref={introRef} className="absolute top-[12.25rem] md:top-[13.25rem] left-1/2 -translate-x-1/2 z-40 w-[min(34rem,calc(100%-2rem))] md:w-max md:max-w-[min(56rem,calc(100%-2rem))]">
                  <div className={`relative rounded-2xl pl-4 pr-10 py-2.5 backdrop-blur-md border ${isNight ? 'bg-black/35 border-white/10 text-white' : 'bg-white/55 border-black/5 text-black'}`}>
                    <button onClick={() => setFeelingIntroClosed(true)} aria-label="Hide this line" className="absolute top-2 right-2 p-1.5 opacity-50 hover:opacity-100">
                      <XIcon size={14} />
                    </button>
                    <div className="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1">
                      <button
                        onClick={() => setFeelingIntroMore(v => !v)}
                        aria-expanded={feelingIntroMore}
                        className="inline-flex items-center gap-1.5 text-left text-[1.0625rem] md:text-lg leading-snug min-w-0 hover:opacity-80"
                        style={{ fontFamily: "'Instrument Serif', serif" }}
                      >
                        {Icon && <Icon size={14} strokeWidth={1.75} aria-hidden="true" className="shrink-0 opacity-70" />}
                        <h1 className="font-normal">{page.heading} <em>{page.headingEm}</em></h1>
                        {/* A chevron says it opens: more about this feeling. */}
                        <ChevronDown size={15} aria-hidden="true" className={`shrink-0 opacity-60 transition-transform ${feelingIntroMore ? 'rotate-180' : ''}`} />
                      </button>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5 w-[calc(100%+1.75rem)] -mr-7 md:mr-0 md:w-auto text-[0.6875rem] opacity-80 md:whitespace-nowrap md:flex-nowrap">
                        <span className="opacity-75">
                          {countText}
                          {letGoText && <> &amp; {letGoText} <span className="hidden md:inline">expressed </span>in <Link to="/let-go" className="underline underline-offset-2 hover:opacity-100">Let Go</Link></>}
                        </span>
                        <button onClick={() => setIsAddingThought(true)} className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold border transition-colors ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`}>
                          <Plus size={12} aria-hidden="true" /> Add yours
                        </button>
                      </span>
                    </div>
                    {feelingIntroMore && (
                      // w-0 min-w-full: the note takes the line's width instead of widening it.
                      <div className="mt-1.5 pb-0.5 w-0 min-w-full">
                        <p className="text-[0.8125rem] opacity-80 leading-relaxed">{page.lead}</p>
                        {page.note.map((x, i) => <p key={i} className="mt-1.5 text-[0.8125rem] opacity-75 leading-relaxed">{x}</p>)}
                        {/* Share this feeling's Sky with someone who might feel it too. */}
                        <div className="mt-2.5 flex justify-end -mr-7 md:mr-0">
                          <button
                            onClick={() => shareFrom(`${page.heading} ${page.headingEm} Anonymous thoughts from people who feel it too, on Zuzu.`, `https://www.zuzu.club${feelingPath(page.key)}`)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.6875rem] font-semibold border transition-colors ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`}
                          >
                            <Share size={12} aria-hidden="true" /> {shareCopied ? 'Link copied' : 'Share this Sky'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}


            {skyLayout && (
              <Later>
              <InfiniteSky
                thoughts={universeThoughts}
                layout={skyLayout}
                isNight={isNight}
                gather={gatherSpots}
                isDimmed={skyDimmed}
                isReachable={skyReachable}
                onOpen={openThought}
                paused={!!selectedThought || isAddingThought}
                apiRef={skyApiRef}
                myIds={myIdsAll}
              />
              </Later>
            )}

            <ThoughtRelease burst={releaseBurst} isNight={isNight} />

            {/* Just posted, and held for a check: it is waiting, not lost. */}
            <AnimatePresence>
              {heldNotice && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  role="status"
                  className={`fixed top-36 left-1/2 -translate-x-1/2 z-[95] w-[min(26rem,calc(100%-2rem))] rounded-3xl px-5 py-4 text-sm backdrop-blur-xl border ${
                    isNight ? 'bg-black/60 border-white/10 text-white' : 'bg-white/90 border-black/10 text-black shadow-lg'
                  }`}
                >
                  <p className="font-semibold flex items-center gap-2"><Clock size={15} aria-hidden="true" /> Your thought is waiting for a quick check</p>
                  <p className="mt-1.5 text-xs opacity-80 leading-relaxed">
                    {heldNotice.fix
                      ? `It has ${FIX_LABEL[heldNotice.fix] ?? 'something that needs a look'}. Take it out to post it now, or leave it and a person will look at it soon.`
                      : 'A person will look at it soon. It is not lost: until then only you can see it, and you will see a note when it is in the Sky.'}
                  </p>
                  <div className="flex gap-2 mt-3">
                    {heldNotice.fix && (
                      <button onClick={editHeldThought} className={`px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'}`}>
                        <Pencil size={13} aria-hidden="true" /> Edit and post again
                      </button>
                    )}
                    <button onClick={() => setHeldNotice(null)} className={`px-4 py-2 rounded-full text-xs font-medium border ${isNight ? 'border-white/15' : 'border-black/10'}`}>
                      {heldNotice.fix ? 'Leave it waiting' : 'OK'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* A shared link to a thought that is no longer in the Sky */}
            <AnimatePresence>
              {linkNotice && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  role="status"
                  className={`fixed top-36 left-1/2 -translate-x-1/2 z-[90] px-5 py-3 rounded-full text-sm backdrop-blur-md border ${
                    isNight ? 'bg-black/50 border-white/10 text-white' : 'bg-white/80 border-black/10 text-black'
                  }`}
                >
                  That thought has drifted away, but the sky is still full of others.
                </motion.div>
              )}
            </AnimatePresence>

            {/* After posting: what is it about (Zuzu's guess is picked), and who else felt it */}
            <AnimatePresence>
              {feelingCard && (() => {
                const mine = universeThoughts.find(t => t.id === feelingCard.thoughtId);
                const chosen = feelingCard.picked ?? mine?.feelings ?? [];
                const feeling = chosen[0]; // the main one, for "others felt this"
                const others = feeling && feelingCounts ? (feelingCounts[feeling] ?? 0) : null;
                const about = feltPhrase(feeling);
                const inSky = feeling ? universeThoughts.filter(t => t.feelings?.includes(feeling) && t.id !== feelingCard.thoughtId).length : 0;
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    role="dialog"
                    aria-label="What is your thought about?"
                    className={`fixed bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-[95] w-[min(28rem,calc(100%-2rem))] rounded-3xl p-5 backdrop-blur-xl border ${
                      isNight ? 'bg-black/55 border-white/15 text-white' : 'bg-white/90 border-black/10 text-black shadow-xl'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="text-[0.625rem] uppercase tracking-widest opacity-60">
                        What is it about?
                        {!feelingCard.picked && (feeling ? <span className={isNight ? 'text-amber-200/90 normal-case tracking-normal ml-2' : 'text-amber-700 normal-case tracking-normal ml-2'}>Zuzu's guess is picked</span> : <span className="normal-case tracking-normal ml-2 opacity-70">Finding it…</span>)}
                      </p>
                      <button onClick={closeFeelingCard} aria-label="Close" className="shrink-0 -mt-1 -mr-1 p-1.5 opacity-50 hover:opacity-100">
                        <XIcon size={14} />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Feeling">
                      {SKY_FEELINGS.map(f => { const Icon = FEELING_ICONS[f.key]; return (
                        <button
                          key={f.key}
                          aria-pressed={chosen.includes(f.key)}
                          onClick={() => pickFeeling(feelingCard.thoughtId, f.key)}
                          className={`px-3 py-1.5 rounded-full text-xs border transition-colors cursor-pointer ${
                            chosen.includes(f.key)
                              ? (isNight ? 'bg-white text-black border-white' : 'bg-[#1a1a1a] text-white border-[#1a1a1a]')
                              : (isNight ? 'border-white/20 text-white/75 hover:bg-white/10' : 'border-black/15 text-black/70 hover:bg-black/5')
                          }`}
                        >
                          <span className="inline-flex items-center gap-1.5">{Icon && <Icon size={12} strokeWidth={1.75} className="opacity-60" aria-hidden="true" />}{f.label}</span>
                        </button>
                      ); })}
                    </div>
                    <p className="text-[0.6875rem] opacity-50 mt-2">Pick up to {MAX_FEELINGS}. No tap needed if Zuzu got it right.</p>
                    {feeling && others !== null && (
                      <div className={`mt-4 pt-4 border-t ${isNight ? 'border-white/10' : 'border-black/10'}`}>
                        <p className="text-sm leading-relaxed">
                          {others >= FEELING_COUNT_MIN
                            ? <><strong className="font-semibold">{others} others</strong> felt {about} too. You are not alone.</>
                            : others > 0
                              ? <>A few others felt {about} too. You are not alone.</>
                              : <>You are one of the first to share how this feels. Others will find it.</>}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {inSky > 0 && (
                            <button
                              onClick={() => { setSkyFeeling(feeling); closeFeelingCard(); }}
                              className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide ${isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'}`}
                            >
                              Read them
                            </button>
                          )}
                          <button
                            onClick={() => shareFrom('Share a feeling anonymously, or read what others feel, in the Sky of Thoughts on Zuzu.', 'https://www.zuzu.club/sky-of-thoughts')}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs border ${isNight ? 'border-white/20' : 'border-black/15'}`}
                          >
                            <Share size={13} /> {shareCopied ? 'Copied' : 'Share Zuzu'}
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })()}
            </AnimatePresence>


            {/* Add Thought Modal */}
            <AnimatePresence>
              {isAddingThought && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/60 backdrop-blur-lg"
                >
                  <motion.div
                    initial={{ scale: 0.9, y: 20, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.9, y: 20, opacity: 0 }}
                    className={`p-8 md:p-12 rounded-[2.5rem] max-w-lg w-full relative max-h-[calc(100dvh-3rem)] overflow-y-auto no-scrollbar ${
                      isNight ? 'frosted-panel text-night-ink' : 'bg-white text-black shadow-2xl'
                    }`}
                  >
                    <button aria-label="Close" 
                      onClick={() => setIsAddingThought(false)}
                      className="absolute top-6 right-6 p-2 opacity-40 hover:opacity-100 transition-opacity"
                    >
                      <XIcon size={20} />
                    </button>

                    <div className="flex flex-col items-center text-center">
                      <div className={`p-4 rounded-full mb-6 ${isNight ? 'bg-white/10' : 'bg-black/5'}`}>
                        <Sparkles size={32} className={isNight ? 'text-white' : 'text-yellow-600'} />
                      </div>
                      <h3 className="serif text-3xl mb-2 italic">Share your Story</h3>
                      <p className="text-xs uppercase tracking-widest opacity-40 mb-8">Your whisper will drift among the others</p>
                      
                      <div className="flex flex-row flex-nowrap overflow-x-auto justify-center gap-1 md:gap-2 mb-6 w-full px-1 py-2 no-scrollbar">
                        {SHARE_QUICK.map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => { setSelectedEmoji(emoji); setShowSharePicker(false); }}
                            className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 text-xl md:text-2xl rounded-full transition-all cursor-pointer flex items-center justify-center ${selectedEmoji === emoji ? (isNight ? 'bg-white/20 scale-110' : 'bg-black/10 scale-110') : 'opacity-50 hover:opacity-100 hover:scale-105'}`}
                          >
                            {emoji}
                          </button>
                        ))}
                        {/* More feelings: shows the picked extra emoji with a small + badge */}
                        <button
                          aria-label="More feelings"
                          aria-expanded={showSharePicker}
                          onClick={() => setShowSharePicker(v => !v)}
                          className={`relative flex-shrink-0 w-10 h-10 md:w-12 md:h-12 text-xl md:text-2xl rounded-full transition-all cursor-pointer flex items-center justify-center border border-dashed ${
                            isNight ? 'border-white/25' : 'border-black/20'
                          } ${!SHARE_QUICK.includes(selectedEmoji) || showSharePicker ? (isNight ? 'bg-white/20 scale-110' : 'bg-black/10 scale-110') : 'opacity-60 hover:opacity-100 hover:scale-105'}`}
                        >
                          {SHARE_QUICK.includes(selectedEmoji) ? <SmilePlus size={20} strokeWidth={1.8} /> : selectedEmoji}
                          {!SHARE_QUICK.includes(selectedEmoji) && (
                            <span className="absolute -right-1 -bottom-1 w-4 h-4 rounded-full flex items-center justify-center bg-[#e8cf92] text-[#04101f]">
                              <Plus size={11} strokeWidth={3} />
                            </span>
                          )}
                        </button>
                      </div>

                      {showSharePicker && (
                        <div className={`w-full -mt-3 mb-5 p-3 rounded-3xl text-left ${isNight ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/5'}`}>
                          {SHARE_MORE.map(([label, list]) => (
                            <div key={label} className="mb-1.5 last:mb-0">
                              <div className="text-[0.625rem] uppercase tracking-widest opacity-40 mb-1 ml-1">{label}</div>
                              <div className="grid grid-cols-8 gap-0.5">
                                {list.map(emoji => (
                                  <button
                                    key={emoji}
                                    aria-label={`Feeling ${emoji}`}
                                    onClick={() => { setSelectedEmoji(emoji); setShowSharePicker(false); }}
                                    className={`h-9 text-xl rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                                      selectedEmoji === emoji ? (isNight ? 'bg-white/20' : 'bg-black/10') : (isNight ? 'hover:bg-white/10' : 'hover:bg-black/5')
                                    }`}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <textarea
                        ref={thoughtBoxRef}
                        value={newThoughtText}
                        maxLength={THOUGHT_MAX_CHARS}
                        onChange={(e) => setNewThoughtText(e.target.value.slice(0, THOUGHT_MAX_CHARS))}
                        onCopy={(e) => e.preventDefault()}
                        placeholder="Whisper your story into the sky..."
                        className={`w-full h-32 p-6 rounded-3xl resize-none outline-none text-lg serif italic leading-relaxed ${
                          isNight ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/5'
                        }`}
                        autoFocus
                      />
                      <EmojiShortcodes target={thoughtBoxRef} isNight={isNight} />
                      {newThoughtText.length >= THOUGHT_MAX_CHARS * 0.9 && (
                        <p className={`self-end text-[0.6875rem] mt-1 pr-2 ${newThoughtText.length >= THOUGHT_MAX_CHARS ? 'text-red-500' : 'opacity-50'}`} aria-live="polite">
                          {newThoughtText.length.toLocaleString()} / {THOUGHT_MAX_CHARS.toLocaleString()}
                        </p>
                      )}

                      <div className="mt-8 w-full flex gap-3">
                        <button 
                          onClick={() => setIsAddingThought(false)}
                          className={`flex-1 py-4 rounded-full uppercase text-[0.625rem] tracking-widest opacity-40 hover:opacity-100 transition-all cursor-pointer ${
                            isNight ? 'border border-white/10' : 'border border-black/10'
                          }`}
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={() => {
                            if (newThoughtText.trim()) {
                              addToUniverse(newThoughtText, selectedEmoji);
                              setNewThoughtText("");
                              setIsAddingThought(false);
                            }
                          }}
                          disabled={!newThoughtText.trim()}
                          className={`flex-1 py-4 rounded-full uppercase text-[0.625rem] tracking-widest font-bold transition-all cursor-pointer ${
                            isNight 
                              ? 'bg-white text-black hover:bg-opacity-90' 
                              : 'bg-black text-white hover:bg-opacity-90'
                          } disabled:opacity-20`}
                        >
                          Release Story
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Thought Modal */}
            <AnimatePresence>
              {selectedThought && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/40 backdrop-blur-md"
                  style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
                  onClick={closeThought}
                >
                  <motion.div
                    initial={{ scale: 0.9, y: 40, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.9, y: 40, opacity: 0 }}
                    className={`p-6 md:p-10 rounded-[3rem] max-w-xl w-full text-center relative overflow-y-auto overscroll-contain no-scrollbar ${
                      isNight ? 'frosted-panel text-night-ink' : 'bg-white text-black border border-black/10 shadow-2xl'
                    }`}
                    // The visible screen (dvh), not 90vh: on iPhone 90vh is taller than the space
                    // above the browser bar, so the bottom buttons could not be reached.
                    style={{ maxHeight: 'min(100%, calc(100dvh - 3rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)))' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-col items-center gap-4">
                      <div>
                        {selectedThought.emoji
                          ? <span className="text-4xl">{selectedThought.emoji}</span>
                          : <Wind size={40} className="opacity-10" />}
                      </div>
                      <div className="max-h-[40vh] overflow-y-auto custom-scrollbar px-4">
                        <p className="serif text-xl md:text-2xl leading-relaxed italic font-light">
                          "{selectedThought.text}"
                        </p>
                        {selectedThought.pending && (
                          <p className={`mt-3 inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 ${isNight ? 'bg-white/10' : 'bg-black/5'}`}>
                            <Clock size={13} aria-hidden="true" /> Waiting for a quick check. Only you can see it for now.
                          </p>
                        )}
                      </div>
                    </div>

                    {(() => {
                      const live = universeThoughts.find(t => t.id === selectedThought.id) ?? selectedThought;
                      const n = live.meToo ?? 0;
                      const isMine = myThoughtIds.has(live.id);
                      const pressed = meTooMine.has(live.id);
                      return (
                        <div className="mt-6 flex flex-col items-center gap-3">
                          {(live.feelings?.length ?? 0) > 0 && (
                            <div className="flex flex-wrap justify-center gap-1.5">
                              {live.feelings!.map(f => {
                                const Icon = FEELING_ICONS[f];
                                return (
                                  <button
                                    key={f}
                                    onClick={() => { setSkyFeeling(f); skyApiRef.current?.closeList(); closeThought(); }}
                                    className={`px-3 py-1 rounded-full text-[0.6875rem] border transition-colors ${isNight ? 'border-amber-200/40 text-amber-100/90 hover:bg-white/5' : 'border-amber-600/30 text-amber-800 hover:bg-amber-50'}`}
                                    aria-label={`Show more thoughts about ${skyFeelingLabel(f).toLowerCase()}`}
                                  >
                                    <span className="inline-flex items-center gap-1.5">
                                      {Icon && <Icon size={12} strokeWidth={1.75} aria-hidden="true" />}
                                      {skyFeelingLabel(f)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <div className="flex items-center gap-3 flex-wrap justify-center">
                            {!isMine && (
                              <button
                                onClick={() => toggleMeToo(live.id)}
                                aria-pressed={pressed}
                                className={`px-5 py-2 rounded-full text-xs font-semibold tracking-wide transition-all active:scale-95 ${
                                  pressed
                                    ? 'bg-[#e8cf92] text-[#04101f]'
                                    : (isNight ? 'border border-white/20 hover:bg-white/10' : 'border border-black/15 hover:bg-black/5')
                                }`}
                              >
                                🤍 {pressed ? 'You too' : 'Me too'}
                              </button>
                            )}
                            <span className="text-xs opacity-60" aria-live="polite">
                              {n > 0
                                ? `${n} ${n === 1 ? 'person' : 'people'} felt this too`
                                : isMine ? 'When someone feels this too, you will see it here.' : 'Felt this too? Let them know.'}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {(() => {
                      const mine = outgoingReach?.thoughtId === selectedThought.id ? outgoingReach : null;
                      const canReach = reachableThoughts.has(selectedThought.id) && !myThoughtIds.has(selectedThought.id);
                      if (!mine && !canReach) return null;
                      const panel = `mt-8 rounded-3xl px-5 py-4 text-left ${isNight ? 'bg-white/[0.06]' : 'bg-amber-50/70 border border-amber-100'}`;
                      if (mine?.status === 'unavailable') {
                        return (
                          <div className={panel} role="status">
                            <p className="text-sm">They're not available to talk right now.</p>
                            <p className="text-xs opacity-60 mt-1">You can still leave them a kind reply below.</p>
                          </div>
                        );
                      }
                      if (mine) {
                        return (
                          <div className={panel} role="status">
                            <div className="flex items-start gap-3">
                              <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin opacity-60" />
                              <div className="flex-1">
                                <p className="text-sm">They've been gently asked if they'd like to talk.</p>
                                <p className="text-xs opacity-60 mt-1">Please keep this open for a moment. They can say yes or not now.</p>
                              </div>
                            </div>
                            <button onClick={closeThought} className="mt-3 text-xs underline underline-offset-4 opacity-60 hover:opacity-100">Never mind</button>
                          </div>
                        );
                      }
                      return (
                        <div className={panel}>
                          <div className="flex items-start gap-3">
                            <Sparkles size={16} className={`mt-0.5 shrink-0 ${isNight ? 'text-amber-200/80' : 'text-amber-500'}`} />
                            <div className="flex-1">
                              <p className="text-sm">The person who wrote this is still here.</p>
                              <p className="text-xs opacity-60 mt-1">If it feels right, you can ask to talk, just the two of you. Nothing starts unless they say yes.</p>
                            </div>
                          </div>
                          <button
                            onClick={() => reachOut(selectedThought.id)}
                            className={`mt-3 w-full sm:w-auto px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide transition-all active:scale-95 ${
                              isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-[#1a1a1a] text-white hover:bg-black/90'
                            }`}
                          >
                            Reach out
                          </button>
                        </div>
                      );
                    })()}

                    {(() => {
                      const replies = universeThoughts.find(t => t.id === selectedThought.id)?.replies ?? selectedThought.replies ?? [];
                      const writerKey = postKey(selectedThought.id); // this device wrote the thought
                      const sendReply = () => {
                        const text = replyText.trim();
                        if (text.length < 2 || replyStatus === 'sending') return;
                        setReplyStatus('sending');
                        // The AI check can take a few seconds.
                        const thoughtId = selectedThought.id;
                        // Sent again after editing: the older, waiting version is taken back
                        // (with its key, so only this device can).
                        if (replyHeld) { socketRef.current?.emit('withdraw_post', { kind: 'reply', id: replyHeld.id, key: replyKey(replyHeld.id) }); forgetReply(replyHeld.id); setReplyHeld(null); }
                        // A secret key, kept on this device, so only the writer can take this reply back while it waits.
                        const rKey = newPostKey();
                        socketRef.current?.timeout(20000).emit('send_reply', { thoughtId, text, key: rKey }, (err: Error | null, res: { ok: boolean; published?: boolean; id?: string; fix?: string } | boolean) => {
                          const ok = typeof res === 'object' ? res.ok : res;
                          const published = typeof res === 'object' && !!res.published;
                          // On this device only: to say "the writer loved your reply", or
                          // that a waiting reply went live, later.
                          if (!err && typeof res === 'object' && res.id) rememberReply(res.id, thoughtId, published ? 'live' : 'waiting', text, rKey);
                          if (!err && ok && typeof res === 'object' && res.fix && res.id) {
                            // Held for something simple: keep the words so they can take it out.
                            setReplyHeld({ id: res.id, fix: res.fix });
                            setReplyStatus('idle');
                          } else if (!err && ok) { setReplyText(''); setReplyStatus(published ? 'live' : 'sent'); }
                          else setReplyStatus('error');
                        });
                      };
                      return (
                        <div className={`mt-8 pt-6 border-t text-left ${isNight ? 'border-white/10' : 'border-black/10'}`}>
                          <div className="flex items-center gap-2 mb-3 text-[0.6875rem] uppercase tracking-widest opacity-60">
                            <MessageCircle size={14} />
                            {replies.length > 0 ? `${replies.length} kind ${replies.length === 1 ? 'reply' : 'replies'}` : 'Replies'}
                          </div>
                          {replies.length > 0 ? (
                            <ul className="space-y-2.5 mb-4 max-h-[22vh] overflow-y-auto custom-scrollbar pr-1">
                              {replies.map(r => (
                                <li key={r.id} className={`relative rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isNight ? 'bg-white/5' : 'bg-black/[0.04]'} ${writerKey ? 'pr-12' : ''}`}>
                                  <span className="block text-[0.625rem] uppercase tracking-widest opacity-50 mb-0.5">{r.name}</span>
                                  {r.text}
                                  {/* Only the thought's writer can love a reply; everyone sees it. */}
                                  {writerKey ? (
                                    <button
                                      onClick={() => loveReply(selectedThought.id, r.id, !r.loved, writerKey)}
                                      aria-pressed={!!r.loved}
                                      aria-label={r.loved ? 'You loved this reply. Tap to take it back' : 'Love this reply'}
                                      title={r.loved ? 'You loved this reply' : 'Love this reply'}
                                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2 rounded-full transition-transform active:scale-90"
                                    >
                                      <Heart size={18} className={r.loved ? 'text-red-500 fill-red-500' : 'opacity-40 hover:opacity-80'} />
                                    </button>
                                  ) : r.loved && (
                                    <span className="mt-1.5 flex items-center gap-1 text-[0.6875rem] opacity-70">
                                      <Heart size={11} className="text-red-500 fill-red-500" aria-hidden="true" /> The writer loved this
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs opacity-50 mb-3">No replies yet. Leave a few kind words for this person.</p>
                          )}
                          {replyStatus === 'live' ? (
                            <p className={`flex items-start gap-2 text-xs rounded-2xl px-4 py-3 ${isNight ? 'bg-white/5' : 'bg-black/[0.04]'}`}>
                              <Check size={14} className="mt-0.5 shrink-0" />
                              Thank you. Your reply is now in the sky for them.
                            </p>
                          ) : replyStatus === 'sent' ? (
                            <p className={`flex items-start gap-2 text-xs rounded-2xl px-4 py-3 ${isNight ? 'bg-white/5' : 'bg-black/[0.04]'}`}>
                              <Check size={14} className="mt-0.5 shrink-0" />
                              Thank you. Your reply is waiting for a quick check by a person. It is not lost: it will appear here once checked, and you will see a note next time you visit.
                            </p>
                          ) : (
                            <>
                              {replyEmoji && (
                                <div className={`mb-2 p-2.5 rounded-2xl border ${isNight ? 'bg-[#1a1a2e] border-white/10' : 'bg-white border-black/10'}`}>
                                  {replyEmoji === 'full' ? (
                                    <ChunkBoundary fallback={<p className="text-xs opacity-70 p-3 text-center">Couldn't load all emojis. Please refresh the page.</p>}>
                                      <Suspense fallback={<div className="h-[min(340px,45dvh)] flex items-center justify-center"><Loader2 className="animate-spin opacity-50" size={20} /></div>}>
                                        <FullEmojiPicker isNight={isNight} onPick={addReplyEmoji} onBack={() => setReplyEmoji('quick')} />
                                      </Suspense>
                                    </ChunkBoundary>
                                  ) : (
                                    <div className="grid grid-cols-8 gap-0.5">
                                      {COMMON_EMOJIS.slice(0, -1).map(emoji => (
                                        <button key={emoji} type="button" onClick={() => addReplyEmoji(emoji)} aria-label={`Add ${emoji}`}
                                          className="text-xl hover:scale-125 transition-transform p-1.5 rounded">
                                          {emoji}
                                        </button>
                                      ))}
                                      <button type="button" onClick={() => setReplyEmoji('full')} aria-label="More emojis" title="More emojis"
                                        className={`rounded-lg flex items-center justify-center transition-colors ${isNight ? 'bg-white/10 hover:bg-white/20' : 'bg-black/5 hover:bg-black/10'}`}>
                                        <SmilePlus size={20} className="opacity-80" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className={`flex items-end gap-2 rounded-2xl border px-3 py-2 ${isNight ? 'border-white/10 bg-white/5' : 'border-black/10 bg-white'}`}>
                                <textarea
                                  ref={replyBoxRef}
                                  value={replyText}
                                  maxLength={REPLY_MAX_CHARS}
                                  onChange={e => { setReplyText(e.target.value.slice(0, REPLY_MAX_CHARS)); if (replyStatus === 'error') setReplyStatus('idle'); }}
                                  placeholder="Write a kind reply..."
                                  aria-label="Write a kind reply"
                                  rows={2}
                                  className="flex-1 resize-none bg-transparent text-sm outline-none placeholder:opacity-40"
                                />
                                <EmojiShortcodes target={replyBoxRef} isNight={isNight} />
                                <button
                                  type="button"
                                  onClick={() => setReplyEmoji(v => (v ? null : 'quick'))}
                                  aria-label={replyEmoji ? 'Hide emojis' : 'Add an emoji'}
                                  aria-expanded={!!replyEmoji}
                                  title="Add an emoji"
                                  className={`p-2 rounded-full transition-opacity ${replyEmoji ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`}
                                >
                                  <Smile size={16} />
                                </button>
                                <button
                                  onClick={sendReply}
                                  disabled={replyText.trim().length < 2 || replyStatus === 'sending'}
                                  aria-label="Send reply"
                                  className={`p-2 rounded-full transition-all disabled:opacity-30 ${isNight ? 'bg-white text-black' : 'bg-black text-white'}`}
                                >
                                  {replyStatus === 'sending' ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={16} />}
                                </button>
                              </div>
                              {replyHeld && (
                                <p role="status" className={`mt-2 text-xs rounded-2xl px-4 py-3 ${isNight ? 'bg-amber-300/10 text-amber-100' : 'bg-amber-50 text-amber-900'}`}>
                                  Your reply is waiting for a check because it has {FIX_LABEL[replyHeld.fix] ?? 'something that needs a look'}. Take it out and send again to post it now, or leave it and a person will look at it.
                                </p>
                              )}
                              {replyText.length >= REPLY_MAX_CHARS * 0.9 && (
                                <p className={`text-right text-[0.6875rem] mt-1 pr-2 ${replyText.length >= REPLY_MAX_CHARS ? 'text-red-500' : 'opacity-50'}`} aria-live="polite">
                                  {replyText.length.toLocaleString()} / {REPLY_MAX_CHARS.toLocaleString()}
                                </p>
                              )}
                              <p className="text-[0.6875rem] opacity-50 mt-2">
                                {replyStatus === 'error'
                                  ? 'That did not go through. Please wait a few seconds and try again.'
                                  : 'Be gentle. No names, numbers or places. Replies are checked before they appear.'}
                              </p>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    <div className="mt-8 flex justify-center gap-2 md:gap-6">
                      <motion.button 
                        whileHover={{ scale: 1.2 }}
                        onClick={() => {
                          setLikedThoughts(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(selectedThought.id)) {
                              newSet.delete(selectedThought.id);
                            } else {
                              newSet.add(selectedThought.id);
                            }
                            return newSet;
                          });
                        }}
                        className={`p-4 rounded-full transition-colors cursor-pointer ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                      >
                        <Heart size={24} className={`transition-colors ${likedThoughts.has(selectedThought.id) ? 'text-red-500 fill-red-500' : 'text-red-400/40 hover:text-red-400/80'}`} />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        onClick={() => shareThought(selectedThought)}
                        aria-label="Share this thought"
                        className={`p-4 rounded-full transition-colors cursor-pointer ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                        title={thoughtLinkCopied ? 'Link copied' : 'Share this thought'}
                      >
                        {thoughtLinkCopied ? <Check size={22} className="opacity-70" /> : <Share size={22} className="opacity-60" />}
                        <span className="sr-only" aria-live="polite">{thoughtLinkCopied ? 'Link copied' : ''}</span>
                      </motion.button>
                      <button aria-label="Close thought" 
                        onClick={closeThought}
                        className={`px-6 md:px-10 py-3 rounded-full transition-colors uppercase text-[0.625rem] tracking-[0.3em] cursor-pointer whitespace-nowrap ${
                          isNight ? 'border border-white/10 hover:bg-white/5' : 'border border-black/10 hover:bg-black/5'
                        }`}
                      >
                        {thoughtFromList ? 'Back to list' : 'Back to Sky'}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Streamlined Share Button */}
            <div data-sky-avoid data-sky-bottom className="fixed bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-4 w-full max-w-xs md:max-w-sm">
              {/* After posting a thought: a quiet, optional share nudge. In this stack too, above the button. */}
            <AnimatePresence>
              {showSkyShare && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  role="status"
                  className={`w-full flex items-center gap-3 pl-5 pr-2 py-2 rounded-full text-sm backdrop-blur-md border ${
                    isNight ? 'bg-black/50 border-white/10 text-white' : 'bg-white/80 border-black/10 text-black'
                  }`}
                >
                  <span className="flex-1 opacity-80">Someone else might be feeling this too.</span>
                  <button
                    onClick={() => shareFrom('Share a feeling anonymously, or read what others feel, in the Sky of Thoughts on Zuzu.', 'https://www.zuzu.club/sky-of-thoughts')}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                      isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'
                    }`}
                  >
                    <Share size={13} /> {shareCopied ? 'Copied' : 'Share'}
                  </button>
                  <button onClick={() => setShowSkyShare(false)} aria-label="Close" className="shrink-0 p-1.5 opacity-50 hover:opacity-100">
                    <XIcon size={14} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
              {/* October: a quiet pointer to the World Mental Health Day page, once per visit.
                  It sits in this stack, above the button, so the two can never overlap. */}
              <WmhdNudge isNight={isNight} place="sky" inline hidden={showSkyShare || isAddingThought || !!selectedThought || !!replyNudge} />
              <GlassButton 
                isNight={isNight} 
                onClick={() => setIsAddingThought(true)}
                className="w-full"
              >
                <Sparkles size={20} className="opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="flex flex-col items-start">
                  <span className="text-lg font-semibold">Share your Story</span>
                  <span className="text-[0.6875rem] uppercase tracking-widest opacity-70">unburden your mind</span>
                </div>
              </GlassButton>
              <h2 className={`text-xs md:text-sm italic opacity-80 text-center px-4 font-normal ${isNight ? 'text-white/80' : 'text-black/80 font-medium drop-shadow-md'}`}>
                Every emoji holds a secret. Tap to read it, drag to find more.
              </h2>
              {/* A quiet credit (the FAQ answer on what inspired the Sky and Let Go) and a way to send feedback. */}
              <p className={`-mt-2 text-[0.6875rem] text-center ${isNight ? 'text-white/50' : 'text-black/55'}`}>
                <Link to="/faq#are-there-any-projects-that-inspired" className={`underline-offset-4 hover:underline ${isNight ? 'hover:text-white' : 'hover:text-black'}`}>What inspired the Sky?</Link>
                <span aria-hidden="true" className="mx-2">·</span>
                <Link to="/feedback" className={`underline-offset-4 hover:underline ${isNight ? 'hover:text-white' : 'hover:text-black'}`}>Feedback</Link>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </main>
    </div>
  );
}
